import React, { useState, useEffect, useMemo } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { useAuth } from './AuthContext'
import {
  CreditCard, Clock, Truck, FileText, CheckCircle,
  AlertCircle, Loader, Search, ChevronRight, Package, Save, Pencil, X,
  DollarSign, Eye, Send, RefreshCw, Trash2, StickyNote, Ban, ArrowRight, Flag, AlertTriangle, Repeat, Copy
} from 'lucide-react'
import { CopyButton } from './CopyButton'
import PedidosFornecedor from './PedidosFornecedor'
import { verifyFaturaClientePdf, extrairCabecalhoFaturaPdf } from './utils/verifyFaturaCliente'
import { corPorFactura, estiloChipFactura } from './utils/corFactura'
import { detectTracking } from './tracking'
import { parseComprovativoFile } from './utils/parseFatura'

import { C } from './theme'
import { carregarPortes, PORTES_COBRADOS_FALLBACK, PORTES_CUSTO_FALLBACK } from './portes'
import { lerTudo } from './lerTudo'
import { SLOTS_TRANSITO } from './constants'

// Escalões do fornecedor: 100 un é o mínimo para sequer fazer pedido;
// a partir das 200 un há mais 2% de desconto. A diferença entre 170 e 200
// vale dinheiro, por isso o contador mostra os dois marcos.
const DESCONTO_ESCALAO_UN = 200
const DESCONTO_ESCALAO_PCT = 2

const fmt2 = (n) => Number(n || 0).toFixed(2)

// Portes: ver src/portes.js — COBRADOS ao cliente vs CUSTO nosso. Nunca confundir.
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—'

// Total efetivo da encomenda (com desconto aplicado, se existir)
const totalEfetivo = (e) => Number(e?.total_final ?? e?.total_com_iva ?? 0)

// Cor da linha no Arquivo → determinística pelo nº fatura (mesma factura sempre a mesma cor)
const corLinhaFactura = (numero) => numero ? corPorFactura(numero).bg : 'transparent'

// ─── Regra única da 1.ª prestação ────────────────────────────────────────────
// A mesma que está nos emails e na base de dados. Por ordem de prioridade:
//   1) pago_1a_valor   — o que entrou de facto (admin confirmou)
//   2) sinal_escolhido — o que o cliente escolheu pagar (acordo dos 8%)
//   3) total / 2       — encomendas 50/50 de sempre
// A 2.ª é SEMPRE `total − 1.ª`, nunca "a outra metade": com um sinal de 8% a
// diferença entre as duas leituras é a encomenda quase inteira.
// Fornecedores de pagamento total — o Naypes — nao tem 2.a prestacao: a
// encomenda paga-se de uma vez, na compra. A marca vem de
// encomendas.tipo_pagamento, posta pela RPC a partir de
// fornecedores.exige_pagamento_total.
//
// O Financeiro nao sabia disto e partia sempre o total ao meio. Numa encomenda
// de 168,82 EUR mostrava duas prestacoes de 84,41 EUR, pedia comprovativo para
// cada uma e oferecia "notificar a 2.a prestacao" que nunca vai existir.
export const pagaTudoDeUmaVez = (enc) => String(enc?.tipo_pagamento) === '100'

const primeiraPrestacao = (enc, total) =>
  enc?.pago_1a_valor != null   ? Number(enc.pago_1a_valor)
  : pagaTudoDeUmaVez(enc)      ? total
  : enc?.sinal_escolhido != null ? Number(enc.sinal_escolhido)
  : total / 2
const pctDoTotal = (valor, total) => total > 0 ? Math.round((valor / total) * 1000) / 10 : 50
const fmtPct = (n) => String(Math.round(n * 10) / 10).replace('.', ',')
// Encomendas com sinal reduzido deixam de poder ser rotuladas "50% Pago".
const temSinalReduzido = (enc) => enc?.sinal_escolhido != null

// O estado `pago_1_tranche` na BD nao sabe QUANTO foi pago — so que a 1.a
// prestacao entrou. Com o sinal flexivel, "50% Pago" passa a mentir: mostra-se
// a percentagem real. O valor guardado na BD nao muda, so o rotulo.
const estadoInfo = (enc, total) => {
  const base = STATUS_LABEL[enc?.estado] || STATUS_LABEL['pendente']
  if (enc?.estado !== 'pago_1_tranche' || !(temSinalReduzido(enc) || pagaTudoDeUmaVez(enc))) return base
  const pct = pctDoTotal(primeiraPrestacao(enc, total), total)
  // No pagamento total a 1.a prestacao E o total: rotular "50% Pago" seria
  // dizer ao cliente que ainda deve metade de uma encomenda ja liquidada.
  if (pct >= 99.9) return { ...STATUS_LABEL['pago_2_tranche'] }
  return { ...base, label: `${fmtPct(pct)}% Pago`, color: '#92400e', bg: '#fff8e1' }
}

const STATUS_LABEL = {
  pendente:        { label: 'Pendente',        color: C.warning,  bg: '#fff8e1' },
  pago_1_tranche:  { label: '50% Pago',        color: C.primary,  bg: '#e3f2fd' },
  pago_2_tranche:  { label: '100% Pago',       color: C.success,  bg: '#e8f5e9' },
  enviado:         { label: 'Enviado',         color: C.success,  bg: '#dff6dd' },
  concluido:       { label: 'Concluído',       color: C.textMuted,bg: C.bg      },
  cancelado:       { label: 'Cancelado',       color: C.danger,   bg: '#fde7e9' },
}

// Bancos PT mais comuns — usados no autocomplete do registo de pagamento
const BANCOS_PT = [
  'Millennium BCP',
  'Santander Totta',
  'Caixa Geral de Depósitos',
  'Novo Banco',
  'BPI',
  'Crédito Agrícola',
  'Montepio',
  'EuroBic',
  'Banco CTT',
  'ActivoBank',
  'Bankinter',
  'BIG',
  'Banco Best',
  'N26',
  'Revolut',
  'Wise',
  'Conta interna',
]

export default function Financeiro({ setActivePage } = {}) {
  const { perfil, isAdmin } = useAuth()
  const [encomendas, setEncomendas] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showPedidosForn, setShowPedidosForn] = useState(false)
  const [internosIds, setInternosIds] = useState(new Set())
  // Condicoes comerciais para o aviso do topo. Vem da vista publica, que o
  // cliente tambem pode ler — a tabela `fornecedores` esta fechada por RLS e
  // uma leitura bloqueada devolve zero linhas em silencio.
  const [condicoesForn, setCondicoesForn] = useState([])
  // fornecedor_id → mínimo de unidades do pedido. Só lá estão os que têm.
  const [minUnPorForn, setMinUnPorForn] = useState(new Map())
  // Fornecedores com o desconto de cada um, para o painel do custo.
  const [fornsAdmin, setFornsAdmin] = useState([])
  const [particularesIds, setParticularesIds] = useState(new Set())
  const [custosPorNome, setCustosPorNome] = useState(new Map())
  // Nome do artigo -> portes que pagamos por unidade, vindos do fornecedor dele.
  const [portesCustoPorNome, setPortesCustoPorNome] = useState(new Map())
  // Nome do artigo -> nome do fornecedor a quem se compra. Serve para se saber,
  // sem abrir a encomenda, onde e que a compra vai ser feita. Sai do mesmo ciclo
  // que ja le Produtos(Nome, fornecedor_id) para os portes — nao ha consulta
  // nova por causa disto.
  const [fornecedorPorNome, setFornecedorPorNome] = useState(new Map())
  const [nifPorCliente, setNifPorCliente] = useState(new Map())
  const [sinalPctPorCliente, setSinalPctPorCliente] = useState(new Map())
  const [portesPorCliente, setPortesPorCliente] = useState(new Map())
  const [facturaOutraEntidadeIds, setFacturaOutraEntidadeIds] = useState(new Set())
  const [facturasFornecedor, setFacturasFornecedor] = useState(new Map()) // numero_fatura -> { data_envio_prevista, pago_1a_data }
  // Set de facturas fornecedor ainda presentes em algum Transito*_Factura de produto.
  // Enquanto a factura estiver aqui, encomendas com essa factura NAO podem arquivar
  // (stock ainda nao chegou ao LOGNOW / trânsito ainda pendente).
  const [facturasEmTransito, setFacturasEmTransito] = useState(new Set())
  const [iban, setIban] = useState('')
  const [PORTES_COBRADOS, setPortesCobrados] = useState(PORTES_COBRADOS_FALLBACK)
  const [PORTES_CUSTO, setPortesCusto] = useState(PORTES_CUSTO_FALLBACK)
  const [ibanEdit, setIbanEdit] = useState('')
  const [bancoNome, setBancoNome] = useState('')
  const [bancoNomeEdit, setBancoNomeEdit] = useState('')
  const [bancoTitular, setBancoTitular] = useState('')
  const [bancoTitularEdit, setBancoTitularEdit] = useState('')
  const [editandoIban, setEditandoIban] = useState(false)
  const [savingIban, setSavingIban] = useState(false)
  const [ibanMsg, setIbanMsg] = useState({ tipo: '', texto: '' })

  // Desconto do fornecedor (config global) — afeta todos os preço_custo
  const [savingDesconto, setSavingDesconto] = useState(false)
  const [descontoMsg, setDescontoMsg] = useState({ tipo: '', texto: '' })

  // Modal de detalhe de pagamento
  const [modalPag, setModalPag] = useState(null)  // encomenda seleccionada
  const [modalItens, setModalItens] = useState([])
  const [modalItensLoading, setModalItensLoading] = useState(false)
  // Edição de quantidades — só em contas internas (ver editarQuantidades)
  const [editandoQtds, setEditandoQtds] = useState(false)
  const [qtdsEditadas, setQtdsEditadas] = useState({})   // { item_id: qtd }
  const [guardandoQtds, setGuardandoQtds] = useState(false)
  const [erroQtds, setErroQtds] = useState('')
  const [resultadoEdicao, setResultadoEdicao] = useState(null)   // conferência do pagamento após alterar
  const [portesInput, setPortesInput] = useState('')
  const [savingPortes, setSavingPortes] = useState(false)
  const [portesMsg, setPortesMsg] = useState({ tipo: '', texto: '' })
  const [enviandoEmailSegunda, setEnviandoEmailSegunda] = useState(false)
  const [emailSegundaMsg, setEmailSegundaMsg] = useState({ tipo: '', texto: '' })
  const [enviandoPedido100, setEnviandoPedido100] = useState(false)
  const [pedido100Msg, setPedido100Msg] = useState({ tipo: '', texto: '' })

  // Sistema Ativas / Arquivo + filtros
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('fin_tab') || 'ativas')
  const [showFiltrosAvancados, setShowFiltrosAvancados] = useState(false)
  const [filtroCliente, setFiltroCliente] = useState(() => localStorage.getItem('fin_cliente') || '')
  const [filtroPeriodo, setFiltroPeriodo] = useState(() => localStorage.getItem('fin_periodo') || 'tudo')
  const [filtroOrigem, setFiltroOrigem] = useState(() => localStorage.getItem('fin_origem') || 'todas')
  const [filtroFactFornecedor, setFiltroFactFornecedor] = useState('')
  const [filtroValorMin, setFiltroValorMin] = useState('')
  const [filtroFacturacao, setFiltroFacturacao] = useState('todas')
  const [chipsAtivos, setChipsAtivos] = useState({
    pendencias: false, aguardaPag: false, aguardaEnvio: false, faltaFactura: false, transitoHoje: false,
  })
  useEffect(() => { localStorage.setItem('fin_tab', activeTab) }, [activeTab])
  useEffect(() => { localStorage.setItem('fin_cliente', filtroCliente) }, [filtroCliente])
  useEffect(() => { localStorage.setItem('fin_periodo', filtroPeriodo) }, [filtroPeriodo])
  useEffect(() => { localStorage.setItem('fin_origem', filtroOrigem) }, [filtroOrigem])

  const [alterandoArquivo, setAlterandoArquivo] = useState(false)

  // Notas internas admin
  const [notaAdmin, setNotaAdmin] = useState('')
  const [notaAdminInicial, setNotaAdminInicial] = useState('')
  const [notaAdminUpdated, setNotaAdminUpdated] = useState(null)

  // Promover encomenda nao paga ao "Pedidos a Fornecedor" (admin only)
  const [promoverModal, setPromoverModal] = useState(null) // { enc, nota }
  const [promovendo, setPromovendo] = useState(false)
  const [savingNotaAdmin, setSavingNotaAdmin] = useState(false)
  const [notaAdminMsg, setNotaAdminMsg] = useState({ tipo: '', texto: '' })

  // Verificacao automatica do PDF da factura (compara NIF/nome/valores com encomenda)
  const [verifFatura, setVerifFatura] = useState(null) // { result, file } | null
  const [reverificando, setReverificando] = useState(false)

  // Mudança de estado (Marcar como Enviado / Concluir / Cancelar)
  const [mudandoEstado, setMudandoEstado] = useState(false)
  const [estadoMsg, setEstadoMsg] = useState({ tipo: '', texto: '' })

  useEffect(() => { carregar(); carregarIban() }, [])

  // Auto-abrir modal de pagamento quando se chega vindo do clique numa notificacao
  // (encomenda, comprovativo, fatura_falta, fatura_urgente, pagamento, etc.)
  useEffect(() => {
    if (!encomendas || encomendas.length === 0) return
    try {
      const raw = sessionStorage.getItem('notif_open')
      if (!raw) return
      const data = JSON.parse(raw)
      if (data?.table !== 'encomendas' || !data?.id) return
      sessionStorage.removeItem('notif_open')
      const enc = encomendas.find(e => e.id === data.id)
      if (enc) setTimeout(() => abrirModal(enc), 80)
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encomendas])

  const carregarIban = async () => {
    // Portes: mesma fonte que o Catalogo usa ao criar a encomenda
    const pt = await carregarPortes(supabase)
    setPortesCobrados(pt.cobrados); setPortesCusto(pt.custo)
    const { data } = await supabase.from('config_sistema').select('chave, valor').in('chave', ['iban', 'banco_nome', 'banco_titular'])
    if (data) {
      data.forEach(r => {
        if (r.chave === 'iban') setIban(r.valor || '')
        if (r.chave === 'banco_nome') setBancoNome(r.valor || '')
        if (r.chave === 'banco_titular') setBancoTitular(r.valor || '')
        // desconto_fornecedor_pct: a chave global deixou de ser lida. O
        // desconto e de cada fornecedor, na ficha dele.
      })
    }
  }

  // Recalcula o custo de cada artigo a partir do desconto do fornecedor DELE.
  // Substitui o aplicarDescontoFornecedor, que mandava uma percentagem unica
  // para a base de dados inteira — herdada de quando havia um fornecedor so.
  const recalcularCustos = async () => {
    if (!window.confirm(
      'Recalcular o preço de CUSTO de todos os artigos?\n\n' +
      'Cada artigo é recalculado com o desconto do fornecedor dele:\n' +
      '  preço de custo = preço fornecedor × (1 − desconto do fornecedor)\n\n' +
      'O preço de venda NÃO é alterado.'
    )) return
    setSavingDesconto(true)
    setDescontoMsg({ tipo: '', texto: '' })
    const { data, error } = await supabase.rpc('recalcular_custos_por_artigo')
    setSavingDesconto(false)
    if (error) {
      setDescontoMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
      return
    }
    setDescontoMsg({ tipo: 'ok', texto: `${data?.produtos_atualizados ?? '?'} artigos recalculados, cada um com o desconto do seu fornecedor.` })
    setTimeout(() => setDescontoMsg({ tipo: '', texto: '' }), 6000)
    carregar()
  }

  // O aplicarDescontoFornecedor foi daqui removido. Mandava uma percentagem
  // unica para a base de dados inteira, atraves da RPC recalcular_custos_fornecedor.
  // Substituido pelo recalcularCustos acima, que nao leva percentagem nenhuma.
  // A RPC antiga continua na base de dados e ja nao e chamada por ninguem:
  // se um dia for apagada, e por aqui que se procura quem a usava.

  const guardarIban = async () => {
    setSavingIban(true)
    const rows = [
      { chave: 'iban', valor: ibanEdit.trim() },
      { chave: 'banco_nome', valor: bancoNomeEdit.trim() },
      { chave: 'banco_titular', valor: bancoTitularEdit.trim() },
    ]
    const { error } = await supabase.from('config_sistema').upsert(rows, { onConflict: 'chave' })
    if (error) {
      setIbanMsg({ tipo: 'erro', texto: 'Erro ao guardar.' })
    } else {
      setIban(ibanEdit.trim())
      setBancoNome(bancoNomeEdit.trim())
      setBancoTitular(bancoTitularEdit.trim())
      setEditandoIban(false)
      setIbanMsg({ tipo: 'ok', texto: 'Dados bancários atualizados.' })
      setTimeout(() => setIbanMsg({ tipo: '', texto: '' }), 3000)
    }
    setSavingIban(false)
  }

  const carregar = async () => {
    setLoading(true)
    let query = supabase
      .from('encomendas')
      .select('*, encomenda_itens(quantidade, produto_nome, produto_ean, preco_unitario, transito_data_aplicada, produto_id, produto:produto_id(id, "Transito1_Qtd", "Transito1_Data", "Transito2_Qtd", "Transito2_Data", "Transito3_Qtd", "Transito3_Data", "Transito4_Qtd", "Transito4_Data", "Transito5_Qtd", "Transito5_Data"))')
      .order('created_at', { ascending: false })
    if (!isAdmin && perfil?.email) {
      query = query.eq('cliente_email', perfil.email)
    }
    const [{ data }, internosRes, particularesRes, portesRes, outraEntidadeRes, facturasRes, custosRes, descSupplierRes, facturasTransitoRes] = await Promise.all([
      query,
      supabase.from('perfis').select('id').eq('cliente_interno', true),
      supabase.from('perfis').select('id').eq('tipo_cliente', 'particular'),
      isAdmin ? supabase.from('perfis').select('id, portes_por_unidade, nif, desconto_percentagem, cliente_interno, sinal_minimo_pct') : Promise.resolve({ data: [] }),
      isAdmin ? supabase.from('perfis').select('id').eq('factura_outra_entidade', true) : Promise.resolve({ data: [] }),
      isAdmin ? supabase.from('compras_fornecedor').select('numero_fatura, data_envio_prevista, pago_1a_data, pago_1a_prestacao') : Promise.resolve({ data: [] }),
      // As tres leituras seguintes varrem o catalogo todo — tem de ser paginadas
      // (src/lerTudo.js), senao param nas primeiras 1000 linhas e os artigos de
      // fora ficam sem custo e sem desconto de fornecedor nas margens deste ecra.
      isAdmin ? lerTudo(() => supabase.from('produtos_internos').select('produto_nome, preco_custo, preco_fornecedor'), 'produto_nome') : Promise.resolve({ data: [] }),
      isAdmin ? lerTudo(() => supabase.from('Produtos').select('id, Nome, desconto_supplier_pct').not('desconto_supplier_pct', 'is', null), 'id') : Promise.resolve({ data: [] }),
      // Facturas fornecedor ainda em transito (stock nao chegou ao LOGNOW) - bloqueiam arquivo.
      lerTudo(() => supabase.from('Produtos').select('id, Transito1_Factura, Transito2_Factura, Transito3_Factura, Transito4_Factura, Transito5_Factura, Transito1_Qtd, Transito2_Qtd, Transito3_Qtd, Transito4_Qtd, Transito5_Qtd'), 'id'),
    ])
    // Condicoes de pagamento por fornecedor — o aviso do topo era escrito a
    // mao com as do Plus Skill e passou a mentir no dia em que entrou o Naypes.
    const { data: condForn } = await supabase.from('fornecedores_publicos')
      .select('id, prazo_envio_dias, exige_pagamento_total, min_encomenda_b2b')
      .eq('ativo', true)
    setCondicoesForn(condForn || [])
    // Minimo de unidades por fornecedor — decide quem entra na barra do pedido.
    // Vem da tabela e nao da vista publica: e informacao de compra, nao condicao
    // comercial, e este ecra so abre para admin.
    if (isAdmin) {
      const { data: fa } = await supabase.from('fornecedores')
        .select('id, nome, ativo, min_unidades_pedido, desconto_base_pct')
        .order('nome')
      setFornsAdmin(fa || [])
      setMinUnPorForn(new Map((fa || [])
        .filter(f => Number(f.min_unidades_pedido) > 0)
        .map(f => [String(f.id), Number(f.min_unidades_pedido)])))
    }
    // ─── Mapa Nome -> portes que PAGAMOS por unidade nesse artigo ─────────────
    // Era uma constante de 0,75 € para todos os artigos, como se todos os
    // fornecedores cobrassem transporte. No Naypes sao gratis, e somar-lhes
    // 0,75 € de custo mostrava aqui menos lucro do que existe — 75 centimos por
    // unidade vendida, que numa encomenda de mil unidades sao 750 €.
    //
    // Pagina de propriedade: sao 2.652 produtos e o Supabase devolve no maximo
    // 1000 linhas por consulta. Sem paginar, os artigos a partir da milesima
    // linha ficavam de fora do mapa e caiam no fallback — exactamente o bug que
    // ja apanhamos no catalogo e em duas sincronizacoes.
    const portesCustoPorNome = new Map()
    // Desconto de compra do FORNECEDOR de cada artigo. Serve de recurso quando o
    // proprio artigo nao tem desconto gravado — ver mais abaixo, onde antes se
    // assumiam 10% para toda a gente.
    const descBasePorNome = new Map()
    const fornecedorPorNome = new Map()
    if (isAdmin) {
      try {
        const { data: forns } = await supabase.from('fornecedores')
          .select('id, nome, portes_custo_por_unidade, desconto_base_pct')
        const portesPorForn = new Map((forns || [])
          .map(f => [String(f.id), f.portes_custo_por_unidade != null ? Number(f.portes_custo_por_unidade) : null]))
        const descPorForn = new Map((forns || [])
          .map(f => [String(f.id), f.desconto_base_pct != null ? Number(f.desconto_base_pct) : null]))
        // NOME CURTO, para caber na etiqueta sem cortar a meio.
        //
        // "Naypes Fragancias y Cosmeticos, S.L." vira "Naypes" e "PLUS SKILL
        // Ltd" vira "PLUS SKILL". A primeira palavra chega quase sempre; so
        // quando e curta demais para se perceber (o "PLUS") e que se leva a
        // segunda atras.
        const curto = (nome) => {
          const limpo = String(nome || '').split(',')[0].trim()
          // Sem expressao regular: uma barra invertida perdida aqui partia os
          // nomes nas letras erradas, e em silencio.
          const partes = limpo.split(" ").filter(Boolean)
          if (!partes[0]) return ''
          return partes[0].length >= 5 ? partes[0] : partes.slice(0, 2).join(' ')
        }
        const nomePorForn = new Map((forns || []).map(f => [String(f.id), curto(f.nome)]))
        for (let de = 0; ; de += 1000) {
          const r = await supabase.from('Produtos')
            .select('Nome, fornecedor_id').order('Nome').range(de, de + 999)
          if (r.error) break
          for (const p of (r.data || [])) {
            const v = portesPorForn.get(String(p.fornecedor_id))
            if (p.Nome && v != null) portesCustoPorNome.set(p.Nome, v)
            const d = descPorForn.get(String(p.fornecedor_id))
            if (p.Nome && d != null) descBasePorNome.set(p.Nome, d)
            const nf = nomePorForn.get(String(p.fornecedor_id))
            if (p.Nome && nf) fornecedorPorNome.set(p.Nome, nf)
          }
          if (!r.data || r.data.length < 1000) break
        }
      } catch { /* sem mapa, cada linha cai no valor de sempre */ }
    }
    setPortesCustoPorNome(portesCustoPorNome)
    setFornecedorPorNome(fornecedorPorNome)

    // Mapa Nome -> desconto_supplier_pct do produto (fallback global 10% aplicado no calculo)
    const descSupplierMap = new Map()
    for (const p of (descSupplierRes.data || [])) {
      if (p.Nome && p.desconto_supplier_pct != null && !descSupplierMap.has(p.Nome)) {
        descSupplierMap.set(p.Nome, Number(p.desconto_supplier_pct))
      }
    }
    const encsComQtd = (data || []).map(e => ({
      ...e,
      total_unidades: (e.encomenda_itens || []).reduce((s, i) => s + Number(i.quantidade || 0), 0),
    }))
    setEncomendas(encsComQtd)
    setInternosIds(new Set((internosRes.data || []).map(r => r.id)))
    setParticularesIds(new Set((particularesRes.data || []).map(r => r.id)))
    // Portes deixaram de variar por cliente — PORTES_COBRADOS/PORTES_CUSTO são iguais para todos.
    // O campo perfis.portes_por_unidade já não é lido aqui.
    setNifPorCliente(new Map((portesRes.data || []).filter(r => r.nif).map(r => [r.id, String(r.nif)])))
    // Necessarios para reconstruir a decomposicao do sinal por artigo — ver o
    // painel "Factura do sinal". Tem de reproduzir o calculo do RPC.
    setSinalPctPorCliente(new Map((portesRes.data || []).filter(r => r.sinal_minimo_pct != null).map(r => [r.id, Number(r.sinal_minimo_pct)])))
    setPortesPorCliente(new Map((portesRes.data || []).map(r => [r.id, Number(r.portes_por_unidade ?? 1)])))
    setFacturaOutraEntidadeIds(new Set((outraEntidadeRes.data || []).map(r => r.id)))
    setFacturasFornecedor(new Map((facturasRes.data || []).filter(r => r.numero_fatura).map(r => [String(r.numero_fatura), { data_envio_prevista: r.data_envio_prevista, pago_1a_data: r.pago_1a_data, pago_1a_prestacao: r.pago_1a_prestacao }])))
    // Custo por unidade = preco_fornecedor × (1 - desc_supplier_pct%) + portes
    // desc_supplier_pct vem do Produto (5%, 6%, ...) ou 10 por defeito
    setCustosPorNome(new Map((custosRes.data || []).filter(r => r.preco_custo != null || r.preco_fornecedor != null).map(r => [r.produto_nome, {
      preco_custo: r.preco_custo != null ? Number(r.preco_custo) : null,
      preco_fornecedor: r.preco_fornecedor != null ? Number(r.preco_fornecedor) : null,
      // TRES DEGRAUS, e a ordem e a que evita inventar dinheiro.
      //
      // 1) O desconto gravado no proprio artigo, que e o mais especifico.
      // 2) O desconto de compra do FORNECEDOR dele. Este degrau nao existia: um
      //    artigo do Naypes ou do Bplatz — que nao dao desconto nenhum — caia
      //    directamente nos 10% do Plus Skill se o nome nao batesse certo no
      //    mapa. E os nomes nao batem sempre: as sincronizacoes acrescentam o
      //    EAN ao nome dos repetidos para os tornar unicos.
      //    O custo aparecia 10% abaixo do real e o lucro 10% acima.
      // 3) So quando nem o fornecedor se sabe e que se cai nos 10 — que e o
      //    valor historico, de quando o Plus Skill era o unico fornecedor.
      desconto_supplier_pct: descSupplierMap.has(r.produto_nome)
        ? descSupplierMap.get(r.produto_nome)
        : (descBasePorNome.has(r.produto_nome) ? descBasePorNome.get(r.produto_nome) : 10),
    }])))
    // Facturas fornecedor ainda em transito - so conta se ainda houver qtd > 0
    const facsTransito = new Set()
    for (const p of (facturasTransitoRes.data || [])) {
      for (const n of SLOTS_TRANSITO) {
        if (Number(p[`Transito${n}_Qtd`] || 0) > 0 && p[`Transito${n}_Factura`]) facsTransito.add(String(p[`Transito${n}_Factura`]))
      }
    }
    setFacturasEmTransito(facsTransito)
    setLoading(false)
  }

  const abrirModal = async (enc) => {
    setModalPag(enc)
    setPortesInput(enc.portes > 0 ? String(enc.portes) : '')
    setPortesMsg({ tipo: '', texto: '' })
    setFaturaForm({
      numero: enc.fatura_numero || '',
      data: enc.fatura_emitida_em || new Date().toISOString().slice(0, 10),
      notas: enc.fatura_notas || '',
    })
    // Sem isto o numero da factura do sinal da encomenda anterior ficava no
    // campo ao abrir a seguinte.
    setFaturaSinalForm({
      numero: enc.fatura_sinal_numero || '',
      data: enc.fatura_sinal_emitida_em || new Date().toISOString().slice(0, 10),
    })
    setFaturaMsg({ tipo: '', texto: '' })
    setFornFatura(enc.fornecedor_fatura_numero || '')
    setFornFaturaSaved(enc.fornecedor_fatura_numero || '')
    setFornMsg({ tipo: '', texto: '' })
    setTrackingForm({
      codigo: enc.tracking || '',
      transportadora: enc.transportadora || '',
      data: enc.data_envio ? new Date(enc.data_envio).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
    })
    setTrackingMsg({ tipo: '', texto: '' })
    setIrmasInfo(null)
    setConfirmPropagar(false)
    // Detectar encomendas irmas (mesma compra ao fornecedor / mesma factura fisica)
    ;(async () => {
      try {
        const { data: link } = await supabase
          .from('compras_fornecedor_encomendas')
          .select('compra_id')
          .eq('encomenda_id', enc.id)
          .limit(1)
        const compraId = link && link[0]?.compra_id
        if (!compraId) return
        const [{ data: outros }, { data: cfRow }] = await Promise.all([
          supabase
            .from('compras_fornecedor_encomendas')
            .select('encomenda_id, encomendas:encomenda_id(numero_encomenda, cliente_nome, cliente_email)')
            .eq('compra_id', compraId)
            .neq('encomenda_id', enc.id),
          supabase
            .from('compras_fornecedor')
            .select('numero_fatura, numero_interno')
            .eq('id', compraId)
            .single(),
        ])
        const irmas = (outros || []).map(r => ({
          id: r.encomenda_id,
          numero: r.encomendas?.numero_encomenda || r.encomenda_id.slice(0,8).toUpperCase(),
          cliente: r.encomendas?.cliente_nome || r.encomendas?.cliente_email || '—',
        }))
        if (irmas.length > 0) {
          setIrmasInfo({
            compraId,
            numeroFatura: cfRow?.numero_fatura || cfRow?.numero_interno || '—',
            count: irmas.length,
            irmas,
          })
        }
      } catch (e) {
        console.warn('Erro a detectar encomendas irmas:', e)
      }
    })()
    setModalItens([])
    setModalItensLoading(true)
    setNotaAdmin('')
    setNotaAdminInicial('')
    setNotaAdminUpdated(null)
    setNotaAdminMsg({ tipo: '', texto: '' })
    const { data } = await supabase
      .from('encomenda_itens')
      .select('*')
      .eq('encomenda_id', enc.id)
      .order('created_at')
    setModalItens(data || [])
    setModalItensLoading(false)
    if (isAdmin) {
      const { data: nota } = await supabase
        .from('encomenda_notas_admin')
        .select('texto, updated_at')
        .eq('encomenda_id', enc.id)
        .maybeSingle()
      const txt = nota?.texto || ''
      setNotaAdmin(txt)
      setNotaAdminInicial(txt)
      setNotaAdminUpdated(nota?.updated_at || null)
    }
  }

  const guardarNotaAdmin = async () => {
    if (!modalPag || !isAdmin) return
    setSavingNotaAdmin(true)
    setNotaAdminMsg({ tipo: '', texto: '' })
    try {
      const texto = notaAdmin
      const { data: { user } } = await supabase.auth.getUser()
      const { data, error } = await supabase
        .from('encomenda_notas_admin')
        .upsert({
          encomenda_id: modalPag.id,
          texto,
          updated_by: user?.id || null,
        }, { onConflict: 'encomenda_id' })
        .select('texto, updated_at')
        .single()
      if (error) throw error
      setNotaAdminInicial(data?.texto || '')
      setNotaAdminUpdated(data?.updated_at || null)
      setNotaAdminMsg({ tipo: 'ok', texto: 'Nota guardada.' })
      setTimeout(() => setNotaAdminMsg({ tipo: '', texto: '' }), 3000)
    } catch (err) {
      setNotaAdminMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setSavingNotaAdmin(false)
    }
  }

  const enviarTracking = async (modo = 'auto') => {
    // modo: 'auto' = decidir conforme irmas; 'so_esta' = nao propagar; 'todas' = propagar
    if (!modalPag) return
    const codigo = trackingForm.codigo.replace(/\s+/g, '').trim()
    if (!codigo) { setTrackingMsg({ tipo: 'erro', texto: 'Código de tracking obrigatório.' }); return }
    // Se ha irmas e o admin ainda nao decidiu, pedir confirmacao
    if (modo === 'auto' && irmasInfo && irmasInfo.count > 0) {
      setConfirmPropagar(true)
      return
    }
    const propagar = modo === 'todas'
    setEnviandoTracking(true)
    setTrackingMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/notificar-tracking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          encomenda_id: modalPag.id,
          tracking: codigo,
          transportadora: trackingForm.transportadora.trim() || null,
          data_envio: trackingForm.data || null,
          marcar_enviado: true,
          propagar_irmas: !!propagar,
        }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'erro desconhecido')
      const trackingValores = {
        tracking: codigo,
        transportadora: trackingForm.transportadora.trim() || null,
        data_envio: trackingForm.data || new Date().toISOString().slice(0,10),
        estado: 'enviado',
      }
      const atualizado = { ...modalPag, ...trackingValores }
      setModalPag(atualizado)
      // Atualizar lista local: a propria + irmas (se propagado)
      const idsAfectados = new Set([modalPag.id])
      if (propagar && irmasInfo) irmasInfo.irmas.forEach(i => idsAfectados.add(i.id))
      setEncomendas(prev => prev.map(e => idsAfectados.has(e.id) ? { ...e, ...trackingValores } : e))
      const okN = data.sucesso || 1
      const totalN = data.total || 1
      const txt = data.propagado
        ? `Tracking aplicado a ${okN}/${totalN} encomendas. Emails enviados aos clientes.`
        : `Email enviado para ${modalPag.cliente_email}.`
      setTrackingMsg({ tipo: 'ok', texto: txt })
      setConfirmPropagar(false)
      setTimeout(() => setTrackingMsg({ tipo: '', texto: '' }), 5000)
    } catch (err) {
      setTrackingMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setEnviandoTracking(false)
    }
  }

  // Editar quantidades — SÓ em encomendas de contas internas (ex: Cátia), e só
  // enquanto ainda não seguiram para o fornecedor. Num cliente real isto seria
  // perigoso: já há factura e valor combinado. Numa conta interna não há ninguém
  // do outro lado — é a nossa própria compra.
  //
  // Todo o trabalho é feito na função editar_itens_encomenda_interna: os totais,
  // a regra de IVA 0% das contas internas e o reverter/reaplicar dos trânsitos
  // têm de acontecer juntos ou não acontecer de todo. Feito daqui aos bocados,
  // uma falha a meio deixava o stock desalinhado.
  // Conta interna: livre, não afecta ninguém.
  // Cliente real: só como excepção (ex: ruptura de stock no fornecedor), exige
  // motivo escrito, e nunca depois da factura emitida — aí é nota de crédito.
  const editarInterno = (enc) =>
    isAdmin && !!enc && internosIds.has(enc.cliente_id)
    && !enc.encomendado_fornecedor_em
    && !['cancelado', 'concluido'].includes(enc.estado)

  const editarCliente = (enc) =>
    isAdmin && !!enc && !internosIds.has(enc.cliente_id)
    && !enc.fatura_emitida_em
    && !['cancelado', 'concluido'].includes(enc.estado)

  const podeEditarQtds = (enc) => editarInterno(enc) || editarCliente(enc)

  // ── QUANTAS UNIDADES PARA A FACTURA FINAL ────────────────────────────────
  //
  // O sinal ja tem o seu bloco, com CEIL: nunca abaixo dos 8 por cento, porque
  // e o que garante a encomenda. A factura final e o contrario — arredonda para
  // BAIXO — e a razao esta no programa de facturacao do Marco: ele deixa somar
  // uma linha de ajuste, nunca subtrair. Uma factura que passe do valor em
  // divida so se corrigiria com um desconto, e um desconto mexe no preco
  // unitario que o cliente ve.
  //
  // O ponto de partida sao as unidades que sobram do sinal. Quando a encomenda
  // nao foi mexida, isso da o valor exacto. Quando foi — a 0026/2026 perdeu um
  // artigo inteiro DEPOIS de o sinal estar facturado — sobram unidades a mais
  // para o dinheiro que falta, e e preciso cortar.
  //
  // Corta-se a mais cara primeiro: tira menos unidades para o mesmo efeito, e
  // deixa o resto da encomenda mais parecido com o que o cliente comprou.
  const facturaFinal = useMemo(() => {
    if (!modalPag || modalPag.sinal_escolhido == null) return null
    const itens = modalPag.encomenda_itens || []
    if (!itens.length) return null
    const pct = sinalPctPorCliente.get(modalPag.cliente_id)
    const portes = portesPorCliente.get(modalPag.cliente_id) ?? 1
    const desc = Number(modalPag.desconto_percentagem || 0)
    const iva = internosIds.has(modalPag.cliente_id) ? 0 : 0.23
    const total = Number(modalPag.total_final ?? modalPag.total_com_iva ?? 0)
    // So se desconta o sinal se ele ja foi mesmo facturado. Enquanto nao o for,
    // a factura final e a encomenda toda.
    const sinalFacturado = modalPag.fatura_sinal_pdf_url ? Number(modalPag.sinal_escolhido) : 0
    const emDivida = Math.round((total - sinalFacturado) * 100) / 100
    if (!(emDivida > 0.01)) return null

    // TUDO EM VALORES SEM IVA, porque e assim que a factura e feita.
    //
    // A primeira versao dava o ajuste com IVA incluido. O Sage trabalha sem
    // IVA — a caixa "este documento tem IVA incluido" esta desmarcada — e
    // acrescentou 23 por cento a um numero que ja os tinha: 15,72 viraram
    // 19,34 e a factura saiu 3,56 EUR acima do devido.
    //
    // O preco unitario tambem se arredonda aqui aos centimos, que e o que se
    // escreve no Sage. Se se trabalhasse com o valor cheio, o total calculado
    // ficava uns centimos ao lado do que a factura vai mostrar.
    const alvoSemIva = iva > 0
      ? Math.round((emDivida / (1 + iva)) * 100) / 100
      : emDivida

    const linhas = itens.map(it => {
      const qtd = Number(it.quantidade || 0)
      const pu = Number(it.preco_unitario || 0)
      // O desconto nao incide sobre o euro de portes embutido no preco.
      const unitSemIva = Math.round((pu - Math.max(0, pu - portes) * (desc / 100)) * 100) / 100
      const noSinal = (pct && sinalFacturado > 0) ? Math.ceil(qtd * pct / 100) : 0
      return {
        nome: it.produto_nome, ean: it.produto_ean,
        qtd, noSinal, un: Math.max(0, qtd - noSinal),
        unitSemIva, unitComIva: Math.round(unitSemIva * (1 + iva) * 100) / 100,
      }
    }).filter(l => l.unitSemIva > 0)

    let soma = linhas.reduce((s, l) => s + l.un * l.unitSemIva, 0)
    let guarda = 0
    while (soma > alvoSemIva + 0.005 && guarda++ < 20000) {
      const cand = linhas.filter(l => l.un > 0).sort((a, b) => b.unitSemIva - a.unitSemIva)[0]
      if (!cand) break
      cand.un -= 1
      soma -= cand.unitSemIva
    }
    soma = Math.round(soma * 100) / 100
    const ajuste = Math.round((alvoSemIva - soma) * 100) / 100
    const totalSemIva = Math.round((soma + ajuste) * 100) / 100
    const valorIva = Math.round(totalSemIva * iva * 100) / 100
    const totalComIva = Math.round((totalSemIva + valorIva) * 100) / 100
    const usadas = linhas.filter(l => l.un > 0)

    // O AJUSTE VAI NOS DOIS FORMATOS, e cada um e calculado a partir da sua
    // propria soma. Nao e o mesmo numero multiplicado por 1,23: os precos
    // unitarios sao arredondados aos centimos em cada formato, e a diferenca
    // acumula-se ao longo das unidades. Aqui, 12,83 s/IVA e 15,89 c/IVA — e nao
    // 15,78 — e sao os dois que fazem a factura fechar nos 2 159,15.
    //
    // Assim funciona com a caixa "este documento tem IVA incluido" marcada ou
    // desmarcada, sem ninguem ter de fazer a conversao a mao. Foi a passar um
    // valor com IVA para um documento sem IVA que a primeira factura saiu
    // 3,56 EUR acima.
    const somaComIva = Math.round(usadas.reduce((sm, l) => sm + l.un * l.unitComIva, 0) * 100) / 100
    const ajusteComIva = Math.round((emDivida - somaComIva) * 100) / 100
    return {
      linhas: usadas,
      emDivida,
      soma,
      ajuste,
      totalSemIva,
      valorIva,
      totalComIva,
      somaComIva, ajusteComIva,
      cortou: linhas.some(l => l.un < l.qtd - l.noSinal),
      iva,
      texto: [
        `FACTURA FINAL - ${modalPag.numero_encomenda || ''}`,
        'Valores SEM IVA - o Sage acrescenta o IVA por cima.',
        ...usadas.map(l => `${l.un} un  ${l.nome}  ${fmt2(l.unitSemIva)} EUR/un`),
        `Ajustes  1 un  ${fmt2(ajuste)} EUR s/IVA  (${fmt2(Math.round((emDivida - Math.round(usadas.reduce((sm, l) => sm + l.un * l.unitComIva, 0) * 100) / 100) * 100) / 100)} EUR c/IVA)`,
        `Total s/IVA: ${fmt2(totalSemIva)} EUR`,
        iva > 0 ? `IVA 23%: ${fmt2(valorIva)} EUR` : '',
        `TOTAL: ${fmt2(totalComIva)} EUR`,
      ].join('\n'),
    }
  }, [modalPag, sinalPctPorCliente, portesPorCliente, internosIds])

  const guardarQuantidades = async () => {
    if (!modalPag) return
    const alteracoes = modalItens
      .filter(it => qtdsEditadas[it.id] != null && Number(qtdsEditadas[it.id]) !== Number(it.quantidade))
      .map(it => ({ item_id: it.id, quantidade: Number(qtdsEditadas[it.id]) }))
    if (alteracoes.length === 0) { setEditandoQtds(false); return }
    if (alteracoes.some(a => !Number.isInteger(a.quantidade) || a.quantidade < 0)) {
      setErroQtds('As quantidades têm de ser números inteiros e não negativos.')
      return
    }
    const resumo = alteracoes.map(a => {
      const it = modalItens.find(x => x.id === a.item_id)
      return `• ${it?.produto_nome}: ${it?.quantidade} → ${a.quantidade}`
    }).join('\n')

    const ehCliente = editarCliente(modalPag)
    let motivo = null
    if (ehCliente) {
      // Encomenda de um cliente real: tem de ficar registado porquê.
      motivo = window.prompt(
        `Alterar a encomenda ${modalPag.numero_encomenda} de ${modalPag.cliente_nome}?\n\n${resumo}\n\n` +
        `Isto muda o que o cliente recebe e o que ainda tem a pagar.\n` +
        `Escreve o motivo (fica registado na encomenda):`,
        'Ruptura de stock no fornecedor'
      )
      if (motivo == null || motivo.trim().length < 5) {
        if (motivo != null) setErroQtds('Motivo demasiado curto.')
        return
      }
    } else {
      if (!window.confirm(`Alterar quantidades da encomenda ${modalPag.numero_encomenda}?\n\n${resumo}\n\nOs totais e os trânsitos são recalculados.`)) return
    }

    setGuardandoQtds(true); setErroQtds('')
    try {
      const { data, error } = ehCliente
        ? await supabase.rpc('editar_itens_encomenda_cliente', {
            p_encomenda_id: modalPag.id, p_itens: alteracoes, p_motivo: motivo.trim(),
          })
        : await supabase.rpc('editar_itens_encomenda_interna', {
            p_encomenda_id: modalPag.id, p_itens: alteracoes,
          })
      if (error) throw new Error(error.message)
      // Conferência do que mudou no pagamento — é o que interessa saber a seguir
      if (ehCliente && data) {
        setResultadoEdicao(data)
      }
      setEditandoQtds(false)
      setQtdsEditadas({})
      const { data: novos } = await supabase
        .from('encomenda_itens').select('*')
        .eq('encomenda_id', modalPag.id).order('created_at')
      setModalItens(novos || [])
      await carregar()
      setModalPag(prev => prev ? {
        ...prev,
        total_itens: data?.linhas ?? prev.total_itens,
        total_sem_iva: data?.total_sem_iva ?? prev.total_sem_iva,
        total_final: data?.total_final ?? prev.total_final,
        total_com_iva: data?.total_final ?? prev.total_com_iva,
      } : prev)
    } catch (e) {
      setErroQtds(e.message || String(e))
    } finally {
      setGuardandoQtds(false)
    }
  }

  // Toggle "encomendado ao fornecedor" directo da listagem (1 clique).
  // Auto-mark tambem corre por trigger ao alocar em compras_fornecedor_encomendas.
  const toggleEncomendadoFornecedor = async (enc, ev) => {
    ev?.stopPropagation()
    if (!isAdmin) return
    const novoValor = enc.encomendado_fornecedor_em ? null : new Date().toISOString()
    if (novoValor === null) {
      const ok = window.confirm(`Desmarcar a encomenda ${enc.numero_encomenda || ''} como encomendada ao fornecedor?`)
      if (!ok) return
    }
    const { data: rows, error } = await supabase.from('encomendas')
      .update({ encomendado_fornecedor_em: novoValor })
      .eq('id', enc.id)
      .select('id')
    if (error) { window.alert('Erro: ' + error.message); return }
    if (!rows || rows.length === 0) { window.alert('Sessão expirada — recarregue.'); return }
    setEncomendas(prev => prev.map(e => e.id === enc.id ? { ...e, encomendado_fornecedor_em: novoValor } : e))
  }

  // Promover encomenda nao paga (1a) para Pedidos a Fornecedor com nota opcional
  const promoverPedido = async () => {
    if (!isAdmin || !promoverModal?.enc) return
    setPromovendo(true)
    const nowIso = new Date().toISOString()
    const { data: rows, error } = await supabase.from('encomendas')
      .update({
        promovido_pedido_em: nowIso,
        promovido_pedido_nota: (promoverModal.nota || '').trim() || null,
        promovido_pedido_por: perfil?.id || null,
      })
      .eq('id', promoverModal.enc.id)
      .select('id')
    setPromovendo(false)
    if (error) { window.alert('Erro: ' + error.message); return }
    if (!rows || rows.length === 0) { window.alert('Sessão expirada — recarregue.'); return }
    setEncomendas(prev => prev.map(e => e.id === promoverModal.enc.id ? {
      ...e,
      promovido_pedido_em: nowIso,
      promovido_pedido_nota: (promoverModal.nota || '').trim() || null,
    } : e))
    setPromoverModal(null)
  }

  const despromoverPedido = async (enc) => {
    if (!isAdmin) return
    const ok = window.confirm(`Despromover a encomenda ${enc.numero_encomenda || ''}? Volta a ficar invisível em "Pedidos a Fornecedor" até ser paga.`)
    if (!ok) return
    const { data: rows, error } = await supabase.from('encomendas')
      .update({ promovido_pedido_em: null, promovido_pedido_nota: null, promovido_pedido_por: null })
      .eq('id', enc.id)
      .select('id')
    if (error) { window.alert('Erro: ' + error.message); return }
    if (!rows || rows.length === 0) { window.alert('Sessão expirada — recarregue.'); return }
    setEncomendas(prev => prev.map(e => e.id === enc.id ? { ...e, promovido_pedido_em: null, promovido_pedido_nota: null } : e))
  }

  const guardarFornFatura = async () => {
    if (!modalPag) return
    const val = fornFatura.trim() || null
    if (val === (fornFaturaSaved || null)) return
    const eraVazio = !fornFaturaSaved
    setSavingForn(true)
    const { error } = await supabase.from('encomendas')
      .update({ fornecedor_fatura_numero: val })
      .eq('id', modalPag.id)
    setSavingForn(false)
    if (error) {
      setFornMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
      return
    }
    setFornFaturaSaved(val || '')
    const atualizado = { ...modalPag, fornecedor_fatura_numero: val }
    setModalPag(atualizado)
    setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))

    // Auto-aplicar transitos se era a primeira vez a linkar a uma factura fornecedor
    // e os transitos ainda nao tinham sido aplicados.
    if (eraVazio && val && !modalPag.transitos_aplicados) {
      try {
        const { error: rpcErr } = await supabase.rpc('reaplicar_transitos_encomenda', { p_enc_id: modalPag.id })
        if (!rpcErr) {
          setModalPag(prev => ({ ...prev, transitos_aplicados: true }))
          setEncomendas(prev => prev.map(e => e.id === modalPag.id ? { ...e, transitos_aplicados: true } : e))
          setFornMsg({ tipo: 'ok', texto: 'Guardado + trânsitos auto-aplicados.' })
        } else {
          setFornMsg({ tipo: 'ok', texto: 'Guardado (trânsitos não aplicados: ' + rpcErr.message + ')' })
        }
      } catch {
        setFornMsg({ tipo: 'ok', texto: 'Guardado.' })
      }
    } else {
      setFornMsg({ tipo: 'ok', texto: 'Guardado.' })
    }
    setTimeout(() => setFornMsg({ tipo: '', texto: '' }), 3000)

    // Propagar a outras encomendas elegíveis (so se era a primeira vez a preencher).
    // Inclui internos (ex: Catia) - se nao linkar, os triggers de transito do
    // fornecedor nao disparam e o stock dela nao actualiza correctamente.
    if (eraVazio && val) {
      const dataRef = new Date(modalPag.created_at)
      const ms45dias = 45 * 24 * 60 * 60 * 1000
      const candidates = encomendas.filter(e =>
        e.id !== modalPag.id &&
        !e.fornecedor_fatura_numero &&
        e.estado !== 'concluido' && e.estado !== 'cancelado' &&
        Math.abs(new Date(e.created_at).getTime() - dataRef.getTime()) <= ms45dias
      )
      if (candidates.length > 0) {
        setPropagarForn({
          numero: val,
          candidates: candidates.map(e => ({
            id: e.id,
            numero_encomenda: e.numero_encomenda || e.id.slice(0, 8),
            cliente_nome: e.cliente_nome || e.cliente_email,
            interno: internosIds.has(e.cliente_id),
            marcada: true,
          })),
        })
      }
    }
  }

  const aplicarPropagacaoForn = async () => {
    if (!propagarForn || aplicandoPropagacao) return
    const ids = propagarForn.candidates.filter(c => c.marcada).map(c => c.id)
    if (ids.length === 0) { setPropagarForn(null); return }
    setAplicandoPropagacao(true)
    const { error } = await supabase.from('encomendas')
      .update({ fornecedor_fatura_numero: propagarForn.numero })
      .in('id', ids)
    if (error) { setAplicandoPropagacao(false); window.alert('Erro: ' + error.message); return }
    setEncomendas(prev => prev.map(e =>
      ids.includes(e.id) ? { ...e, fornecedor_fatura_numero: propagarForn.numero } : e
    ))
    // Auto-aplicar transitos nas que ainda nao tinham (evita incoerencia)
    let transAplicados = 0
    for (const eId of ids) {
      const enc = encomendas.find(x => x.id === eId)
      if (enc && enc.transitos_aplicados) continue
      try {
        const { error: rpcErr } = await supabase.rpc('reaplicar_transitos_encomenda', { p_enc_id: eId })
        if (!rpcErr) transAplicados++
      } catch { /* continua */ }
    }
    setAplicandoPropagacao(false)
    setFornMsg({ tipo: 'ok', texto: `Nº ${propagarForn.numero} aplicado a ${ids.length} encomenda(s). ${transAplicados} trânsito(s) auto-aplicados.` })
    setTimeout(() => setFornMsg({ tipo: '', texto: '' }), 5000)
    setPropagarForn(null)
  }

  // NOTA: existia aqui um `guardarFatura` que gravava numero/data/notas da
  // factura. Ficou orfão — nenhum botão o chamava. Esses campos passaram a ser
  // gravados no fluxo de upload do PDF (ver mais abaixo), que os lê do mesmo
  // faturaForm. Removido em 06/08/2026.

  const removerFatura = async () => {
    if (!modalPag) return
    if (!window.confirm('Remover a fatura desta encomenda? O PDF será apagado do servidor e o cliente deixa de a poder descarregar.')) return
    setSavingFatura(true)
    try {
      if (modalPag.fatura_pdf_url) {
        await supabase.storage.from('faturas').remove([modalPag.fatura_pdf_url])
      }
      const update = { fatura_numero: null, fatura_emitida_em: null, fatura_notas: null, fatura_pdf_url: null }
      const { error } = await supabase.from('encomendas').update(update).eq('id', modalPag.id)
      if (error) throw error
      const atualizado = { ...modalPag, ...update }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
      setFaturaForm({ numero: '', data: new Date().toISOString().slice(0, 10), notas: '' })
      setFaturaMsg({ tipo: 'ok', texto: 'Fatura removida.' })
      setTimeout(() => setFaturaMsg({ tipo: '', texto: '' }), 3000)
    } catch (err) {
      setFaturaMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setSavingFatura(false)
    }
  }

  // Upload do PDF da fatura -> verifica, guarda no storage, regista, envia email ao cliente
  // Step 1: validacoes basicas + verifyFaturaClientePdf (parser local)
  const uploadFaturaPDF = async (file) => {
    if (!modalPag || !file) return
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setFaturaMsg({ tipo: 'erro', texto: 'Apenas ficheiros PDF.' }); return
    }
    if (file.size > 10 * 1024 * 1024) {
      setFaturaMsg({ tipo: 'erro', texto: 'PDF demasiado grande (max 10 MB).' }); return
    }
    if (!modalPag.cliente_id) {
      setFaturaMsg({ tipo: 'erro', texto: 'Encomenda sem cliente associado.' }); return
    }
    // Auto-preencher Nº fatura + data a partir do PDF se admin nao tiver preenchido
    let numeroFinal = (faturaForm.numero || '').trim()
    let dataFinal = (faturaForm.data || '').trim()
    if (!numeroFinal || !dataFinal) {
      setFaturaMsg({ tipo: 'info', texto: 'A ler PDF para detectar nº fatura e data...' })
      const cabecalho = await extrairCabecalhoFaturaPdf(file)
      if (!numeroFinal && cabecalho.numero_fatura) numeroFinal = cabecalho.numero_fatura
      if (!dataFinal && cabecalho.data_emissao) dataFinal = cabecalho.data_emissao
      if (!numeroFinal) {
        setFaturaMsg({ tipo: 'erro', texto: 'Nº fatura nao detectado no PDF. Preenche manualmente antes de carregar (ex: FT 2024/933).' })
        return
      }
      // Atualizar form para refletir o detectado (vai aparecer no input)
      setFaturaForm(f => ({ ...f, numero: numeroFinal, data: dataFinal || f.data }))
    }
    setUploadingFatura(true)
    const ignorarNomeNif = facturaOutraEntidadeIds.has(modalPag.cliente_id)
    setFaturaMsg({ tipo: 'info', texto: ignorarNomeNif ? 'A verificar PDF (apenas valores — cliente facturado a outra entidade)...' : 'A verificar PDF (nome, NIF, valores)...' })
    const interno = internosIds.has(modalPag.cliente_id)
    // NIF para localizar cliente no PDF: encomenda.entrega_nif > perfis.nif do cliente
    const nifEsperado = modalPag.entrega_nif || nifPorCliente.get(modalPag.cliente_id) || ''
    let result = null
    try {
      // Se o sinal ja foi facturado a parte, ESTA factura e so do restante.
      // Sem isto o verificador esperava o total da encomenda e acusava
      // discrepancia em todas as facturas finais de quem paga em duas.
      const sinalFacturado = modalPag.fatura_sinal_pdf_url && modalPag.sinal_escolhido != null
        ? Number(modalPag.sinal_escolhido) : 0
      const totalEsperado = sinalFacturado > 0
        ? Number((Number(modalPag.total_final || 0) - sinalFacturado).toFixed(2))
        : null
      result = await verifyFaturaClientePdf(file, modalPag, interno, { ignorarNomeNif, nifEsperado, totalEsperado })
    } catch (e) {
      result = { ok: false, ilegivel: true, problemas: [{ campo: 'PDF', esperado: '', encontrado: 'Erro ao parsear: ' + (e?.message || String(e)) }], extraido: {}, esperado: {} }
    }
    if (!result.ok) {
      // Bloqueia: mostra modal de confirmacao com lado-a-lado
      setUploadingFatura(false)
      setFaturaMsg({ tipo: '', texto: '' })
      setVerifFatura({ result, file })
      return
    }
    // OK: prosseguir com o upload + envio (toast verde)
    setFaturaMsg({ tipo: 'ok', texto: '✓ Verificação OK — a enviar...' })
    await uploadFaturaPDFConfirmed(file, /* verificada */ true, { numero: numeroFinal, data: dataFinal })
  }

  // Step 2: upload propriamente dito + envio email
  // verificada=true → verificacao automatica passou; grava fatura_verificada_em
  // verificada=false → admin escolheu "Continuar mesmo assim" no modal de discrepancia
  // overrides: { numero, data } - usa estes valores em vez de faturaForm (caso o setState ainda nao se aplicou)
  // Upload da factura do SINAL. Guarda nas colunas fatura_sinal_* e num caminho
  // proprio, para nunca sobrepor a factura final da mesma encomenda.
  const uploadFaturaSinal = async (file) => {
    if (!modalPag || !file) return
    setUploadingFaturaSinal(true)
    try {
      const numero = (faturaSinalForm.numero || '').trim()
      const data = (faturaSinalForm.data || '').trim() || new Date().toISOString().slice(0, 10)
      const path = `${modalPag.cliente_id}/${modalPag.id}-sinal.pdf`
      const { error: upErr } = await supabase.storage.from('faturas').upload(path, file, { upsert: true, contentType: 'application/pdf' })
      if (upErr) throw new Error('Upload: ' + upErr.message)
      const update = {
        fatura_sinal_pdf_url: path,
        fatura_sinal_emitida_em: data,
        fatura_sinal_numero: numero || null,
      }
      const { error: dbErr } = await supabase.from('encomendas').update(update).eq('id', modalPag.id)
      if (dbErr) throw new Error('BD: ' + dbErr.message)
      const atualizado = { ...modalPag, ...update }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
      setFaturaSinalForm({ numero: numero, data })
    } catch (e) {
      alert('Erro: ' + (e?.message || String(e)))
    } finally {
      setUploadingFaturaSinal(false)
    }
  }

  const removerFaturaSinal = async () => {
    if (!modalPag?.fatura_sinal_pdf_url) return
    if (!confirm('Remover a factura do sinal desta encomenda?')) return
    try {
      await supabase.storage.from('faturas').remove([modalPag.fatura_sinal_pdf_url])
      const update = { fatura_sinal_pdf_url: null, fatura_sinal_emitida_em: null, fatura_sinal_numero: null, fatura_sinal_verificada_em: null }
      await supabase.from('encomendas').update(update).eq('id', modalPag.id)
      const atualizado = { ...modalPag, ...update }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
      setFaturaSinalForm({ numero: '', data: '' })
    } catch (e) {
      alert('Erro: ' + (e?.message || String(e)))
    }
  }

  const uploadFaturaPDFConfirmed = async (file, verificada = false, overrides = {}) => {
    if (!modalPag || !file) return
    setUploadingFatura(true)
    try {
      const numeroEfectivo = (overrides.numero || faturaForm.numero || '').trim()
      const dataEfectiva = (overrides.data || faturaForm.data || '').trim()
      const numeroSafe = (numeroEfectivo || `enc-${modalPag.id.slice(0,8)}`).replace(/[^a-zA-Z0-9_-]/g, '_')
      const path = `${modalPag.cliente_id}/${modalPag.id}-${numeroSafe}.pdf`
      const { error: upErr } = await supabase.storage.from('faturas').upload(path, file, { upsert: true, contentType: 'application/pdf' })
      if (upErr) throw new Error('Upload: ' + upErr.message)

      const update = {
        fatura_pdf_url: path,
        fatura_emitida_em: dataEfectiva || new Date().toISOString().slice(0, 10),
        fatura_numero: numeroEfectivo || modalPag.fatura_numero || null,
        fatura_notas: faturaForm.notas.trim() || null,
        fatura_verificada_em: verificada ? new Date().toISOString() : null,
      }
      const { error: dbErr } = await supabase.from('encomendas').update(update).eq('id', modalPag.id)
      if (dbErr) throw new Error('BD: ' + dbErr.message)

      const atualizado = { ...modalPag, ...update }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
      setFaturaForm(f => ({ ...f, data: update.fatura_emitida_em, numero: update.fatura_numero || '' }))

      // Disparar email automatico ao cliente
      setEnviandoFaturaEmail(true)
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/enviar-fatura-cliente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ encomenda_id: modalPag.id }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error('Email: ' + (data.erro || 'erro'))
      setFaturaMsg({ tipo: 'ok', texto: `✓ Fatura verificada e enviada por email para ${modalPag.cliente_email}.` })
      setTimeout(() => setFaturaMsg({ tipo: '', texto: '' }), 6000)
    } catch (err) {
      setFaturaMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setUploadingFatura(false)
      setEnviandoFaturaEmail(false)
    }
  }

  // Re-verifica o PDF ja carregado (retroactivas ou facturas onde a verificacao foi ignorada).
  // Faz download do PDF do storage, corre verifyFaturaClientePdf e:
  //   - OK    -> grava fatura_verificada_em = now()
  //   - falha -> abre modal de discrepancia (sem reenviar email, ja foi enviado)
  const reverificarFatura = async () => {
    if (!modalPag?.fatura_pdf_url || reverificando) return
    setReverificando(true)
    setFaturaMsg({ tipo: 'info', texto: 'A re-verificar PDF (nome, NIF, valores)...' })
    try {
      const { data: blob, error } = await supabase.storage.from('faturas').download(modalPag.fatura_pdf_url)
      if (error) throw new Error('Download PDF: ' + error.message)
      const interno = internosIds.has(modalPag.cliente_id)
      const ignorarNomeNif = facturaOutraEntidadeIds.has(modalPag.cliente_id)
      const nifEsperado = modalPag.entrega_nif || nifPorCliente.get(modalPag.cliente_id) || ''
      const result = await verifyFaturaClientePdf(blob, modalPag, interno, { ignorarNomeNif, nifEsperado })
      if (result.ok) {
        const nowIso = new Date().toISOString()
        const { error: dbErr } = await supabase.from('encomendas').update({ fatura_verificada_em: nowIso }).eq('id', modalPag.id)
        if (dbErr) throw new Error('BD: ' + dbErr.message)
        const atualizado = { ...modalPag, fatura_verificada_em: nowIso }
        setModalPag(atualizado)
        setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
        setFaturaMsg({ tipo: 'ok', texto: '✓ Verificação OK — factura marcada como verificada.' })
        setTimeout(() => setFaturaMsg({ tipo: '', texto: '' }), 6000)
      } else {
        // Mostra modal de discrepancia sem enviar email (factura ja existe no cliente)
        setFaturaMsg({ tipo: '', texto: '' })
        setVerifFatura({ result, file: null, reverificacao: true })
      }
    } catch (err) {
      setFaturaMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setReverificando(false)
    }
  }

  const verComprovativo = async (path) => {
    if (!path) return
    try {
      const { data, error } = await supabase.storage.from('comprovativos').createSignedUrl(path, 3600)
      if (error) throw error
      window.open(data.signedUrl, '_blank')
    } catch (err) {
      alert('Erro ao abrir comprovativo: ' + (err?.message || String(err)))
    }
  }

  // SO ACEITA UM CAMINHO, E NADA MAIS.
  //
  // Um botao escrito `onClick={verFactura}` em vez de
  // `onClick={() => verFactura()}` passa o evento do clique como se fosse o
  // caminho. Como o evento e um objecto, e um objecto e verdadeiro, a guarda
  // do `if (!alvo)` deixava-o passar e o erro so aparecia la dentro, no
  // supabase-js: "t.replace is not a function". Ninguem liga esse texto a um
  // botao mal escrito.
  //
  // Aconteceu no botao "Ver factura" do modal. Em vez de corrigir so o botao,
  // a funcao passa a ignorar o que nao for texto — assim o proximo que se
  // escrever assim cai no caminho por omissao em vez de rebentar.
  const verFactura = async (path) => {
    const alvo = (typeof path === 'string' && path) ? path : modalPag?.fatura_pdf_url
    if (!alvo) return
    try {
      const { data, error } = await supabase.storage.from('faturas').createSignedUrl(alvo, 3600)
      if (error) throw error
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      alert('Erro ao abrir factura: ' + (err?.message || String(err)))
    }
  }

  const reenviarFaturaEmail = async () => {
    if (!modalPag?.fatura_pdf_url) return
    setEnviandoFaturaEmail(true)
    setFaturaMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/enviar-fatura-cliente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ encomenda_id: modalPag.id }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'erro desconhecido')
      setFaturaMsg({ tipo: 'ok', texto: `Email reenviado para ${modalPag.cliente_email}.` })
      setTimeout(() => setFaturaMsg({ tipo: '', texto: '' }), 4000)
    } catch (err) {
      setFaturaMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setEnviandoFaturaEmail(false)
    }
  }

  const [eliminando, setEliminando] = useState(false)

  // Fatura
  const [faturaForm, setFaturaForm] = useState({ numero: '', data: '', notas: '' })
  // Factura do SINAL — separada da final. Clientes empresa (o Diogo) levam
  // factura quando pagam o sinal, e outra no pagamento final. As colunas
  // fatura_* sem sufixo continuam a ser a FINAL; estas sao a do sinal.
  // Deliberadamente sem a verificacao automatica da outra: essa compara o total
  // da encomenda com o do PDF, e aqui o valor esperado e outro (o sinal).
  const [faturaSinalForm, setFaturaSinalForm] = useState({ numero: '', data: '' })
  const [uploadingFaturaSinal, setUploadingFaturaSinal] = useState(false)
  const [savingFatura, setSavingFatura] = useState(false)
  const [faturaMsg, setFaturaMsg] = useState({ tipo: '', texto: '' })

  // Factura do fornecedor (interno, admin only)
  const [fornFatura, setFornFatura] = useState('')
  const [fornFaturaSaved, setFornFaturaSaved] = useState('')
  const [savingForn, setSavingForn] = useState(false)
  const [fornMsg, setFornMsg] = useState({ tipo: '', texto: '' })
  // Propagar nº factura fornecedor a outras encomendas elegíveis
  const [propagarForn, setPropagarForn] = useState(null) // { numero, candidates: [{id, numero_encomenda, cliente_nome, marcada}] }
  const [aplicandoPropagacao, setAplicandoPropagacao] = useState(false)

  // Tracking de envio (admin)
  const [trackingForm, setTrackingForm] = useState({ codigo: '', transportadora: '', data: '' })
  const [enviandoTracking, setEnviandoTracking] = useState(false)
  const [trackingMsg, setTrackingMsg] = useState({ tipo: '', texto: '' })
  // Irmas: outras encomendas da mesma compra ao fornecedor (mesma factura fisica)
  const [irmasInfo, setIrmasInfo] = useState(null) // { compraId, numeroFatura, count, irmas: [{ id, numero, cliente }] }
  const [confirmPropagar, setConfirmPropagar] = useState(false)
  const [uploadingFatura, setUploadingFatura] = useState(false)
  const [enviandoFaturaEmail, setEnviandoFaturaEmail] = useState(false)
  // Admin: modal "Anexar + Confirmar Pagamento" (combina upload, banco, data, ref)
  const [uploadingComp, setUploadingComp] = useState(null)
  const [compModal, setCompModal] = useState(null) // { prestacao: '1a'|'2a'|'total', file, banco, data }
  // Recusar comprovativo: { prest: '1a'|'2a', motivo }
  const [rejeitarModal, setRejeitarModal] = useState(null)
  const [rejeitando, setRejeitando] = useState(false)

  // Recusa o comprovativo e avisa o cliente. O ficheiro NAO e apagado: fica como
  // prova do que foi enviado. Quando o cliente submeter outro, o upload dele
  // limpa a marca de recusa (ver MinhasEncomendas.jsx).
  const rejeitarComprovativo = async () => {
    if (!rejeitarModal || !modalPag) return
    const motivo = (rejeitarModal.motivo || '').trim()
    // Motivo obrigatorio: recusar sem dizer porque so gera um telefonema.
    if (motivo.length < 5) return
    setRejeitando(true)
    try {
      const agora = new Date().toISOString()
      const patch = rejeitarModal.prest === '1a'
        ? { comprovativo_1a_rejeitado_em: agora, comprovativo_1a_rejeitado_motivo: motivo }
        : { comprovativo_2a_rejeitado_em: agora, comprovativo_2a_rejeitado_motivo: motivo }
      // .select('id') porque o supabase-js nao da erro quando a RLS filtra tudo —
      // sem isto uma sessao expirada passava por sucesso.
      const { data: rows, error } = await supabase.from('encomendas')
        .update(patch).eq('id', modalPag.id).select('id')
      if (error) throw new Error(error.message)
      if (!rows || rows.length === 0) throw new Error('Sessão expirada — recarrega a página.')

      const atualizado = { ...modalPag, ...patch }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? { ...e, ...patch } : e))

      // Email ao cliente. Nao bloqueia: a recusa ja esta gravada e visivel no
      // site do cliente mesmo que o email falhe.
      try {
        const { data: { session } } = await supabase.auth.getSession()
        await fetch(`${SUPABASE_URL}/functions/v1/comprovativo-rejeitado`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ encomenda_id: modalPag.id, prestacao: rejeitarModal.prest, motivo }),
        })
      } catch (_) { /* silencioso */ }

      setRejeitarModal(null)
    } catch (e) {
      alert('Erro ao recusar: ' + (e?.message || String(e)))
    } finally {
      setRejeitando(false)
    }
  }
  const abrirComprovativoModal = (prestacao) => {
    if (!modalPag) return
    const refExist = prestacao === '1a' ? modalPag.comprovativo_1a_ref
                    : prestacao === '2a' ? modalPag.comprovativo_2a_ref
                    : (modalPag.comprovativo_1a_ref || modalPag.comprovativo_2a_ref)
    const bancoExist = prestacao === '1a' ? modalPag.pago_1a_banco
                      : prestacao === '2a' ? modalPag.pago_2a_banco
                      : (modalPag.pago_1a_banco || modalPag.pago_2a_banco)
    const dataExist = prestacao === '1a' ? modalPag.pago_1a_data
                     : prestacao === '2a' ? modalPag.pago_2a_data
                     : (modalPag.pago_1a_data || modalPag.pago_2a_data)
    setCompModal({
      prestacao,
      file: null,
      banco: bancoExist || bancoNome || '',
      data: dataExist || new Date().toISOString().slice(0, 10),
      ref: refExist || '',
      msg: { tipo: '', texto: '' },
    })
  }
  const submeterComprovativoAdmin = async () => {
    if (!compModal || !modalPag) return
    const { file, banco, data, prestacao } = compModal
    if (!file) { setCompModal(p => ({ ...p, msg: { tipo: 'erro', texto: 'Anexa o ficheiro.' } })); return }
    if (!banco.trim()) { setCompModal(p => ({ ...p, msg: { tipo: 'erro', texto: 'Indica o banco.' } })); return }
    if (!data) { setCompModal(p => ({ ...p, msg: { tipo: 'erro', texto: 'Indica a data.' } })); return }
    setUploadingComp(prestacao)
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
      const path = `${modalPag.cliente_id}/${modalPag.id}-${prestacao}.${ext}`
      const { error: upErr } = await supabase.storage.from('comprovativos').upload(path, file, { upsert: true, contentType: file.type })
      if (upErr) throw new Error(upErr.message)
      const now = new Date().toISOString()
      const bancoStr = banco.trim()
      // Update: anexa ficheiro + marca prestacao paga + banco + data (faz tudo num so passo)
      const update = prestacao === 'total'
        ? {
            comprovativo_1a_url: path, comprovativo_1a_em: now,
            comprovativo_2a_url: path, comprovativo_2a_em: now,
            pago_1a_prestacao: true, pago_1a_data: data, pago_1a_banco: bancoStr,
            pago_2a_prestacao: true, pago_2a_data: data, pago_2a_banco: bancoStr,
          }
        : prestacao === '1a'
          ? {
              comprovativo_1a_url: path, comprovativo_1a_em: now,
              pago_1a_prestacao: true, pago_1a_data: data, pago_1a_banco: bancoStr,
            }
          : {
              comprovativo_2a_url: path, comprovativo_2a_em: now,
              pago_2a_prestacao: true, pago_2a_data: data, pago_2a_banco: bancoStr,
            }
      const { error } = await supabase.from('encomendas').update(update).eq('id', modalPag.id)
      if (error) throw new Error(error.message)
      // Notificar cliente por email (ref vai incluida via edge function v3)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        await fetch(`${SUPABASE_URL}/functions/v1/notificar-pagamento-confirmado`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ encomenda_id: modalPag.id, prestacao }),
        })
      } catch (e) { console.warn('Email falhou:', e) }
      const atualizado = { ...modalPag, ...update }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
      setCompModal(null)
    } catch (e) {
      setCompModal(p => p ? { ...p, msg: { tipo: 'erro', texto: 'Erro: ' + (e?.message || String(e)) } } : null)
    } finally {
      setUploadingComp(null)
    }
  }

  // Sub-modal de confirmação de pagamento por prestação (funciona a partir da
  // tabela ou da modal de detalhe — `pagEnc` guarda a encomenda em causa).
  const [pagEnc, setPagEnc] = useState(null)
  const [pagModal, setPagModal] = useState(null) // { tranche: '1'|'2', data, banco }
  const [savingPag, setSavingPag] = useState(false)
  const [pagMsg, setPagMsg] = useState({ tipo: '', texto: '' })

  const abrirPagamento = (tranche, enc) => {
    const target = enc ?? modalPag
    if (!target) return
    // 'total' = confirma 1ª + 2ª em simultâneo (pagamento 100% upfront)
    const dataExistente = tranche === '1' ? target.pago_1a_data
                        : tranche === '2' ? target.pago_2a_data
                        : (target.pago_1a_data || target.pago_2a_data)
    const bancoExistente = tranche === '1' ? target.pago_1a_banco
                         : tranche === '2' ? target.pago_2a_banco
                         : (target.pago_1a_banco || target.pago_2a_banco)
    // VALOR ESPERADO, JA PREENCHIDO.
    //
    // Confirmar um pagamento gravava a marca, a data e o banco — nunca o valor.
    // Na maioria das encomendas nao se notava, porque o sinal_escolhido diz o
    // que era esperado e os ecras usam-no. Mas a 0030/2026 nao tem sinal, e o
    // sistema ficou a assumir metade sem ninguem saber: 369,46 EUR que sao um
    // palpite com ar de facto. Foi tambem esse vazio que deixou a funcao de
    // edicao gravar 2.453,45 na 0026/2026.
    //
    // Vem preenchido com o que se espera: um Enter e fica igual ao de antes.
    // Se o cliente pagou outra coisa, corrige-se aqui — que e o unico momento
    // em que alguem tem o comprovativo a frente.
    const totalAlvo = totalEfetivo(target)
    const p1Esperada = primeiraPrestacao(target, totalAlvo)
    const valorEsperado = tranche === '1' ? p1Esperada
                        : tranche === '2' ? Math.max(0, totalAlvo - p1Esperada)
                        : totalAlvo
    const valorExistente = tranche === '2' ? null : target.pago_1a_valor
    setPagEnc(target)
    setPagModal({
      tranche,
      data: dataExistente || new Date().toISOString().slice(0, 10),
      banco: bancoExistente || '',
      valor: fmt2(valorExistente != null ? Number(valorExistente) : valorEsperado),
      valorEsperado: Math.round(valorEsperado * 100) / 100,
      lido: null,
      semComprovativo: tranche === '2'
        ? !!target.pago_2a_sem_comprovativo
        : !!target.pago_1a_sem_comprovativo,
      nota: (tranche === '2' ? target.pago_2a_nota : target.pago_1a_nota) || '',
    })
    setPagMsg({ tipo: '', texto: '' })

    // LE O VALOR NO COMPROVATIVO QUE O CLIENTE JA CARREGOU.
    //
    // O `pago_1a_valor` era escrito a mao, e quando ficava por escrever o
    // sistema caia numa cadeia de palpites que acaba em "metade". Numa
    // encomenda de 4.652 EUR isso foram 2.326 EUR dados como recebidos sem
    // ninguem ter confirmado — e o comprovativo, que diz o valor exacto,
    // estava guardado ha semanas sem ninguem o abrir.
    //
    // Nao grava nada: mostra o que leu, e o admin carrega em "usar" se
    // concordar. E dinheiro; um parser que se engana em silencio e pior do
    // que nao haver parser.
    const url = tranche === '2' ? target.comprovativo_2a_url
                                : (target.comprovativo_1a_url || target.comprovativo_2a_url)
    if (!url) { setPagModal(m => (m ? { ...m, lido: { ok: false, motivo: 'sem comprovativo anexado' } } : m)); return }
    ;(async () => {
      try {
        const { data, error } = await supabase.storage.from('comprovativos').createSignedUrl(url, 120)
        if (error || !data?.signedUrl) throw new Error('nao consegui abrir o ficheiro no storage')
        const resp = await fetch(data.signedUrl)
        if (!resp.ok) throw new Error('download falhou (' + resp.status + ')')
        const blob = await resp.blob()
        const r = await parseComprovativoFile(blob)
        // Guarda-se mesmo quando falha. Uma leitura que nao acontece em
        // silencio e indistinguivel de nao haver leitor nenhum — e nao ha
        // como saber se foi o ficheiro, o formato do banco, ou um erro meu.
        setPagModal(m => (m ? {
          ...m,
          lido: r.ok ? r : {
            ok: false,
            motivo: (r.raw_text || '').trim().length < 20
              ? 'o ficheiro nao tem texto (sera uma imagem?)'
              : 'nao encontrei o montante neste formato',
            amostra: (r.raw_text || '').replace(/\s+/g, ' ').slice(0, 170),
            candidatos: r.candidatos || [],
          },
        } : m))
      } catch (e) {
        setPagModal(m => (m ? { ...m, lido: { ok: false, motivo: e?.message || 'erro a ler' } } : m))
      }
    })()
  }

  const fecharPagamento = () => { setPagModal(null); setPagEnc(null); setPagMsg({ tipo: '', texto: '' }) }

  const aplicarUpdatePagamento = async (update) => {
    if (!pagEnc) return
    setSavingPag(true)
    const { error } = await supabase.from('encomendas').update(update).eq('id', pagEnc.id)
    setSavingPag(false)
    if (error) {
      setPagMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
      return
    }
    const atualizado = { ...pagEnc, ...update }
    setEncomendas(prev => prev.map(e => e.id === pagEnc.id ? atualizado : e))
    if (modalPag?.id === pagEnc.id) setModalPag(atualizado)
    setPagModal(null)
    setPagEnc(null)
  }

  const confirmarPagamento = async () => {
    if (!pagEnc || !pagModal) return
    if (!pagModal.data) { setPagMsg({ tipo: 'erro', texto: 'Indique a data do pagamento.' }); return }
    if (!pagModal.banco.trim()) { setPagMsg({ tipo: 'erro', texto: 'Indique o banco.' }); return }
    const banco = pagModal.banco.trim()
    // O valor entra no pago_1a_valor. Nao ha coluna para a 2.a prestacao — o que
    // falta e sempre o total menos a primeira — por isso ai so se marca.
    const valorNum = Number(String(pagModal.valor ?? '').replace(',', '.'))
    if (pagModal.tranche !== '2' && !(valorNum > 0)) {
      setPagMsg({ tipo: 'erro', texto: 'Indique o valor recebido.' }); return
    }
    // A marca de "sem comprovativo" e a nota andam com a prestacao que se esta
    // a confirmar. Nota vazia grava NULL e nao string vazia, para a lista poder
    // perguntar simplesmente "ha nota?".
    const sc = !!pagModal.semComprovativo
    const nt = (pagModal.nota || '').trim() || null
    const update = pagModal.tranche === '1'
      ? { pago_1a_prestacao: true, pago_1a_data: pagModal.data, pago_1a_banco: banco,
          pago_1a_valor: Math.round(valorNum * 100) / 100,
          pago_1a_sem_comprovativo: sc, pago_1a_nota: nt }
      : pagModal.tranche === '2'
        ? { pago_2a_prestacao: true, pago_2a_data: pagModal.data, pago_2a_banco: banco,
            pago_2a_sem_comprovativo: sc, pago_2a_nota: nt }
        : {
            pago_1a_prestacao: true, pago_1a_data: pagModal.data, pago_1a_banco: banco,
            pago_1a_valor: Math.round(valorNum * 100) / 100,
            pago_1a_sem_comprovativo: sc, pago_1a_nota: nt,
            pago_2a_prestacao: true, pago_2a_data: pagModal.data, pago_2a_banco: banco,
            pago_2a_sem_comprovativo: sc, pago_2a_nota: nt,
          }
    const encId = pagEnc.id
    const prestacaoNotif = pagModal.tranche === '1' ? '1a' : pagModal.tranche === '2' ? '2a' : 'total'
    await aplicarUpdatePagamento(update)
    // Notificar cliente por email (silencioso em caso de falha)
    // Email confirma recebimento — NÃO promete expedição (vem só com tracking)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      await fetch(`${SUPABASE_URL}/functions/v1/notificar-pagamento-confirmado`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ encomenda_id: encId, prestacao: prestacaoNotif }),
      })
    } catch (_) { /* silent */ }
  }

  const desmarcarPagamento = async () => {
    if (!pagEnc || !pagModal) return
    if (!window.confirm('Desmarcar pagamento desta prestação?')) return
    // O valor sai com a marca. Um pago_1a_valor sem pagamento e pior do que
    // nenhum: os ecras leem-no primeiro que tudo e passariam a contar dinheiro
    // de um pagamento que foi desmarcado.
    const update = pagModal.tranche === '1'
      ? { pago_1a_prestacao: false, pago_1a_data: null, pago_1a_banco: null, pago_1a_valor: null,
          pago_1a_sem_comprovativo: false, pago_1a_nota: null }
      : pagModal.tranche === '2'
        ? { pago_2a_prestacao: false, pago_2a_data: null, pago_2a_banco: null,
            pago_2a_sem_comprovativo: false, pago_2a_nota: null }
        : {
            pago_1a_prestacao: false, pago_1a_data: null, pago_1a_banco: null, pago_1a_valor: null,
            pago_1a_sem_comprovativo: false, pago_1a_nota: null,
            pago_2a_prestacao: false, pago_2a_data: null, pago_2a_banco: null,
            pago_2a_sem_comprovativo: false, pago_2a_nota: null,
          }
    await aplicarUpdatePagamento(update)
  }

  const exportarCSV = () => {
    if (!filtradas || filtradas.length === 0) return
    const esc = (v) => {
      if (v == null) return ''
      const s = String(v)
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const fmtD = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : ''
    const fmtN = (n) => Number(n || 0).toFixed(2).replace('.', ',')
    const headers = [
      'Numero','Data','Cliente','Email','NIF','Estado',
      'Total s/IVA','Total c/IVA','Desconto %','Desconto €','Total Final',
      'Portes','1a Valor','1a %','2a Valor',
      '1a Paga','1a Data','1a Banco','2a Paga','2a Data','2a Banco',
      'Fatura Nº','Fatura Data','Fatura Fornecedor','Transportadora','Tracking','Data Envio'
    ]
    const rows = filtradas.map(e => [
      e.numero_encomenda || e.id.slice(0,8).toUpperCase(),
      fmtD(e.created_at),
      e.cliente_nome || '',
      e.cliente_email || '',
      e.entrega_nif || '',
      e.estado || '',
      fmtN(e.total_sem_iva),
      fmtN(e.total_com_iva),
      e.desconto_percentagem ? fmtN(e.desconto_percentagem) : '',
      e.desconto_valor ? fmtN(e.desconto_valor) : '',
      fmtN(e.total_final ?? e.total_com_iva),
      fmtN(e.portes),
      // Valores das prestações — é por aqui que se reconciliam as transferências.
      // Sem isto o CSV não distinguia uma encomenda 50/50 de uma com sinal de 8%.
      ...(() => {
        const tot = Number(e.total_final ?? e.total_com_iva ?? 0)
        const p1 = Math.round(primeiraPrestacao(e, tot) * 100) / 100
        return [fmtN(p1), fmtN(pctDoTotal(p1, tot)), fmtN(Math.round((tot - p1) * 100) / 100)]
      })(),
      e.pago_1a_prestacao ? 'Sim' : 'Não',
      fmtD(e.pago_1a_data),
      e.pago_1a_banco || '',
      e.pago_2a_prestacao ? 'Sim' : 'Não',
      fmtD(e.pago_2a_data),
      e.pago_2a_banco || '',
      e.fatura_numero || '',
      fmtD(e.fatura_emitida_em),
      e.fornecedor_fatura_numero || '',
      e.transportadora || '',
      e.tracking || '',
      fmtD(e.data_envio),
    ])
    const csv = '﻿' + [headers, ...rows].map(r => r.map(esc).join(';')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `encomendas_${new Date().toISOString().slice(0,10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const mudarEstado = async (novoEstado, opts = {}) => {
    if (!modalPag) return
    const labels = { enviado: 'Enviado', concluido: 'Concluído', cancelado: 'Cancelado' }
    if (opts.confirm) {
      const ok = window.confirm(opts.confirmMsg || `Mudar estado para "${labels[novoEstado] || novoEstado}"?`)
      if (!ok) return
    }
    let motivo = null
    if (novoEstado === 'cancelado') {
      motivo = window.prompt('Motivo do cancelamento (será mostrado ao cliente no email):')
      if (motivo === null) return
    }
    setMudandoEstado(true)
    setEstadoMsg({ tipo: '', texto: '' })
    // Para cancelado/concluido: usar edge function que envia email automático ao cliente
    if (novoEstado === 'cancelado' || novoEstado === 'concluido') {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch(`${SUPABASE_URL}/functions/v1/mudar-estado-encomenda`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ encomenda_id: modalPag.id, novo_estado: novoEstado, motivo: motivo?.trim() || null }),
        })
        const data = await res.json()
        if (!data.ok) throw new Error(data.erro || 'erro desconhecido')
      } catch (err) {
        setMudandoEstado(false)
        setEstadoMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
        return
      }
    } else {
      // outros estados: update directo
      const { error } = await supabase.from('encomendas').update({ estado: novoEstado }).eq('id', modalPag.id)
      if (error) {
        setMudandoEstado(false)
        setEstadoMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
        return
      }
    }
    setMudandoEstado(false)
    const atualizado = { ...modalPag, estado: novoEstado }
    setModalPag(atualizado)
    setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
    const sufixo = (novoEstado === 'cancelado' || novoEstado === 'concluido') ? ' Cliente notificado por email.' : ''
    setEstadoMsg({ tipo: 'ok', texto: `Estado atualizado para "${labels[novoEstado] || novoEstado}".${sufixo}` })
    setTimeout(() => setEstadoMsg({ tipo: '', texto: '' }), 4000)
  }

  const eliminarEncomenda = async () => {
    if (!modalPag) return
    const num = modalPag.numero_encomenda || modalPag.id.slice(0, 8).toUpperCase()

    // -- Encomenda com dinheiro recebido nao se apaga por engano ------------
    // A 29/08/2026 foi apagada a 0033/2026, que tinha 508 EUR ja recebidos. A
    // caixa de confirmacao falava de "a encomenda e todos os seus itens" e nao
    // dizia uma palavra sobre o dinheiro. Nao foi distraccao de quem carregou:
    // o ecra nao deu a informacao que era precisa para decidir.
    //
    // Apagar faz desaparecer o registo de um movimento que aconteceu mesmo. O
    // saldo ate fica certo, porque a entrada e a devolucao anulam-se — mas
    // fica-se sem saber que houve uma devolucao e porque. Cancelar guarda isso
    // e as contas ficam iguais.
    const tot = totalEfetivo(modalPag)
    const p1 = primeiraPrestacao(modalPag, tot)
    const recebido = (modalPag.pago_1a_prestacao ? p1 : 0)
      + (modalPag.pago_2a_prestacao ? Math.max(0, tot - p1) : 0)

    if (recebido > 0.01) {
      const escrito = window.prompt(
        `ATENCAO — a encomenda ${num} tem ${fmt2(recebido)} € JÁ RECEBIDOS.\n\n` +
        `Apagá-la faz desaparecer o registo desse dinheiro. Fica sem rasto de que entrou, ` +
        `de que foi devolvido, e porquê.\n\n` +
        `Se foi uma anulação, usa CANCELAR em vez de apagar: as contas ficam exactamente ` +
        `iguais e o motivo fica guardado.\n\n` +
        `Para apagar mesmo assim, escreve o número da encomenda:`)
      if (String(escrito ?? '').trim() !== num) {
        if (escrito !== null) {
          setPortesMsg({ tipo: 'erro', texto: 'Número não confere — a encomenda NÃO foi apagada.' })
        }
        return
      }
    } else {
      const conf = window.confirm(`Eliminar PERMANENTEMENTE a encomenda ${num}?\n\nEsta ação apaga a encomenda e todos os seus itens. Não pode ser desfeita.`)
    if (!conf) return
      if (!conf) return
    }
    setEliminando(true)
    setPortesMsg({ tipo: '', texto: '' })
    const { error } = await supabase.from('encomendas').delete().eq('id', modalPag.id)
    setEliminando(false)
    if (error) {
      setPortesMsg({ tipo: 'erro', texto: 'Erro a eliminar: ' + error.message })
      return
    }
    setEncomendas(prev => prev.filter(e => e.id !== modalPag.id))
    setModalPag(null)
  }

  // Acção unificada: guarda portes + envia email da 2ª tranche ao cliente
  const confirmarPortesENotificar = async () => {
    if (!modalPag) return
    const portes = parseFloat((portesInput || '0').replace(',', '.')) || 0
    const total = totalEfetivo(modalPag)
    // Regra normal: metade. Excepção: se a encomenda foi alterada depois de paga,
    // pago_1a_valor guarda o que entrou mesmo e o que falta é total menos isso —
    // senão pedia-se ao cliente metade de um total que já não é o que ele pagou.
    const metade = primeiraPrestacao(modalPag, total)
    const totalSegundaTranche = Math.max(0, Math.round((total - metade) * 100) / 100) + portes

    setSavingPortes(true)
    setEnviandoEmailSegunda(true)
    setPortesMsg({ tipo: '', texto: '' })
    setEmailSegundaMsg({ tipo: '', texto: '' })
    try {
      // 1) Guardar portes
      const { error: errPortes } = await supabase.from('encomendas').update({ portes }).eq('id', modalPag.id)
      if (errPortes) throw new Error('Erro ao guardar portes: ' + errPortes.message)

      // 2) Notificar cliente
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${SUPABASE_URL}/functions/v1/segunda-tranche`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          encomenda_id: modalPag.id,
          portes,
          total_segunda_tranche: totalSegundaTranche,
        }),
      })
      const result = await response.json()
      if (!result.ok) throw new Error(result.erro || 'Erro ao enviar email')

      const atualizado = { ...modalPag, portes }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? atualizado : e))
      setEmailSegundaMsg({ tipo: 'ok', texto: `Portes definidos e email enviado para ${modalPag.cliente_email}.` })
      setTimeout(() => setEmailSegundaMsg({ tipo: '', texto: '' }), 5000)
      carregar()
    } catch (err) {
      setEmailSegundaMsg({ tipo: 'erro', texto: err.message })
    }
    setSavingPortes(false)
    setEnviandoEmailSegunda(false)
  }

  const mudarOverrideLista = async (encomendaId, valor) => {
    setAlterandoArquivo(true)
    try {
      const { error } = await supabase.from('encomendas').update({ override_lista: valor }).eq('id', encomendaId)
      if (error) throw error
      setEncomendas(prev => prev.map(e => e.id === encomendaId ? { ...e, override_lista: valor } : e))
      if (modalPag?.id === encomendaId) setModalPag(prev => ({ ...prev, override_lista: valor }))
    } catch (err) {
      alert('Erro: ' + err.message)
    }
    setAlterandoArquivo(false)
  }

  const arquivarBlocoFatura = async (numeroFatura) => {
    if (!numeroFatura) return
    const irmasIds = encomendas.filter(e => (e.fornecedor_fatura_numero || '') === String(numeroFatura)).map(e => e.id)
    if (irmasIds.length === 0) return
    if (!window.confirm(`Arquivar ${irmasIds.length} encomenda(s) ligada(s) à factura fornecedor ${numeroFatura}?\n\nTodas passam a Arquivo (override manual).`)) return
    setAlterandoArquivo(true)
    try {
      const { error } = await supabase.from('encomendas').update({ override_lista: 'arquivada' }).in('id', irmasIds)
      if (error) throw error
      setEncomendas(prev => prev.map(e => irmasIds.includes(e.id) ? { ...e, override_lista: 'arquivada' } : e))
      if (modalPag && irmasIds.includes(modalPag.id)) setModalPag(prev => ({ ...prev, override_lista: 'arquivada' }))
    } catch (err) {
      alert('Erro: ' + err.message)
    }
    setAlterandoArquivo(false)
  }

  const pedirPagamento100 = async () => {
    if (!modalPag?.id) return
    const jaPedido = !!modalPag.pedido_100pct_em
    const confirmMsg = jaPedido
      ? `Ja foi pedido pagamento 100% a este cliente em ${new Date(modalPag.pedido_100pct_em).toLocaleString('pt-PT')}. Reenviar email?`
      : `Enviar email a pedir pagamento 100% (${fmt2(totalEfetivo(modalPag))} EUR) para ${modalPag.cliente_email}?`
    if (!window.confirm(confirmMsg)) return
    setEnviandoPedido100(true)
    setPedido100Msg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/pedir-pagamento-100pct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ encomenda_id: modalPag.id }),
      })
      const result = await res.json()
      if (!result.ok) throw new Error(result.erro || 'Erro ao enviar email')
      const agora = new Date().toISOString()
      const atualizado = { ...modalPag, pedido_100pct_em: agora }
      setModalPag(atualizado)
      setEncomendas(prev => prev.map(e => e.id === modalPag.id ? { ...e, pedido_100pct_em: agora } : e))
      setPedido100Msg({ tipo: 'ok', texto: `Pedido de pagamento 100% enviado para ${modalPag.cliente_email}.` })
      setTimeout(() => setPedido100Msg({ tipo: '', texto: '' }), 6000)
    } catch (err) {
      setPedido100Msg({ tipo: 'erro', texto: err.message })
    }
    setEnviandoPedido100(false)
  }

  // ─── Sistema Ativas / Arquivo ───
  // Concluida = TODOS os passos terminados. Externo: pago + enviado + factura + transitos chegados.
  // Interno (Catia): dispensa factura (revenda a custo, conta interna).
  const isConcluida = (e) => {
    if (!e) return false
    if (!e.pago_1a_prestacao) return false
    if (!e.pago_2a_prestacao) return false
    if (!e.data_envio) return false
    const ehInterno = internosIds.has(e.cliente_id)
    if (!ehInterno && !e.fatura_pdf_url) return false
    // Interno (Catia) SEM transitos aplicados = venda de stock existente → concluída.
    if (ehInterno && !e.transitos_aplicados) return true
    if (!e.transitos_aplicados) {
      // Externa sem transitos aplicados = vendida de stock existente (nao havia factura fornecedor).
      // Nao ha stock a esperar; se ja passou o prazo pos-envio, considera concluida.
      const diasDesdeEnvio = (Date.now() - new Date(e.data_envio).getTime()) / 86400000
      return diasDesdeEnvio >= 7
    }
    // REGRA DE OURO: encomenda com transitos aplicados so arquiva quando LOGNOW consumiu
    // o stock — ou seja, quando a factura fornecedor ligada ja NAO aparece em nenhum
    // Transito*_Factura de produto (nao ha stock a chegar). Enquanto la estiver, fica ativa.
    const numFat = e.fornecedor_fatura_numero ? String(e.fornecedor_fatura_numero) : null
    if (numFat && facturasEmTransito.has(numFat)) return false
    return true
  }

  // Devolve 'ativas' ou 'arquivo' considerando override manual.
  // Regra: assim que fica concluída (todos os passos completos), passa IMEDIATAMENTE
  // para arquivo — evita concluídas a estorvar as pendentes.
  const qualLista = (e) => {
    if (e.override_lista === 'arquivada') return 'arquivo'
    if (e.override_lista === 'ativa') return 'ativas'
    // Cancelada vai logo para o arquivo (19/09/2026): nao ha nada a fazer nela
    // e ficava nas activas a ocupar linha — a 0030 ficou la quatro dias.
    // "Activas quanto menos melhor." O override 'ativa' continua a manda-la de
    // volta, se um dia for preciso olhar para uma.
    if (String(e.estado || '').startsWith('cancel')) return 'arquivo'
    return isConcluida(e) ? 'arquivo' : 'ativas'
  }

  // ─── Chip helpers (predicados de pendências) ───
  const temPendPagamento = (e) => !e.pago_1a_prestacao || !e.pago_2a_prestacao
  const temPendEnvio = (e) => e.pago_1a_prestacao && e.pago_2a_prestacao && !e.data_envio
  const temPendFactura = (e) => {
    const ehInterno = internosIds.has(e.cliente_id)
    return !ehInterno && !!e.data_envio && !e.fatura_pdf_url
  }
  const chegaHoje = (e) => {
    const hoje = new Date(); hoje.setHours(0,0,0,0)
    const amanha = new Date(hoje); amanha.setDate(hoje.getDate()+1)
    return (e.encomenda_itens || []).some(it => {
      if (!it.transito_data_aplicada) return false
      const d = new Date(it.transito_data_aplicada)
      return d >= hoje && d < amanha
    })
  }

  // Filtro texto (search)
  const matchSearch = (e) => {
    const s = search.toLowerCase()
    if (!s) return true
    return (e.produto_nome || '').toLowerCase().includes(s)
      || (e.cliente_email || '').toLowerCase().includes(s)
      || (e.cliente_nome || '').toLowerCase().includes(s)
      || (e.numero_encomenda || '').toLowerCase().includes(s)
      || (e.fatura_numero || '').toLowerCase().includes(s)
      || (e.fornecedor_fatura_numero || '').toLowerCase().includes(s)
  }

  // Aplicar filtros avançados
  const matchFiltrosAvancados = (e) => {
    if (filtroCliente && (e.cliente_email || '').toLowerCase() !== filtroCliente.toLowerCase()) return false
    if (filtroOrigem !== 'todas') {
      const ehInterno = internosIds.has(e.cliente_id)
      if (filtroOrigem === 'interno' && !ehInterno) return false
      if (filtroOrigem === 'b2b' && (ehInterno || e.origem === 'b2c_fornecedor_directo')) return false
      if (filtroOrigem === 'b2c' && e.origem !== 'b2c_fornecedor_directo') return false
    }
    if (filtroFactFornecedor && (e.fornecedor_fatura_numero || '') !== filtroFactFornecedor) return false
    if (filtroValorMin && totalEfetivo(e) < Number(filtroValorMin)) return false
    if (filtroFacturacao === 'faturadas' && !e.fatura_pdf_url) return false
    if (filtroFacturacao === 'sem_factura' && !!e.fatura_pdf_url) return false
    if (filtroPeriodo !== 'tudo') {
      const dias = { '7d': 7, '30d': 30, '90d': 90, '6m': 180, '12m': 365 }[filtroPeriodo]
      const cutoff = Date.now() - dias * 24 * 60 * 60 * 1000
      const d = new Date(e.created_at || 0).getTime()
      if (d < cutoff) return false
    }
    return true
  }

  const matchChips = (e) => {
    if (chipsAtivos.pendencias && !(temPendPagamento(e) || temPendEnvio(e) || temPendFactura(e))) return false
    if (chipsAtivos.aguardaPag && !temPendPagamento(e)) return false
    if (chipsAtivos.aguardaEnvio && !temPendEnvio(e)) return false
    if (chipsAtivos.faltaFactura && !temPendFactura(e)) return false
    if (chipsAtivos.transitoHoje && !chegaHoje(e)) return false
    return true
  }

  const filtradas = encomendas.filter(e => {
    if (!matchSearch(e)) return false
    if (qualLista(e) !== activeTab) return false
    if (!matchFiltrosAvancados(e)) return false
    if (!matchChips(e)) return false
    return true
  })

  // Cor por factura no Arquivo → hash determinístico (mesma factura, mesma cor
  // em qualquer vista). Ver src/utils/corFactura.js.

  // KPIs — calculados sobre TODAS as encomendas visíveis (ambas as tabs), não só filtradas
  const encsVisiveisCliente = encomendas.filter(matchSearch)
  const naoConcluidas = encsVisiveisCliente.filter(e => !isConcluida(e))
  const totalPendente = naoConcluidas
    .filter(e => e.estado !== 'concluido' && e.estado !== 'cancelado' && !internosIds.has(e.cliente_id))
    .reduce((s, e) => s + totalEfetivo(e), 0)
  const nAguardaPag = naoConcluidas.filter(temPendPagamento).length
  const nAguardaEnvio = naoConcluidas.filter(temPendEnvio).length
  const nFaltaFactura = naoConcluidas.filter(temPendFactura).length
  const nTransitoHoje = naoConcluidas.filter(chegaHoje).length
  const nConcluidasMes = encsVisiveisCliente.filter(e => {
    if (!isConcluida(e)) return false
    const dataRef = e.data_envio || e.updated_at
    if (!dataRef) return false
    const d = new Date(dataRef)
    const hoje = new Date()
    return d.getMonth() === hoje.getMonth() && d.getFullYear() === hoje.getFullYear()
  }).length
  // A encomenda conta para a barra do mínimo de unidades? Só se o fornecedor
  // dela tiver mínimo em unidades definido na ficha. Encomendas antigas sem
  // fornecedor gravado contam, porque antes do Naypes era tudo do mesmo.
  const fornecedorTemMinimoUn = (e) =>
    e?.fornecedor_id == null ? true : minUnPorForn.has(String(e.fornecedor_id))

  const nAtivas = encsVisiveisCliente.filter(e => qualLista(e) === 'ativas').length
  const nArquivo = encsVisiveisCliente.filter(e => qualLista(e) === 'arquivo').length

  // Calculos da modal
  // Estrutura fiscal (CIVA art.16): desconto sobre s/IVA, IVA depois.
  // desconto_valor armazena valor s/IVA (sobre produtos).
  const mpSemIvaBruto = modalPag ? Number(modalPag.total_sem_iva || 0) : 0
  const mpDescontoValor = modalPag ? Number(modalPag.desconto_valor || 0) : 0  // s/IVA
  const mpDescontoPct = modalPag ? Number(modalPag.desconto_percentagem || 0) : 0
  const mpBaseTributavel = mpSemIvaBruto - mpDescontoValor
  const IVA_RATE_FIN = 0.23
  // Cliente interno (ex: Catia) = revenda intracomunitaria, IVA 0% (reverse charge OSS).
  const mpEhInterno = modalPag ? internosIds.has(modalPag.cliente_id) : false
  const mpIvaValor = mpEhInterno ? 0 : mpBaseTributavel * IVA_RATE_FIN
  const mpTotal = modalPag ? (mpEhInterno ? mpBaseTributavel : totalEfetivo(modalPag)) : 0
  // Separados de proposito: com 50/50 davam o mesmo numero e o codigo usava um
  // so `mpMetade` para as duas coisas. Com um sinal de 8% sao valores opostos —
  // 79 EUR de entrada contra 917 EUR em falta.
  const mp1a = modalPag ? Math.round(primeiraPrestacao(modalPag, mpTotal) * 100) / 100 : 0
  const mpFalta = Math.round((mpTotal - mp1a) * 100) / 100
  const mpPct1a = pctDoTotal(mp1a, mpTotal)
  // Encomenda de fornecedor com pagamento total: nao ha 2.a prestacao nenhuma
  // e tudo o que fale dela tem de desaparecer do ecra.
  const mpPagaTudo = modalPag ? pagaTudoDeUmaVez(modalPag) : false
  const mpPortes = parseFloat((portesInput || '0').replace(',', '.')) || 0
  const mp2aPrestacao = mpFalta + mpPortes

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

      {/* ── MODAL PEDIDOS A FORNECEDOR ── */}
      {showPedidosForn && <PedidosFornecedor onClose={() => setShowPedidosForn(false)} />}

      {/* ── MODAL DETALHE PAGAMENTO ── */}
      {modalPag && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target === e.currentTarget) setModalPag(null) }}>
          <div style={{ background: C.surface, borderRadius: '6px', width: '100%', maxWidth: '600px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>

            {/* Header modal */}
            <div style={{ background: C.primary, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.75)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Detalhe da Encomenda</p>
                <p style={{ margin: '3px 0 0', fontSize: '16px', fontWeight: '700', color: '#fff' }}>{modalPag.numero_encomenda || modalPag.id.slice(0, 8).toUpperCase()}</p>
                {isAdmin && <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>{modalPag.cliente_nome} — {modalPag.cliente_email}</p>}
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>{fmtDate(modalPag.created_at)}</p>
              </div>
              <button onClick={() => setModalPag(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>

              {/* Itens */}
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Itens da Encomenda</p>
                {/* Editar quantidades — só contas internas, antes de ir ao fornecedor */}
                {podeEditarQtds(modalPag) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    {!editandoQtds ? (
                      <button onClick={() => { setEditandoQtds(true); setErroQtds('') }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', border: `1px solid ${C.primary}`, borderRadius: '3px', background: C.surface, color: C.primary, cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                        <Pencil size={11} /> Editar quantidades
                      </button>
                    ) : (
                      <>
                        <button onClick={guardarQuantidades} disabled={guardandoQtds}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', border: 'none', borderRadius: '3px', background: C.primary, color: '#fff', cursor: guardandoQtds ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '700', opacity: guardandoQtds ? 0.6 : 1 }}>
                          {guardandoQtds ? <><Loader size={11} /> A guardar…</> : <><CheckCircle size={11} /> Guardar</>}
                        </button>
                        <button onClick={() => { setEditandoQtds(false); setQtdsEditadas({}); setErroQtds('') }} disabled={guardandoQtds}
                          style={{ padding: '5px 10px', border: `1px solid ${C.border}`, borderRadius: '3px', background: C.surface, color: C.textMuted, cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                          Cancelar
                        </button>
                      </>
                    )}
                    <span style={{ fontSize: '10px', color: C.textMuted }}>
                      {editarCliente(modalPag)
                        ? 'Cliente real — exige motivo, e recalcula o que ainda falta pagar'
                        : 'Conta interna — totais e trânsitos são recalculados ao guardar'}
                    </span>
                    {erroQtds && (
                      <span style={{ fontSize: '11px', color: C.danger, fontWeight: '600' }}>{erroQtds}</span>
                    )}
                  </div>
                )}

                {/* Conferência do pagamento depois de alterar — o cliente pagou
                    sobre o total antigo, por isso o que falta deixa de ser metade. */}
                {resultadoEdicao && (
                  <div style={{ marginBottom: '10px', padding: '12px 14px', background: '#eff6fc', border: `2px solid ${C.info}`, borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <strong style={{ fontSize: '12px', color: C.text }}>Encomenda alterada — o que muda no pagamento</strong>
                      <button onClick={() => setResultadoEdicao(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: '16px' }}>×</button>
                    </div>
                    <table style={{ width: '100%', fontSize: '12.5px', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '3px 0', color: C.textMuted }}>Total c/IVA antes</td>
                          <td style={{ padding: '3px 0', textAlign: 'right', textDecoration: 'line-through', color: C.textMuted }}>{fmt2(resultadoEdicao.total_antes)} €</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '3px 0', color: C.text, fontWeight: '700' }}>Total c/IVA agora</td>
                          <td style={{ padding: '3px 0', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt2(resultadoEdicao.total_depois)} €</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '3px 0', color: C.success }}>Já pagou</td>
                          <td style={{ padding: '3px 0', textAlign: 'right', color: C.success, fontWeight: '700' }}>{fmt2(resultadoEdicao.ja_pago)} €</td>
                        </tr>
                        <tr style={{ borderTop: `2px solid ${C.info}55` }}>
                          <td style={{ padding: '6px 0 0', fontWeight: '800', fontSize: '13px', color: C.text }}>
                            {Number(resultadoEdicao.a_devolver) > 0 ? 'A DEVOLVER AO CLIENTE' : 'FALTA O CLIENTE PAGAR'}
                          </td>
                          <td style={{ padding: '6px 0 0', textAlign: 'right', fontWeight: '800', fontSize: '17px', color: Number(resultadoEdicao.a_devolver) > 0 ? C.warning : C.info }}>
                            {fmt2(Number(resultadoEdicao.a_devolver) > 0 ? resultadoEdicao.a_devolver : resultadoEdicao.falta_pagar)} €
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <p style={{ margin: '8px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
                      O motivo ficou registado nas notas da encomenda. A 2.ª prestação passa a ser este valor
                      e não metade do total — porque o cliente pagou sobre o total antigo.
                      {!!modalPag.encomendado_fornecedor_em && <><br /><strong style={{ color: '#92400e' }}>Não esquecer:</strong> esta encomenda já seguiu ao fornecedor — corrige também o pedido junto dele.</>}
                    </p>
                    {/* A alocacao a compra ja foi acertada pela funcao; o valor
                        da factura do fornecedor NAO, e de proposito — e um
                        documento dele e nao nosso. Diz-se aqui em vez de o
                        adivinhar. */}
                    {!!resultadoEdicao.rever_factura_fornecedor && (
                      <p style={{ margin: '8px 0 0', padding: '8px 10px', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: '3px', fontSize: '11.5px', color: '#92400e', lineHeight: 1.5 }}>
                        <strong>Factura do fornecedor por rever.</strong> As unidades alocadas já foram acertadas,
                        mas o <strong>valor</strong> da factura continua o que era — é um documento do fornecedor e não se inventa aqui.
                        Actualiza-o em Compras quando ele emitir a versão corrigida.
                      </p>
                    )}
                  </div>
                )}
                <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                  {modalItensLoading ? (
                    <div style={{ padding: '14px', textAlign: 'center', color: C.textMuted, fontSize: '12px' }}>A carregar...</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        {(() => {
                          const descPctH = Number(modalPag.desconto_percentagem || 0)
                          return (
                            <tr style={{ background: C.bg }}>
                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Produto</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Qtd</th>
                              {isAdmin && (
                                <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f3f0ff' }} title={`Custo real por unidade s/IVA = preço fornecedor × 0.90 (desc 10%) + portes do fornecedor do artigo (só admin)`}>
                                  Comprei a s/IVA
                                </th>
                              )}
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }} title={descPctH > 0 ? `Preço unitário venda s/IVA já com desconto de ${descPctH}% aplicado (portes ${fmt2(PORTES_COBRADOS)}€/un excluídos do desconto)` : 'Preço unitário venda s/IVA'}>
                                Vendi a s/IVA{descPctH > 0 ? ' c/desc' : ''}
                              </th>
                              {isAdmin && (
                                <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f3f0ff' }} title="Margem por unidade (venda - custo)">
                                  Margem/un
                                </th>
                              )}
                              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total s/IVA</th>
                            </tr>
                          )
                        })()}
                      </thead>
                      <tbody>
                        {modalItens.length === 0 ? (
                          <tr><td colSpan={isAdmin ? "6" : "4"} style={{ padding: '12px', textAlign: 'center', color: C.textMuted, fontSize: '12px' }}>—</td></tr>
                        ) : modalItens.map((it, i) => {
                          const descPctI = Number(modalPag.desconto_percentagem || 0)
                          const precoUnitTabela = Number(it.preco_unitario || 0)
                          // Desconto so sobre a parte do produto: os portes cobrados (1.00) ficam de fora
                          const precoUnitCDesc = descPctI > 0
                            ? PORTES_COBRADOS + Math.max(0, (precoUnitTabela - PORTES_COBRADOS)) * (1 - descPctI / 100)
                            : precoUnitTabela
                          const qtd = Number(it.quantidade || 0)
                          const totalSemIvaLinha = qtd * precoUnitCDesc
                          // Custo = preco_fornecedor × (1 - desc_supplier%) + portes; fallback: preco_custo + portes
                          const infoCusto = custosPorNome.get(it.produto_nome)
                          let custoUn = null
                          // 10 é o desconto de UM fornecedor, usado como último
                          // recurso para artigos sem valor registado.
                          let descSupplierUsado = 10
                          // Portes do fornecedor DESTE artigo. Sem entrada no
                          // mapa (artigo sem fornecedor definido) fica o valor
                          // de sempre.
                          const portesDeste = portesCustoPorNome.has(it.produto_nome)
                            ? portesCustoPorNome.get(it.produto_nome)
                            : PORTES_CUSTO
                          if (infoCusto) {
                            // NAO usar `|| 10`: em JavaScript `0 || 10` da 10,
                            // porque o zero e falso. Um fornecedor que nao da
                            // desconto nenhum tem aqui 0 — e legitimo, nao e
                            // ausencia de valor — e levava com os 10% de outro.
                            // No Naypes isso baixava o custo de 2,24 para 2,02:
                            // a margem aparecia maior do que e, e cada artigo
                            // libertado era reprecado a partir de um custo que
                            // nunca existiu. So `null`/`undefined` cai no valor
                            // por defeito.
                            const dRaw = infoCusto.desconto_supplier_pct
                            descSupplierUsado = (dRaw == null || Number.isNaN(Number(dRaw))) ? 10 : Number(dRaw)
                            if (infoCusto.preco_fornecedor != null) {
                              custoUn = infoCusto.preco_fornecedor * (1 - descSupplierUsado / 100) + portesDeste
                            } else if (infoCusto.preco_custo != null) {
                              custoUn = infoCusto.preco_custo + portesDeste
                            }
                          }
                          const margemUn = (custoUn != null) ? (precoUnitCDesc - custoUn) : null
                          const margemPct = (margemUn != null && precoUnitCDesc > 0) ? (margemUn / precoUnitCDesc * 100) : null
                          return (
                            <tr key={it.id || i} style={{ borderTop: `1px solid ${C.border}` }}>
                              <td style={{ padding: '8px 10px', color: C.text }}>
                                {/* O nome copia-se como o EAN (19/09/2026): quando o artigo nao existe
                                    no software de facturacao, o Marco tem de o escrever a mao — e
                                    um clique poupa isso. */}
                                <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span>{it.produto_nome}</span>
                                  <CopyButton text={it.produto_nome} size={12} title="Copiar nome do artigo" />
                                </div>
                                {it.produto_ean && (
                                  <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    EAN: {it.produto_ean}
                                    <CopyButton text={it.produto_ean} size={11} title="Copiar EAN" />
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: C.text }}>
                                {editandoQtds ? (
                                  <input
                                    type="number" min="0" step="1"
                                    value={qtdsEditadas[it.id] ?? qtd}
                                    onChange={ev => setQtdsEditadas(p => ({ ...p, [it.id]: ev.target.value === '' ? '' : Number(ev.target.value) }))}
                                    style={{ width: '68px', padding: '4px 6px', textAlign: 'center', border: `1px solid ${C.primary}`, borderRadius: '3px', fontSize: '13px', fontWeight: '700', color: C.text }}
                                  />
                                ) : qtd}
                              </td>
                              {isAdmin && (
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: custoUn != null ? C.text : C.textMuted, background: '#faf9ff', fontFamily: 'monospace', fontSize: '12px' }} title={custoUn != null && infoCusto?.preco_fornecedor != null ? `Cálculo: ${fmt2(infoCusto.preco_fornecedor)} × ${(1 - descSupplierUsado / 100).toFixed(2)} (desc ${descSupplierUsado}%) + ${fmt2(portesDeste)} portes = ${fmt2(custoUn)}€` : (custoUn != null ? `Fallback: preco_custo + ${fmt2(portesDeste)} portes` : 'Sem custo registado em produtos_internos')}>
                                  {custoUn != null ? fmt2(custoUn) + ' €' : '—'}
                                </td>
                              )}
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: C.text, fontWeight: '600' }}>{fmt2(precoUnitCDesc)} €</td>
                              {isAdmin && (
                                <td style={{ padding: '8px 10px', textAlign: 'right', background: '#faf9ff', fontFamily: 'monospace', fontSize: '12px', color: margemUn == null ? C.textMuted : (margemUn > 0 ? C.success : C.danger), fontWeight: '600' }} title={margemPct != null ? `${margemPct.toFixed(1)}% sobre venda` : ''}>
                                  {margemUn != null ? fmt2(margemUn) + ' €' : '—'}
                                  {margemPct != null && <div style={{ fontSize: '9px', fontWeight: '400', color: C.textMuted }}>{margemPct.toFixed(1)}%</div>}
                                </td>
                              )}
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: C.text, fontWeight: '700' }}>{fmt2(totalSemIvaLinha)} €</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                {/* Reconciliacao fiscal — mostrado sempre para encomendas com desconto */}
                {(() => {
                  const descPct = Number(modalPag.desconto_percentagem || 0)
                  const descValor = Number(modalPag.desconto_valor || 0)
                  if (descPct <= 0 || descValor <= 0) return null
                  const subtotalSemIvaTabela = Number(modalPag.total_sem_iva || 0)
                  const baseTributavel = subtotalSemIvaTabela - descValor
                  const iva = baseTributavel * 0.23
                  const totalFinal = Number(modalPag.total_final || modalPag.total_com_iva || 0)
                  return (
                    <div style={{ marginTop: '8px', padding: '10px 12px', background: '#fff8e1', border: `1px solid ${C.warning}66`, borderRadius: '4px', fontSize: '12px', color: '#78350f', lineHeight: '1.6' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <AlertTriangle size={14} color={C.warning} style={{ flexShrink: 0, marginTop: '1px' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '4px' }}>
                            Preços acima já com desconto de {descPct}% aplicado por linha — IVA só aparece no total final:
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 12px', fontFamily: 'monospace', fontSize: '11px' }}>
                            <span>Base tributável (soma linhas s/IVA):</span>
                            <span style={{ textAlign: 'right' }}>{fmt2(baseTributavel)} €</span>
                            <span>IVA 23%:</span>
                            <span style={{ textAlign: 'right' }}>+{fmt2(iva)} €</span>
                            <span style={{ fontWeight: '700', borderTop: `1px solid ${C.warning}55`, paddingTop: '3px', marginTop: '2px' }}>Total factura c/IVA:</span>
                            <span style={{ textAlign: 'right', fontWeight: '700', color: C.success, borderTop: `1px solid ${C.warning}55`, paddingTop: '3px', marginTop: '2px' }}>{fmt2(totalFinal)} €</span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#92400e', marginTop: '6px', fontStyle: 'italic' }}>
                            Sage: preços de linha ficam a TABELA (subtotal {fmt2(subtotalSemIvaTabela)}€) + desconto global -{fmt2(descValor)}€. Total factura é o mesmo: {fmt2(totalFinal)}€.
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>

              {/* Morada do cliente */}
              {modalPag.entrega_nome && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Morada do Cliente
                    {/* Copia o bloco todo de uma vez — é assim que se cola numa guia de transporte */}
                    <CopyButton
                      size={12}
                      title="Copiar morada completa"
                      text={[
                        modalPag.entrega_nome,
                        modalPag.entrega_nif ? `NIF: ${modalPag.entrega_nif}` : null,
                        modalPag.entrega_morada,
                        [modalPag.entrega_cod_postal, modalPag.entrega_pais].filter(Boolean).join(' '),
                        modalPag.entrega_telefone,
                      ].filter(Boolean).join('\n')}
                    />
                  </p>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', padding: '10px 12px', fontSize: '13px', color: C.text }}>
                    <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {modalPag.entrega_nome}
                      {modalPag.entrega_nome && <CopyButton text={modalPag.entrega_nome} size={11} title="Copiar nome" />}
                    </div>
                    {modalPag.entrega_nif && (
                      <div style={{ color: C.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        NIF: {modalPag.entrega_nif}
                        <CopyButton text={modalPag.entrega_nif} size={11} title="Copiar NIF" />
                      </div>
                    )}
                    <div style={{ color: C.textMuted, fontSize: '12px' }}>{modalPag.entrega_morada}</div>
                    <div style={{ color: C.textMuted, fontSize: '12px' }}>{modalPag.entrega_cod_postal} {modalPag.entrega_pais}</div>
                    {modalPag.entrega_telefone && (
                      <div style={{ color: C.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        {modalPag.entrega_telefone}
                        <CopyButton text={modalPag.entrega_telefone} size={11} title="Copiar telefone" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comprovativos de pagamento (submetidos pelo cliente) */}
              {isAdmin && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comprovativos de Pagamento</p>

                  {/* Detector: pagamento 100% (mesmo ficheiro em ambas).
                      So faz sentido nas encomendas de duas prestacoes — nas de
                      pagamento total o comprovativo unico e a regra, nao a
                      excepcao, e o aviso era ruido. */}
                  {!mpPagaTudo && (() => {
                    const ehTotal = modalPag.comprovativo_1a_url
                      && modalPag.comprovativo_2a_url
                      && modalPag.comprovativo_1a_url === modalPag.comprovativo_2a_url
                    if (!ehTotal) return null
                    const jaConfirmadoTudo = modalPag.pago_1a_prestacao && modalPag.pago_2a_prestacao
                    return (
                      <div style={{ marginBottom: '8px', padding: '12px 14px', background: jaConfirmadoTudo ? '#dff6dd' : '#fff4ce', border: `2px solid ${jaConfirmadoTudo ? C.success : C.warning}`, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '22px' }}>💯</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: jaConfirmadoTudo ? C.success : C.warning }}>
                              {jaConfirmadoTudo ? 'Pagamento total (100%) confirmado' : 'Cliente pagou 100% — confirmar ambas as prestações'}
                            </div>
                            <div style={{ fontSize: '11px', color: C.textMuted }}>
                              Comprovativo único para 1.ª e 2.ª prestações
                            </div>
                          </div>
                        </div>
                        {!jaConfirmadoTudo && (
                          <button onClick={() => abrirPagamento('total')}
                            style={{ background: C.success, color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <CheckCircle size={13} /> Confirmar Pagamento Total
                          </button>
                        )}
                      </div>
                    )
                  })()}

                  {/* Atalho admin: anexar comprovativo total (100%) quando nenhum existe ainda.
                      No pagamento total o proprio cartao unico ja faz isto. */}
                  {!mpPagaTudo && !modalPag.comprovativo_1a_url && !modalPag.comprovativo_2a_url && (
                    <button
                      onClick={() => abrirComprovativoModal('total')}
                      disabled={!!uploadingComp}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        marginBottom: '8px', padding: '10px 12px',
                        border: `2px dashed ${C.success}`, borderRadius: '4px', background: '#e8f5e9',
                        cursor: uploadingComp ? 'not-allowed' : 'pointer',
                        color: C.success, fontWeight: '700', fontSize: '12px',
                        opacity: uploadingComp === 'total' ? 0.6 : 1,
                      }}>
                      <span style={{ fontSize: '16px' }}>💯</span>
                      {uploadingComp === 'total' ? 'A enviar…' : 'Anexar comprovativo 100% (cliente pagou tudo de uma vez)'}
                    </button>
                  )}

                  {/* Atalho admin: pedir ao cliente para pagar 100% (casos excepcionais) */}
                  {!modalPag.pago_1a_prestacao && !modalPag.pago_2a_prestacao && !modalPag.comprovativo_1a_url && !modalPag.comprovativo_2a_url && (
                    <>
                      <button
                        onClick={pedirPagamento100}
                        disabled={enviandoPedido100}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          marginBottom: '6px', padding: '10px 12px',
                          border: `2px solid ${C.primary}`, borderRadius: '4px', background: '#eff6fc',
                          cursor: enviandoPedido100 ? 'wait' : 'pointer',
                          color: C.primary, fontWeight: '700', fontSize: '12px',
                          opacity: enviandoPedido100 ? 0.6 : 1,
                        }}>
                        <span style={{ fontSize: '15px' }}>📧</span>
                        {enviandoPedido100
                          ? 'A enviar…'
                          : modalPag.pedido_100pct_em
                            ? `Reenviar pedido de pagamento 100% (já enviado ${new Date(modalPag.pedido_100pct_em).toLocaleDateString('pt-PT')})`
                            : 'Pedir pagamento 100% ao cliente'}
                      </button>
                      {pedido100Msg.texto && (
                        <div style={{ fontSize: '11px', marginBottom: '8px', padding: '6px 10px', borderRadius: '4px', color: pedido100Msg.tipo === 'ok' ? C.success : C.danger, background: pedido100Msg.tipo === 'ok' ? '#e8f5e9' : '#fdecea' }}>
                          {pedido100Msg.texto}
                        </div>
                      )}
                    </>
                  )}

                  {/* Um cartao por prestacao — mas no pagamento total so ha uma,
                      e o que ela confirma sao as duas colunas da BD de uma vez. */}
                  <div style={{ display: 'grid', gridTemplateColumns: mpPagaTudo ? '1fr' : '1fr 1fr', gap: '8px' }}>
                    {(mpPagaTudo ? ['1a'] : ['1a', '2a']).map(prest => {
                      // Alvo das accoes: no pagamento total e sempre 'total',
                      // senao o admin confirmava metade de um pagamento inteiro.
                      const alvo = mpPagaTudo ? 'total' : prest
                      const url = prest === '1a' ? modalPag.comprovativo_1a_url : modalPag.comprovativo_2a_url
                      const em  = prest === '1a' ? modalPag.comprovativo_1a_em  : modalPag.comprovativo_2a_em
                      const ref = prest === '1a' ? modalPag.comprovativo_1a_ref : modalPag.comprovativo_2a_ref
                      const pago = prest === '1a' ? modalPag.pago_1a_prestacao : modalPag.pago_2a_prestacao
                      const pagoData = prest === '1a' ? modalPag.pago_1a_data : modalPag.pago_2a_data
                      const pagoBanco = prest === '1a' ? modalPag.pago_1a_banco : modalPag.pago_2a_banco
                      const submetido = !!url
                      // Recusado = há comprovativo, foi rejeitado e ainda não foi
                      // confirmado. Depois de pago a recusa deixa de importar.
                      const rejEm     = prest === '1a' ? modalPag.comprovativo_1a_rejeitado_em     : modalPag.comprovativo_2a_rejeitado_em
                      const rejMotivo = prest === '1a' ? modalPag.comprovativo_1a_rejeitado_motivo : modalPag.comprovativo_2a_rejeitado_motivo
                      const recusado  = !!rejEm && !pago
                      // Evita 100.01€ mostrar "50.01 + 50.01 = 100.02€": 2ª = total − 1ª.
                      const total = totalEfetivo(modalPag)
                      // Se houver valor congelado (encomenda alterada depois de paga),
                      // é esse a 1.ª prestação e o resto é o que falta.
                      const metade1 = Math.round(primeiraPrestacao(modalPag, total) * 100) / 100
                      const valor = prest === '1a' ? metade1 : Math.round((total - metade1) * 100) / 100
                      const pagoCartao = mpPagaTudo ? (modalPag.pago_1a_prestacao && modalPag.pago_2a_prestacao) : pago
                      return (
                        <div key={prest} style={{
                          border: `1px solid ${pagoCartao ? C.success : recusado ? C.danger : submetido ? C.warning : C.border}`,
                          background: pagoCartao ? '#e8f5e9' : recusado ? '#fdecea' : submetido ? '#fff8e1' : C.surface,
                          borderRadius: '4px', padding: '10px 12px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <span>{mpPagaTudo ? 'Pagamento 100%' : prest === '1a' ? '1.ª Prestação' : '2.ª Prestação'}</span>
                                <span style={{ color: pagoCartao ? C.success : C.text, fontSize: '13px', fontWeight: '800' }}>{fmt2(valor)} €</span>
                              </div>
                              <div style={{ fontSize: '11px', color: pagoCartao ? C.success : recusado ? C.danger : submetido ? C.warning : C.textMuted, fontWeight: '600' }}>
                                {pagoCartao
                                  ? `✓ Pago em ${fmtDate(pagoData)}${pagoBanco ? ` · ${pagoBanco}` : ''}`
                                  : recusado
                                    ? `✕ Recusado em ${fmtDate(rejEm)} — aguarda novo comprovativo`
                                    : submetido
                                      ? `Submetido em ${fmtDate(em)} — aguarda confirmação`
                                      : 'Não submetido'}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {submetido && (
                              <button
                                onClick={() => verComprovativo(url)}
                                style={{
                                  background: C.success, color: '#fff', border: 'none',
                                  borderRadius: '4px', padding: '6px 10px', cursor: 'pointer',
                                  fontSize: '11px', fontWeight: '700', display: 'inline-flex',
                                  alignItems: 'center', gap: '4px',
                                }}
                                title="Abrir comprovativo"
                              >
                                <Eye size={12} /> Ver
                              </button>
                            )}
                            {/* Recusar: so faz sentido se ha comprovativo, ainda nao
                                foi confirmado e ainda nao esta recusado. */}
                            {submetido && !pagoCartao && !recusado && (
                              <button
                                onClick={() => setRejeitarModal({ prest, motivo: '' })}
                                style={{
                                  background: '#fff', color: C.danger, border: `1px solid ${C.danger}`,
                                  borderRadius: '4px', padding: '6px 10px', cursor: 'pointer',
                                  fontSize: '11px', fontWeight: '700', display: 'inline-flex',
                                  alignItems: 'center', gap: '4px',
                                }}
                                title="Recusar este comprovativo e pedir outro ao cliente">
                                <X size={12} /> Recusar
                              </button>
                            )}
                            <button
                              onClick={() => abrirComprovativoModal(alvo)}
                              disabled={!!uploadingComp}
                              style={{
                                padding: '6px 10px',
                                border: `1px solid ${C.primary}`,
                                background: submetido ? '#fff' : C.primary,
                                color: submetido ? C.primary : '#fff',
                                borderRadius: '4px',
                                cursor: uploadingComp === alvo ? 'not-allowed' : 'pointer',
                                fontSize: '11px', fontWeight: '700',
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                opacity: uploadingComp === alvo ? 0.7 : 1,
                              }}
                              title="Anexar comprovativo + indicar banco em nome do cliente">
                              {uploadingComp === alvo ? 'A enviar…' : (submetido ? 'Substituir' : 'Anexar')}
                            </button>
                            </div>
                          </div>
                          {recusado && rejMotivo && (
                            <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: `1px dashed ${C.danger}55`, fontSize: '11px', color: C.danger, lineHeight: 1.45 }}>
                              <strong>Motivo enviado ao cliente:</strong> {rejMotivo}
                            </div>
                          )}
                          {ref && (
                            <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: `1px dashed ${submetido ? `${C.success}55` : C.border}`, fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontWeight: '600' }}>Ref.:</span>
                              <span style={{ color: C.text, fontFamily: 'monospace', fontWeight: '600', wordBreak: 'break-all' }}>{ref}</span>
                            </div>
                          )}
                          {/* Confirmar pagamento recebido — só quando ha comprovativo e ainda nao foi confirmado */}
                          {submetido && !pagoCartao && (
                            <button
                              onClick={() => abrirPagamento(mpPagaTudo ? 'total' : prest === '1a' ? '1' : '2')}
                              style={{
                                marginTop: '8px', width: '100%', height: '34px',
                                background: C.success, color: '#fff', border: 'none',
                                borderRadius: '4px', cursor: 'pointer',
                                fontSize: '12px', fontWeight: '700',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                              }}
                              title="Confirmar que o pagamento foi recebido (preencher banco e data)">
                              <CheckCircle size={13} /> Confirmar Pagamento Recebido
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Arquivo / Ativas — override manual (admin) */}
              {isAdmin && (() => {
                const concluida = isConcluida(modalPag)
                const listaActual = qualLista(modalPag)
                const override = modalPag.override_lista
                return (
                  <div style={{ marginBottom: '16px', padding: '12px 14px', background: listaActual === 'arquivo' ? '#f5f5f4' : '#eff6fc', border: `1px solid ${listaActual === 'arquivo' ? C.border : C.primary}40`, borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Estado da Lista</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>
                        {listaActual === 'arquivo' ? '📁 Arquivo' : '📋 Ativas'}
                        {override && <span style={{ marginLeft: '6px', padding: '1px 6px', fontSize: '10px', background: C.warning, color: '#fff', borderRadius: '3px', fontWeight: '800' }}>OVERRIDE MANUAL</span>}
                        {!override && concluida && listaActual === 'ativas' && <span style={{ marginLeft: '6px', fontSize: '11px', color: C.textMuted }}>(concluída — arquiva auto quando LOGNOW confirmar entrada)</span>}
                      </span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {listaActual === 'ativas' && (
                          <button onClick={() => mudarOverrideLista(modalPag.id, 'arquivada')} disabled={alterandoArquivo}
                            style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '6px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            📁 Arquivar já
                          </button>
                        )}
                        {listaActual === 'ativas' && modalPag.fornecedor_fatura_numero && (() => {
                          const nIrmasAtivas = encomendas.filter(e => (e.fornecedor_fatura_numero || '') === String(modalPag.fornecedor_fatura_numero) && qualLista(e) === 'ativas').length
                          if (nIrmasAtivas <= 1) return null
                          return (
                            <button onClick={() => arquivarBlocoFatura(modalPag.fornecedor_fatura_numero)} disabled={alterandoArquivo}
                              title={`Arquivar todas as ${nIrmasAtivas} encomendas ligadas à fatura fornecedor ${modalPag.fornecedor_fatura_numero}`}
                              style={{ background: '#fff4ce', border: `1px solid ${C.warning}`, borderRadius: '3px', padding: '6px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: '#92400e', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              📦 Arquivar bloco Fact. {modalPag.fornecedor_fatura_numero} ({nIrmasAtivas})
                            </button>
                          )
                        })()}
                        {listaActual === 'arquivo' && (
                          <button onClick={() => mudarOverrideLista(modalPag.id, 'ativa')} disabled={alterandoArquivo}
                            style={{ background: C.primary, border: 'none', borderRadius: '3px', padding: '6px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            📋 Trazer para Ativas
                          </button>
                        )}
                        {override && (
                          <button onClick={() => mudarOverrideLista(modalPag.id, null)} disabled={alterandoArquivo}
                            style={{ background: 'transparent', border: `1px solid ${C.warning}`, borderRadius: '3px', padding: '6px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: C.warning }}>
                            ↩ Voltar ao auto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* Notas DA ENCOMENDA — é aqui que fica o motivo escrito quando se
                  altera quantidades ("Ruptura de stock no fornecedor", artigos
                  em falta, etc.), gravado pela RPC com data e autor.
                  Só existia no ecrã do cliente, em "As minhas encomendas": o
                  admin escrevia a nota e depois não tinha onde a ler. */}
              {modalPag.notas && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <StickyNote size={12} /> Notas da encomenda
                    <span style={{ fontWeight: '600', textTransform: 'none', letterSpacing: 0, color: C.warning }}>· o cliente também vê</span>
                  </p>
                  <div style={{ border: `1px solid ${C.border}`, background: C.bg, borderRadius: '4px', padding: '10px 12px', fontSize: '13px', color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                    {modalPag.notas}
                  </div>
                </div>
              )}

              {/* Notas internas admin (invisíveis ao cliente — RLS) */}
              {isAdmin && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <StickyNote size={12} /> Notas Internas (só admin)
                    </p>
                    {notaAdminUpdated && (
                      <span style={{ fontSize: '10px', color: C.textMuted }}>
                        Atualizado em {new Date(notaAdminUpdated).toLocaleString('pt-PT')}
                      </span>
                    )}
                  </div>
                  <div style={{ border: `1px solid #f0d070`, background: '#fffbe6', borderRadius: '4px', padding: '10px 12px' }}>
                    <textarea
                      value={notaAdmin}
                      onChange={e => setNotaAdmin(e.target.value)}
                      placeholder="Ex: Cliente combinou pagamento parcial em dinheiro. Confirmar morada por telefone…"
                      rows={4}
                      style={{
                        width: '100%', boxSizing: 'border-box', resize: 'vertical',
                        border: `1px solid ${C.border}`, borderRadius: '4px',
                        padding: '8px 10px', fontSize: '13px', color: C.text,
                        background: C.surface, fontFamily: 'inherit', outline: 'none',
                      }}
                    />
                    {notaAdminMsg.texto && (
                      <div style={{
                        marginTop: '8px', fontSize: '12px', fontWeight: '600',
                        color: notaAdminMsg.tipo === 'ok' ? C.success : C.danger,
                        display: 'flex', alignItems: 'center', gap: '5px',
                      }}>
                        {notaAdminMsg.tipo === 'ok' ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                        {notaAdminMsg.texto}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', gap: '6px' }}>
                      <button
                        onClick={() => { setNotaAdmin(notaAdminInicial); setNotaAdminMsg({ tipo: '', texto: '' }) }}
                        disabled={savingNotaAdmin || notaAdmin === notaAdminInicial}
                        style={{
                          background: C.surface, color: C.textMuted, border: `1px solid ${C.border}`,
                          borderRadius: '4px', padding: '6px 12px',
                          cursor: (savingNotaAdmin || notaAdmin === notaAdminInicial) ? 'not-allowed' : 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          opacity: (savingNotaAdmin || notaAdmin === notaAdminInicial) ? 0.5 : 1,
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={guardarNotaAdmin}
                        disabled={savingNotaAdmin || notaAdmin === notaAdminInicial}
                        style={{
                          background: C.primary, color: '#fff', border: 'none',
                          borderRadius: '4px', padding: '6px 12px',
                          cursor: (savingNotaAdmin || notaAdmin === notaAdminInicial) ? 'not-allowed' : 'pointer',
                          fontSize: '12px', fontWeight: '700',
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          opacity: (savingNotaAdmin || notaAdmin === notaAdminInicial) ? 0.5 : 1,
                        }}
                      >
                        {savingNotaAdmin ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> A guardar...</> : <><Save size={12} /> Guardar nota</>}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Caixa de Ações de Estado (admin) */}
              {isAdmin && (() => {
                const est = modalPag.estado || 'pendente'
                const podeEnviar    = est === 'pago_2_tranche'
                const podeConcluir  = est === 'enviado'
                const podeCancelar  = !['concluido','cancelado'].includes(est)
                const infoAtual     = estadoInfo(modalPag, mpTotal)
                const labelAtual    = infoAtual.label
                const corAtual      = infoAtual.color
                const bgAtual       = infoAtual.bg
                const btnBase = { padding: '8px 12px', height: '36px', borderRadius: '4px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none' }
                const btnDis  = (cor, on) => ({ ...btnBase, background: on ? cor : '#f3f2f1', color: on ? '#fff' : C.textMuted, cursor: on ? 'pointer' : 'not-allowed', opacity: on ? 1 : 0.55 })
                return (
                  <div style={{ marginBottom: '16px', border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ padding: '8px 10px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <Flag size={12} /> Ações da Encomenda
                      </p>
                      <span style={{ padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: bgAtual, color: corAtual, textTransform: 'uppercase' }}>
                        {labelAtual}
                      </span>
                    </div>
                    {estadoMsg.texto && (
                      <div style={{ padding: '8px 14px', fontSize: '12px', fontWeight: '600', background: estadoMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', color: estadoMsg.tipo === 'ok' ? C.success : C.danger, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {estadoMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertCircle size={13} />} {estadoMsg.texto}
                      </div>
                    )}
                    <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <button
                        onClick={() => {
                          const portes = Number(modalPag?.portes || 0)
                          if (portes <= 0) {
                            const ok = window.confirm('⚠️ Esta encomenda ainda não tem PORTES de envio definidos.\n\nSe avançares, a 2.ª prestação foi/será cobrada sem portes — terás que faturar separadamente os portes ao cliente.\n\nQueres mesmo marcar como Enviado sem portes definidos?')
                            if (!ok) return
                          }
                          mudarEstado('enviado', { confirm: true, confirmMsg: 'Marcar esta encomenda como ENVIADA? O cliente será notificado.' })
                        }}
                        disabled={!podeEnviar || mudandoEstado}
                        title={podeEnviar ? 'Marcar como Enviado' : 'Disponível só após 100% pago'}
                        style={btnDis(C.primary, podeEnviar && !mudandoEstado)}
                      >
                        <Truck size={13} /> Marcar como Enviado
                      </button>
                      <button
                        onClick={() => mudarEstado('concluido', { confirm: true, confirmMsg: 'Concluir esta encomenda? Marca como totalmente terminada.' })}
                        disabled={!podeConcluir || mudandoEstado}
                        title={podeConcluir ? 'Concluir encomenda' : 'Disponível só depois de Enviado'}
                        style={btnDis(C.success, podeConcluir && !mudandoEstado)}
                      >
                        <CheckCircle size={13} /> Concluir
                      </button>
                      <button
                        onClick={() => mudarEstado('cancelado', { confirm: true, confirmMsg: 'Cancelar esta encomenda? O cliente será notificado e os produtos voltam ao catálogo.' })}
                        disabled={!podeCancelar || mudandoEstado}
                        title={podeCancelar ? 'Cancelar encomenda' : 'Já está em estado final'}
                        style={btnDis(C.danger, podeCancelar && !mudandoEstado)}
                      >
                        <Ban size={13} /> Cancelar
                      </button>
                      {mudandoEstado && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: C.textMuted, fontSize: '12px' }}>
                          <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> A guardar…
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '0 14px 12px', fontSize: '11px', color: C.textMuted, lineHeight: '1.5' }}>
                      <ArrowRight size={10} style={{ verticalAlign: 'middle' }} /> {mpPagaTudo
                        ? 'Pendente → 100% Pago → Enviado → Concluído.'
                        : 'Pendente → 1.ª prestação paga → 100% Pago (automático com prestações) → Enviado → Concluído.'}
                    </div>
                  </div>
                )
              })()}

              {/* Tabela resumo financeiro — estrutura fiscal (CIVA art.16) */}
              <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '9px 14px', color: C.textMuted }}>Subtotal s/ IVA</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', color: C.text, fontWeight: '600' }}>{fmt2(mpSemIvaBruto)} €</td>
                    </tr>
                    {mpDescontoValor > 0 && (
                      <tr style={{ background: '#fff4ce' }}>
                        <td style={{ padding: '9px 14px', color: C.warning, fontWeight: '700' }}>Desconto ({fmt2(mpDescontoPct)}%)</td>
                        <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: C.warning }}>−{fmt2(mpDescontoValor)} €</td>
                      </tr>
                    )}
                    <tr style={{ background: C.bg, borderTop: `1px dashed ${C.border}` }}>
                      <td style={{ padding: '9px 14px', color: C.text, fontWeight: '700' }}>Base tributável</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: C.text }}>{fmt2(mpBaseTributavel)} €</td>
                    </tr>
                    {mpEhInterno ? (
                      <tr>
                        <td style={{ padding: '9px 14px', color: C.textMuted }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', background: '#e0f2fe', color: '#075985', fontSize: '11px', fontWeight: '700', letterSpacing: '0.3px' }}>
                            Reverse Charge · IVA 0%
                          </span>
                        </td>
                        <td style={{ padding: '9px 14px', textAlign: 'right', color: C.textMuted }}>+0,00 €</td>
                      </tr>
                    ) : (
                      <tr>
                        <td style={{ padding: '9px 14px', color: C.textMuted }}>IVA (23%)</td>
                        <td style={{ padding: '9px 14px', textAlign: 'right', color: C.text }}>+{fmt2(mpIvaValor)} €</td>
                      </tr>
                    )}
                    <tr style={{ background: '#e8f5e9', borderTop: `2px solid ${C.success}` }}>
                      <td style={{ padding: '11px 14px', fontWeight: '700', color: C.success, fontSize: '14px' }}>Total a pagar</td>
                      <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: '700', color: C.success, fontSize: '17px' }}>{fmt2(mpTotal)} €</td>
                    </tr>
                    <tr
                      onClick={() => isAdmin && abrirPagamento(mpPagaTudo ? 'total' : '1')}
                      style={{ cursor: isAdmin ? 'pointer' : 'default', ...(mpPagaTudo ? { background: '#e8f5e9', borderTop: `2px solid ${C.success}` } : null) }}
                      title={isAdmin ? 'Clique para registar/editar pagamento' : ''}
                    >
                      <td style={{ padding: mpPagaTudo ? '11px 14px' : '9px 14px', color: mpPagaTudo ? C.text : C.textMuted, fontWeight: '700', fontSize: mpPagaTudo ? '14px' : undefined }}>
                        {mpPagaTudo ? 'Pagamento único — 100% na encomenda' : `1.ª Prestação — ${fmtPct(mpPct1a)}% pago na encomenda`}
                        {/* Só admin: o rótulo e o "acordo especial" do tooltip
                            não acrescentam nada a quem já sabe quanto pagou. */}
                        {isAdmin && !mpPagaTudo && temSinalReduzido(modalPag) && (
                          <span title="Sinal reduzido — acordo especial com o fornecedor" style={{ marginLeft: '6px', fontSize: '9px', fontWeight: '700', color: '#92400e', background: '#fff8e1', border: `1px solid ${C.warning}66`, borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase' }}>sinal</span>
                        )}
                        {modalPag.pago_1a_prestacao && (
                          <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.success, fontWeight: '700' }}>
                            <CheckCircle size={12} />
                            <span>Pago em {fmtDate(modalPag.pago_1a_data)}{modalPag.pago_1a_banco ? ` · ${modalPag.pago_1a_banco}` : ''}</span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: mpPagaTudo ? '11px 14px' : '9px 14px', textAlign: 'right', fontWeight: '700', fontSize: mpPagaTudo ? '18px' : undefined, color: mpPagaTudo ? C.success : (modalPag.pago_1a_prestacao ? C.success : C.textMuted) }}>{fmt2(mp1a)} €</td>
                    </tr>
                    {!mpPagaTudo && (
                    <tr style={{ background: C.bg }}>
                      <td style={{ padding: '9px 14px', color: C.textMuted, fontWeight: '600' }}>{fmtPct(Math.round((100 - mpPct1a) * 10) / 10)}% em falta (antes envio)</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: C.warning }}>{fmt2(mpFalta)} €</td>
                    </tr>
                    )}
                    <tr>
                      <td style={{ padding: '9px 14px', color: C.textMuted, fontWeight: '600' }}>Portes de Envio</td>
                      <td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: '700', color: Number(modalPag.portes || 0) > 0 ? C.text : C.success }}>
                        {Number(modalPag.portes || 0) > 0 ? `${fmt2(Number(modalPag.portes))} €` : 'GRÁTIS (incluídos)'}
                      </td>
                    </tr>
                    {!mpPagaTudo && (
                    <tr
                      onClick={() => isAdmin && abrirPagamento('2')}
                      style={{ background: '#e8f5e9', borderTop: `2px solid ${C.success}`, cursor: isAdmin ? 'pointer' : 'default' }}
                      title={isAdmin ? 'Clique para registar/editar pagamento' : ''}
                    >
                      <td style={{ padding: '11px 14px', fontWeight: '700', color: C.text, fontSize: '14px' }}>
                        2.ª Prestação Total a Receber
                        {modalPag.pago_2a_prestacao && (
                          <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: C.success, fontWeight: '700' }}>
                            <CheckCircle size={12} />
                            <span>Pago em {fmtDate(modalPag.pago_2a_data)}{modalPag.pago_2a_banco ? ` · ${modalPag.pago_2a_banco}` : ''}</span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: '700', color: C.success, fontSize: '18px' }}>{fmt2(mpFalta + Number(modalPag.portes || 0))} €</td>
                    </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Atalho admin: confirmar pagamento total (100%) numa única acção */}
              {isAdmin && !(modalPag.pago_1a_prestacao && modalPag.pago_2a_prestacao) && (
                <div style={{ marginBottom: '16px', padding: '12px 14px', background: '#fff4ce', border: `1px dashed ${C.warning}`, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>💯</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>
                        {mpPagaTudo ? 'Confirmar o pagamento desta encomenda' : 'Cliente pagou os 100% de uma só vez?'}
                      </div>
                      <div style={{ fontSize: '11px', color: C.textMuted }}>
                        {mpPagaTudo
                          ? 'Este fornecedor é a 100% — não há 2.ª prestação · expede após confirmar'
                          : 'Confirma ambas as prestações em simultâneo · expede após confirmar'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => abrirPagamento('total')}
                    style={{ background: C.success, color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircle size={13} /> {mpPagaTudo ? 'Confirmar Pagamento Recebido' : 'Confirmar 100% Pago'}
                  </button>
                </div>
              )}

              {/* Notificar 2.ª prestação — portes embutidos por defeito; admin pode definir extra em casos excepcionais.
                  Escondido no pagamento total: nao ha 2.ª prestacao para notificar. */}
              {isAdmin && !mpPagaTudo && (
                <div style={{ background: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Send size={13} /> Notificar 2.ª Prestação
                  </p>
                  <div style={{ background: '#e8f5e9', border: `1px solid ${C.success}55`, borderRadius: '3px', padding: '8px 10px', marginBottom: '10px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: C.success, fontWeight: '700' }}>
                      ✓ Portes incluídos no preço — cliente paga apenas o que falta: <strong>{fmt2(mpFalta)} €</strong>
                    </p>
                  </div>
                  <details style={{ marginBottom: '10px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '11px', color: C.textMuted, fontWeight: '600' }}>Adicionar portes extra (excepcional — encomenda volumosa/internacional)</summary>
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: C.textMuted, fontWeight: '700' }}>€</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={portesInput}
                          onChange={e => setPortesInput(e.target.value)}
                          placeholder="0.00 (default)"
                          style={{ width: '100%', height: '34px', padding: '0 10px 0 26px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    {portesInput && parseFloat(portesInput.replace(',', '.')) > 0 && (
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.warning, fontWeight: '600' }}>
                        Cliente paga {fmt2(mpFalta)} € + {fmt2(mpPortes)} € portes = <strong>{fmt2(mp2aPrestacao)} €</strong>
                      </p>
                    )}
                  </details>
                  {emailSegundaMsg.texto && (
                    <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: emailSegundaMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `4px solid ${emailSegundaMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: emailSegundaMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px' }}>
                      {emailSegundaMsg.texto}
                    </div>
                  )}
                  <button onClick={confirmarPortesENotificar} disabled={savingPortes || enviandoEmailSegunda}
                    style={{ width: '100%', height: '40px', border: 'none', borderRadius: '2px', backgroundColor: C.primary, color: '#fff', cursor: (savingPortes || enviandoEmailSegunda) ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: (savingPortes || enviandoEmailSegunda) ? 0.7 : 1 }}>
                    {(savingPortes || enviandoEmailSegunda) ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> A processar...</> : <><Send size={14} /> Notificar 2.ª Prestação</>}
                  </button>
                </div>
              )}

              {/* Linhas para factura (c/IVA, ja com desconto) — admin only */}
              {isAdmin && modalItens.length > 0 && (() => {
                const interno = internosIds.has(modalPag.cliente_id)
                const particular = particularesIds.has(modalPag.cliente_id)
                const descPct = Number(modalPag.desconto_percentagem) || 0
                // Portes DESTE cliente, não a constante.
                //
                // O desconto não incide sobre os portes embutidos no preço, e a
                // RPC protege `qtd × perfis.portes_por_unidade` — o valor da
                // ficha DELE. Aqui estava a constante de 1,00 €, e os clientes
                // cuja ficha ainda tem 0,75 € davam desvio: na 0015/2026 do
                // Ruben, 25 un × 0,25 € de diferença na base protegida = 6,25 €,
                // 10% disso são 0,625 € que × 1,23 dão os 0,77 € que o aviso
                // acusava. As linhas para facturar não batiam com o que ele
                // pagou — e uma factura emitida por esse valor deixava 77
                // cêntimos por explicar na contabilidade.
                const IVA = 0.23
                const fmtIvaMul = interno ? 1 : (1 + IVA)
                // O desconto NÃO se recalcula: usa-se o que ficou GRAVADO na
                // encomenda.
                //
                // Recalcular a partir da ficha do cliente dá outro número
                // sempre que um parâmetro mudou desde a encomenda. A 0015/2026
                // é de 09/07: a ficha do Ruben tinha 0,75 €/un de portes nessa
                // altura e hoje tem 1,00. O desconto gravado (114,26 €) foi
                // calculado com 0,75; a conta feita hoje dava 113,63 — e as
                // linhas somavam 1288,63 quando ele pagou 1287,86.
                //
                // Uma factura tem de dizer o que foi cobrado, não o que a mesma
                // encomenda custaria se fosse feita hoje. O desconto gravado
                // reparte-se pelas linhas na proporção do valor de cada uma, e
                // o resto do arredondamento vai para a maior — assim a soma
                // bate ao cêntimo com o total da encomenda, sempre.
                const brutoLinhas = modalItens.map(it =>
                  (Number(it.preco_unitario) || 0) * (Number(it.quantidade) || 0))
                const brutoTotal = brutoLinhas.reduce((s, v) => s + v, 0)
                const descGravado = Number(modalPag.desconto_valor) || 0
                const descLinhas = brutoLinhas.map(v =>
                  brutoTotal > 0 ? Math.round((descGravado * v / brutoTotal) * 100) / 100 : 0)
                const restoDesc = Math.round((descGravado - descLinhas.reduce((s, v) => s + v, 0)) * 100) / 100
                if (restoDesc !== 0 && descLinhas.length > 0) {
                  const iMaior = brutoLinhas.indexOf(Math.max(...brutoLinhas))
                  descLinhas[iMaior] = Math.round((descLinhas[iMaior] + restoDesc) * 100) / 100
                }
                const linhas = modalItens.map((it, i) => {
                  const qtd = Number(it.quantidade) || 0
                  const totalLinhaSIva = brutoLinhas[i] - descLinhas[i]
                  // Aplicar IVA ao preco com desconto (interno fica = sIva)
                  const totalLinha = totalLinhaSIva * fmtIvaMul
                  const precoUnDesc = qtd > 0 ? totalLinha / qtd : 0
                  return { nome: it.produto_nome, ean: it.produto_ean, qtd, precoUnDesc, totalLinha, totalLinhaSIva }
                })
                const somaComIva = linhas.reduce((s, l) => s + l.totalLinha, 0)
                const somaSemIva = linhas.reduce((s, l) => s + l.totalLinhaSIva, 0)
                const ivaValor = somaComIva - somaSemIva
                const totalEsperado = Number(modalPag.total_final) || (somaSemIva * fmtIvaMul)
                const desvio = Math.abs(somaComIva - totalEsperado) > 0.05
                // O EAN VAI A FRENTE, porque e o que vai sempre na factura.
                //
                // Sem ele, quem passa a factura tinha de voltar ao ecra artigo
                // a artigo para o ir buscar. E o codigo do artigo: se for o
                // errado, a factura sai com o produto errado e so o cliente da
                // por isso.
                //
                // MUDA O QUE SE COLA: eram tres campos (qtd, preco, total) e
                // passam a ser quatro, com o EAN no primeiro.
                const linhaCopia = (l) => [l.ean || '', l.qtd, fmt2(l.precoUnDesc).replace('.', ','), fmt2(l.totalLinha).replace('.', ',')].join(String.fromCharCode(9))
                const blocoCopiar = linhas.map(linhaCopia).join(String.fromCharCode(10))
                const copiarTudo = async () => {
                  try { await navigator.clipboard.writeText(blocoCopiar) } catch {}
                }
                return (
                  <div style={{ background: '#f9fafb', border: `1px solid ${C.border}`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FileText size={13} /> Linhas para factura · {interno ? 's/IVA' : 'c/IVA'}{descPct > 0 ? ` (já com desconto ${fmt2(descPct)}%)` : ''}
                      </p>
                      <button onClick={copiarTudo} title="Copiar Qtd · Preço unit · Total — formato TAB (cola direto no Sage/Excel)"
                        style={{ height: '26px', padding: '0 10px', border: `1px solid ${C.primary}`, borderRadius: '3px', background: '#fff', color: C.primary, cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Copy size={11} /> Copiar tudo
                      </button>
                    </div>
                    <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted }}>
                      {interno
                        ? 'Cliente interno — preço unitário = preço de custo + 0,75 €/un. Sem IVA (Reverse Charge OSS).'
                        : particular
                          ? `Cliente particular B2C — valores apresentados c/IVA 23% incluído.`
                          : `Preço unitário c/IVA 23%. O desconto é o que ficou gravado na encomenda (${fmt2(descGravado)} € s/IVA), repartido pelas linhas — não é recalculado, para a factura bater com o que foi cobrado.`}
                    </p>
                    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 110px 120px 30px', padding: '6px 10px', background: '#f3f4f6', borderBottom: `1px solid ${C.border}`, fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        <span>Produto</span>
                        <span style={{ textAlign: 'right' }}>Qtd</span>
                        <span style={{ textAlign: 'right' }}>Preço un.</span>
                        <span style={{ textAlign: 'right' }}>Total linha</span>
                        <span></span>
                      </div>
                      {linhas.map((l, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 110px 120px 30px', padding: '8px 10px', borderBottom: idx < linhas.length - 1 ? `1px solid ${C.border}` : 'none', fontSize: '12px', alignItems: 'center' }}>
                          <span style={{ color: C.text, lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.nome}</span>
                          <span style={{ textAlign: 'right', color: C.textMuted, fontWeight: '600' }}>{l.qtd}</span>
                          <span style={{ textAlign: 'right', color: C.text, fontWeight: '700', fontFamily: 'monospace' }}>{fmt2(l.precoUnDesc)} €</span>
                          <span style={{ textAlign: 'right', color: C.primary, fontWeight: '800', fontFamily: 'monospace' }}>{fmt2(l.totalLinha)} €</span>
                          <CopyButton text={linhaCopia(l)} size={11} title="Copiar linha (EAN · Qtd · Preço un · Total)" />
                        </div>
                      ))}
                      <div style={{ padding: '8px 10px', background: '#fafbfc', borderTop: `1px solid ${C.border}`, fontSize: '12px' }}>
                        {!interno && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: C.textMuted, fontWeight: '600' }}>Base s/IVA{descPct > 0 ? ' (já descontada)' : ''}</span>
                              <span style={{ color: C.textMuted, fontWeight: '600', fontFamily: 'monospace' }}>{fmt2(somaSemIva)} €</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginTop: '3px' }}>
                              <span style={{ color: C.textMuted, fontWeight: '600' }}>IVA 23% (incluído nas linhas acima)</span>
                              <span style={{ color: C.textMuted, fontWeight: '600', fontFamily: 'monospace' }}>{fmt2(ivaValor)} €</span>
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginTop: '4px', paddingTop: '4px', borderTop: `1px dashed ${C.border}` }}>
                          <span style={{ color: C.text, fontWeight: '700' }}>Total {interno ? 's/IVA' : 'c/IVA'}</span>
                          <span style={{ color: C.success, fontWeight: '800', fontFamily: 'monospace', fontSize: '13px' }}>{fmt2(somaComIva)} €</span>
                        </div>
                      </div>
                    </div>
                    {desvio && (
                      <p style={{ margin: '8px 0 0', fontSize: '11px', color: C.danger, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <AlertCircle size={12} /> Atenção: soma das linhas ({fmt2(somaComIva)} €) não bate com o total da encomenda ({fmt2(totalEsperado)} €). Verifica antes de facturar.
                      </p>
                    )}
                  </div>
                )
              })()}

              {/* Factura do SINAL — só admin, e só em encomendas com sinal.
                  Empresas levam factura ao pagar o sinal e outra no pagamento
                  final; sem isto so havia um espaco e a segunda substituia a
                  primeira. Sem verificacao automatica de proposito: a da factura
                  final compara o total da encomenda com o do PDF, e aqui o valor
                  esperado e o sinal. Ver supabase/sql/sinal_por_unidades.sql */}
              {isAdmin && modalPag.sinal_escolhido != null && (
                <div style={{ background: modalPag.fatura_sinal_pdf_url ? '#e8f5e9' : '#fff8e1', border: `1px solid ${modalPag.fatura_sinal_pdf_url ? `${C.success}55` : `${C.warning}55`}`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '800', color: C.text, marginBottom: '2px' }}>
                    Factura do sinal (1.ª prestação)
                  </div>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '10px', lineHeight: 1.5 }}>
                    Valor a facturar: <strong style={{ color: C.text }}>{fmt2(modalPag.sinal_escolhido)} €</strong>
                    {' · '}na entrega fica <strong style={{ color: C.text }}>{fmt2(Number(modalPag.total_final || 0) - Number(modalPag.sinal_escolhido))} €</strong>
                  </div>

                  {/* O QUE facturar, artigo a artigo. Reconstruido com a mesma
                      regra do RPC — CEIL(quantidade x pct) por linha — para o
                      admin nao ter de andar a fazer contas. So se mostra quando
                      a soma reproduz o sinal gravado: se nao bater, mostrar
                      quantidades erradas era pior do que nao mostrar nada. */}
                  {(() => {
                    const itens = modalPag.encomenda_itens || []
                    const pct = sinalPctPorCliente.get(modalPag.cliente_id)
                    if (!itens.length || !pct) return null
                    const portes = portesPorCliente.get(modalPag.cliente_id) ?? 1
                    const desc = Number(modalPag.desconto_percentagem || 0)
                    const iva = internosIds.has(modalPag.cliente_id) ? 0 : 0.23
                    const linhas = itens.map(it => {
                      const qtd = Number(it.quantidade || 0)
                      const pu = Number(it.preco_unitario || 0)
                      // Preco efectivo: ja com o desconto do cliente, que nao
                      // incide sobre o 1 EUR/un de portes embutido no preco.
                      const unitSemIva = pu - Math.max(0, pu - portes) * (desc / 100)
                      const unitComIva = unitSemIva * (1 + iva)
                      const un = Math.ceil(qtd * pct / 100)
                      return {
                        nome: it.produto_nome, ean: it.produto_ean,
                        un, restantes: qtd - un,
                        unitSemIva, unitComIva,
                        valor: un * unitComIva,
                      }
                    })
                    const soma = linhas.reduce((s, l) => s + l.valor, 0)
                    const bate = Math.abs(soma - Number(modalPag.sinal_escolhido)) <= 0.02
                    if (!bate) return null
                    return (
                      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '3px', padding: '9px 11px', marginBottom: '10px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' }}>
                          A facturar agora
                        </div>
                        {linhas.map((l, i) => (
                          <div key={i} style={{ fontSize: '11.5px', color: C.text, lineHeight: 1.55, paddingBottom: '6px', marginBottom: '6px', borderBottom: i < linhas.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                            <div><strong>{l.un} un</strong> · {l.nome}</div>
                            {l.ean && (
                              <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                EAN <span style={{ fontFamily: 'monospace', color: C.text }}>{l.ean}</span>
                                <CopyButton text={l.ean} size={11} title="Copiar EAN" />
                              </div>
                            )}
                            <div style={{ fontSize: '11px', color: C.textMuted }}>
                              Unitário: <strong style={{ color: C.text }}>{fmt2(l.unitSemIva)} €</strong> s/IVA
                              {iva > 0 && <> · <strong style={{ color: C.text }}>{fmt2(l.unitComIva)} €</strong> c/IVA</>}
                              {' · '}total <strong style={{ color: C.text }}>{fmt2(l.valor)} €</strong> {iva > 0 ? 'c/IVA' : ''}
                            </div>
                          </div>
                        ))}
                        <div style={{ fontSize: '10.5px', color: C.textMuted, marginTop: '5px', paddingTop: '5px', borderTop: `1px dashed ${C.border}` }}>
                          Na entrega: {linhas.map(l => `${l.restantes} un ${l.nome}`).join(' · ')}
                        </div>
                      </div>
                    )
                  })()}

                  {/* A FACTURA FINAL, EM UNIDADES.
                      O bloco de cima esconde-se quando as contas do sinal nao
                      batem — o que acontece exactamente depois de a encomenda
                      ser editada, que e quando faz mais falta. Este aparece
                      sempre, porque parte do que falta receber e nao do que o
                      sinal foi. */}
                  {facturaFinal && (
                    <div style={{ background: '#fff', border: `1px solid ${C.info}55`, borderRadius: '3px', padding: '9px 11px', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '5px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', color: C.info, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                          Factura final — o que facturar
                        </span>
                        <CopyButton text={facturaFinal.texto} size={12} title="Copiar para a facturação" />
                      </div>
                      {facturaFinal.linhas.map((l, i) => (
                        <div key={i} style={{ fontSize: '11.5px', color: C.text, lineHeight: 1.55, paddingBottom: '6px', marginBottom: '6px', borderBottom: i < facturaFinal.linhas.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                          <div><strong>{l.un} un</strong> · {l.nome}</div>
                          {l.ean && (
                            <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                              EAN <span style={{ fontFamily: 'monospace', color: C.text }}>{l.ean}</span>
                              <CopyButton text={l.ean} size={11} title="Copiar EAN" />
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: C.textMuted }}>
                            Preço/un <strong style={{ color: C.text }}>{fmt2(l.unitSemIva)} €</strong> s/IVA
                            {facturaFinal.iva > 0 && <> · <strong style={{ color: C.text }}>{fmt2(l.unitComIva)} €</strong> c/IVA</>}
                            <br />Total da linha <strong style={{ color: C.text }}>{fmt2(Math.round(l.un * l.unitSemIva * 100) / 100)} €</strong> s/IVA
                            {facturaFinal.iva > 0 && <> · <strong style={{ color: C.text }}>{fmt2(Math.round(l.un * l.unitComIva * 100) / 100)} €</strong> c/IVA</>}
                          </div>
                        </div>
                      ))}
                      <table style={{ width: '100%', fontSize: '11.5px', borderCollapse: 'collapse', marginTop: '4px', borderTop: `1px dashed ${C.border}` }}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '4px 0', color: C.textMuted }}>Subtotal das linhas <span style={{ fontSize: '10px' }}>s/IVA</span></td>
                            <td style={{ padding: '4px 0', textAlign: 'right', color: C.text, fontWeight: '600' }}>{fmt2(facturaFinal.soma)} €</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '2px 0', color: C.warning, fontWeight: '700' }}>
                              Linha “Ajustes” <span style={{ fontSize: '10px', fontWeight: '600' }}>1 un, preço s/IVA</span>
                            </td>
                            <td style={{ padding: '2px 0', textAlign: 'right', color: C.warning, fontWeight: '800', whiteSpace: 'nowrap' }}>
                              {fmt2(facturaFinal.ajuste)} € <span style={{ fontSize: '10px', fontWeight: '600' }}>s/IVA</span>
                              {facturaFinal.iva > 0 && <><br />{fmt2(facturaFinal.ajusteComIva)} € <span style={{ fontSize: '10px', fontWeight: '600' }}>c/IVA</span></>}
                            </td>
                          </tr>
                          <tr style={{ borderTop: `1px solid ${C.border}` }}>
                            <td style={{ padding: '5px 0 0', color: C.textMuted }}>Total s/IVA</td>
                            <td style={{ padding: '5px 0 0', textAlign: 'right', color: C.text, fontWeight: '600' }}>{fmt2(facturaFinal.totalSemIva)} €</td>
                          </tr>
                          {facturaFinal.iva > 0 && (
                            <tr>
                              <td style={{ padding: '2px 0', color: C.textMuted }}>IVA 23%</td>
                              <td style={{ padding: '2px 0', textAlign: 'right', color: C.text, fontWeight: '600' }}>{fmt2(facturaFinal.valorIva)} €</td>
                            </tr>
                          )}
                          <tr style={{ borderTop: `1px solid ${C.border}` }}>
                            <td style={{ padding: '5px 0 0', fontWeight: '800', color: C.text }}>Total da factura</td>
                            <td style={{ padding: '5px 0 0', textAlign: 'right', fontWeight: '800', fontSize: '13px', color: C.text }}>{fmt2(facturaFinal.totalComIva)} €</td>
                          </tr>
                        </tbody>
                      </table>
                      <p style={{ margin: '7px 0 0', fontSize: '10.5px', color: C.textMuted, lineHeight: 1.45 }}>
                        <strong>Todos os valores sem IVA</strong>, como no Sage — ele acrescenta os 23% por cima.
                        As unidades ficam sempre <strong>abaixo</strong> do valor em dívida — o ajuste soma-se, nunca se subtrai.
                        {facturaFinal.cortou && <> Foram retiradas unidades porque a encomenda mudou depois do sinal ter sido facturado.</>}
                      </p>
                    </div>
                  )}

                  {modalPag.fatura_sinal_pdf_url ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: C.success, fontWeight: '700' }}>
                        ✓ {modalPag.fatura_sinal_numero || 'sem número'} · {fmtDate(modalPag.fatura_sinal_emitida_em)}
                      </span>
                      <button onClick={() => verFactura(modalPag.fatura_sinal_pdf_url)}
                        style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.border}`, borderRadius: '2px', background: '#fff', cursor: 'pointer', fontSize: '12px' }}>
                        Ver PDF
                      </button>
                      <button onClick={removerFaturaSinal}
                        style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.danger}55`, borderRadius: '2px', background: '#fff', color: C.danger, cursor: 'pointer', fontSize: '12px' }}>
                        Remover
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 130px' }}>
                        <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Nº factura</label>
                        <input value={faturaSinalForm.numero} placeholder="Ex: FT 2026/12"
                          onChange={e => setFaturaSinalForm(f => ({ ...f, numero: e.target.value }))}
                          style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ flex: '0 1 150px' }}>
                        <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Data</label>
                        <input type="date" value={faturaSinalForm.data}
                          onChange={e => setFaturaSinalForm(f => ({ ...f, data: e.target.value }))}
                          style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box' }} />
                      </div>
                      <label style={{ height: '32px', padding: '0 14px', border: `1px solid ${C.primary}`, borderRadius: '2px', background: C.primary, color: '#fff', cursor: uploadingFaturaSinal ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {uploadingFaturaSinal ? 'A enviar…' : 'Anexar PDF'}
                        <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={uploadingFaturaSinal}
                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadFaturaSinal(f); e.target.value = '' }} />
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Fatura — só admin */}
              {isAdmin && (
                <div style={{ background: modalPag.fatura_pdf_url ? '#e8f5e9' : '#eff6fc', border: `1px solid ${modalPag.fatura_pdf_url ? `${C.success}55` : `${C.primary}33`}`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px' }}>
                  {modalPag.fatura_pdf_url ? (
                    modalPag.fatura_verificada_em ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', marginBottom: '12px', background: '#fff', border: `2px solid ${C.success}`, borderRadius: '6px', boxShadow: `0 1px 3px ${C.success}33` }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.success, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <CheckCircle size={22} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: C.success, marginBottom: '2px' }}>
                            Factura verificada (dados OK) e enviada ao cliente
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted, lineHeight: '1.5' }}>
                            {modalPag.fatura_numero ? <><strong>{modalPag.fatura_numero}</strong> · </> : null}
                            emitida em {fmtDate(modalPag.fatura_emitida_em)} · enviada para <strong>{modalPag.cliente_email}</strong>
                            <span style={{ display: 'block', marginTop: '2px', fontSize: '10px', color: C.success, fontWeight: '600' }}>
                              ✓ Verificada automaticamente em {fmtDate(modalPag.fatura_verificada_em)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', marginBottom: '12px', background: '#fff', border: `2px solid ${C.warning}`, borderRadius: '6px', boxShadow: `0 1px 3px ${C.warning}33` }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.warning, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <AlertTriangle size={22} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '800', color: '#92400e', marginBottom: '2px' }}>
                            Factura emitida e enviada — não verificada automaticamente
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted, lineHeight: '1.5' }}>
                            {modalPag.fatura_numero ? <><strong>{modalPag.fatura_numero}</strong> · </> : null}
                            emitida em {fmtDate(modalPag.fatura_emitida_em)} · enviada para <strong>{modalPag.cliente_email}</strong>
                            <span style={{ display: 'block', marginTop: '2px', fontSize: '10px', color: '#92400e', fontWeight: '600' }}>
                              PDF carregado antes do sistema de verificação automática (ou verificação foi ignorada).
                            </span>
                          </div>
                        </div>
                        <button onClick={reverificarFatura} disabled={reverificando} title="Faz download do PDF, corre verificação NIF/Nome/Total e marca como verificada se passar"
                          style={{ flexShrink: 0, height: '32px', padding: '0 12px', border: `1px solid ${C.warning}`, borderRadius: '4px', background: '#fff', color: '#92400e', cursor: reverificando ? 'wait' : 'pointer', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {reverificando ? <><RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> A verificar...</> : <><CheckCircle size={11} /> Verificar agora</>}
                        </button>
                      </div>
                    )
                  ) : (
                    <>
                      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FileText size={13} /> Fatura — Por carregar
                      </p>
                      <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted }}>
                        Emite a fatura no Sage; depois carrega o PDF aqui — o cliente recebe automaticamente um email com a fatura anexada.
                      </p>
                    </>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <div>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '4px' }}>
                        Nº Fatura <span style={{ color: C.danger }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={faturaForm.numero}
                        onChange={e => setFaturaForm(f => ({ ...f, numero: e.target.value }))}
                        placeholder="ex: FAT 2026/123 (obrigatório)"
                        required
                        style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${!faturaForm.numero?.trim() && !modalPag.fatura_pdf_url ? C.danger : C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box', background: !faturaForm.numero?.trim() && !modalPag.fatura_pdf_url ? '#fff8f8' : '#fff' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '4px' }}>Data Emissão</label>
                      <input
                        type="date"
                        value={faturaForm.data}
                        onChange={e => setFaturaForm(f => ({ ...f, data: e.target.value }))}
                        style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {faturaMsg.texto && (() => {
                    const isOk = faturaMsg.tipo === 'ok'
                    const isInfo = faturaMsg.tipo === 'info'
                    const bg = isOk ? '#dff6dd' : isInfo ? '#eff6fc' : '#fde7e9'
                    const cor = isOk ? C.success : isInfo ? C.primary : C.danger
                    return (
                      <div style={{ marginBottom: '8px', padding: '8px 10px', background: bg, borderLeft: `3px solid ${cor}`, borderRadius: '2px', fontSize: '12px', color: cor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {isInfo && <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                        {faturaMsg.texto}
                      </div>
                    )
                  })()}

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                    <label style={{ flex: 1, height: '38px', border: `2px dashed ${C.primary}66`, borderRadius: '4px', background: '#fff', color: C.primary, cursor: (uploadingFatura || enviandoFaturaEmail) ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: (uploadingFatura || enviandoFaturaEmail) ? 0.7 : 1 }}>
                      {uploadingFatura ? (<><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> A carregar PDF…</>) :
                        enviandoFaturaEmail ? (<><Send size={13} /> A enviar email…</>) :
                        modalPag.fatura_pdf_url ? (<><FileText size={13} /> Substituir PDF da fatura</>) :
                        (<><FileText size={13} /> Carregar PDF da fatura</>)}
                      <input type="file" accept="application/pdf,.pdf" disabled={uploadingFatura || enviandoFaturaEmail}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFaturaPDF(f); e.target.value = '' }}
                        style={{ display: 'none' }} />
                    </label>
                    {modalPag.fatura_pdf_url && (
                      <>
                        <button onClick={() => verFactura()}
                          title="Abrir PDF da factura numa nova aba"
                          style={{ height: '38px', padding: '0 12px', border: `1px solid ${C.primary}`, borderRadius: '4px', background: '#fff', color: C.primary, cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Eye size={12} /> Ver factura
                        </button>
                        <button onClick={reenviarFaturaEmail} disabled={enviandoFaturaEmail || uploadingFatura}
                          title="Reenviar email com fatura para o cliente"
                          style={{ height: '38px', padding: '0 12px', border: `1px solid ${C.primary}`, borderRadius: '4px', background: '#fff', color: C.primary, cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px', opacity: (enviandoFaturaEmail || uploadingFatura) ? 0.7 : 1 }}>
                          <Send size={12} /> Reenviar
                        </button>
                        <button onClick={removerFatura} disabled={savingFatura || uploadingFatura}
                          title="Apagar PDF e desassociar fatura"
                          style={{ height: '38px', padding: '0 12px', border: `1px solid ${C.danger}`, borderRadius: '4px', background: '#fff', color: C.danger, cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Re-aplicar trânsitos (admin only) */}
              {isAdmin && (
                <div style={{ background: '#fff4ce', border: `1px solid ${C.warning}66`, borderRadius: '4px', padding: '12px 14px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.warning, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Truck size={12} /> Trânsitos automáticos
                    {modalPag.transitos_aplicados ? (
                      <span style={{ fontSize: '10px', color: C.success, background: '#dff6dd', border: `1px solid ${C.success}55`, padding: '1px 6px', borderRadius: '8px', fontWeight: '700' }}>aplicados ✓</span>
                    ) : (
                      <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: '500' }}>(pendentes)</span>
                    )}
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.textMuted }}>
                    Ao confirmar a 1.ª prestação, as quantidades de cada produto são automaticamente adicionadas aos slots de trânsito do produto em Gestão de Stock, com data = hoje + prazo entrega + 2 dias buffer.
                  </p>
                  <button
                    onClick={async () => {
                      const msg = modalPag.transitos_aplicados
                        ? 'Os trânsitos JÁ foram aplicados a esta encomenda. Re-aplicar vai SOMAR de novo aos slots (pode duplicar quantidades). Tens a certeza?\n\nDica: vai a Gestão de Stock e limpa os slots actuais primeiro se necessário.'
                        : 'Aplicar trânsitos manualmente a esta encomenda? Cada produto vai receber a sua qty no slot apropriado, com data = hoje + tempo entrega + 2.'
                      if (!window.confirm(msg)) return
                      try {
                        const { error } = await supabase.rpc('reaplicar_transitos_encomenda', { p_enc_id: modalPag.id })
                        if (error) throw new Error(error.message)
                        const { data: encAtualizada } = await supabase.from('encomendas').select('transitos_aplicados').eq('id', modalPag.id).single()
                        setModalPag(prev => ({ ...prev, transitos_aplicados: encAtualizada?.transitos_aplicados ?? true }))
                        alert('Trânsitos aplicados. Verifica em Gestão de Stock os produtos desta encomenda.')
                      } catch (err) {
                        alert('Erro: ' + (err?.message || String(err)))
                      }
                    }}
                    style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.warning}`, background: C.surface, color: C.warning, borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <RefreshCw size={11} /> {modalPag.transitos_aplicados ? 'Re-aplicar trânsitos' : 'Aplicar trânsitos agora'}
                  </button>
                </div>
              )}

              {/* Factura do fornecedor — uso interno admin (cliente nunca vê) */}
              {isAdmin && (
                <div style={{ background: '#f3f2f1', border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <FileText size={12} /> Factura do Fornecedor <span style={{ fontSize: '10px', fontWeight: '600', color: C.textMuted, marginLeft: '4px' }}>(interno · cliente não vê)</span>
                  </p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={fornFatura}
                      onChange={e => setFornFatura(e.target.value)}
                      onBlur={guardarFornFatura}
                      placeholder="ex: FT 2026/487"
                      style={{ flex: 1, height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
                    />
                    {savingForn && <Loader size={14} style={{ animation: 'spin 1s linear infinite', color: C.textMuted }} />}
                    {fornMsg.texto && (
                      <span style={{ fontSize: '11px', fontWeight: '700', color: fornMsg.tipo === 'ok' ? C.success : C.danger }}>
                        {fornMsg.texto}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Tracking de envio — admin define + envia email automaticamente ao cliente */}
              {isAdmin && (
                <div style={{ background: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Truck size={13} /> Expedição & Tracking
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.textMuted }}>
                    Ao guardar, a encomenda passa a "enviada", o cliente recebe um email com o código de tracking e link para acompanhar.
                  </p>
                  {/* Banner: cliente já notificado (tracking gravado) */}
                  {modalPag.tracking && (() => {
                    const trk = detectTracking(modalPag.transportadora, modalPag.tracking)
                    return (
                      <div style={{ background: '#dff6dd', border: '1px solid #107c10', borderRadius: '4px', padding: '10px 12px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CheckCircle size={16} style={{ color: '#107c10', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#107c10' }}>
                            Cliente notificado{modalPag.data_envio ? ` em ${new Date(modalPag.data_envio).toLocaleDateString('pt-PT')}` : ''}
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#605e5c' }}>
                            {modalPag.transportadora || 'Tracking'}:{' '}
                            {trk ? (
                              <a href={trk.url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'monospace', fontWeight: '700', color: '#0078d4', textDecoration: 'underline' }}>
                                {/* Endereço por envio (ONTIME) em vez de código: escreve-se
                                    o nome, senão a linha enche-se de caracteres inúteis. */}
                                {/^https?:\/\//i.test(String(modalPag.tracking)) ? `Seguir envio · ${trk.nome}` : modalPag.tracking}
                              </a>
                            ) : (
                              <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{modalPag.tracking}</span>
                            )}
                            <CopyButton text={modalPag.tracking} size={12} title="Copiar tracking" />
                          </p>
                        </div>
                      </div>
                    )
                  })()}
                  {/* Badge: outras encomendas da mesma compra ao fornecedor */}
                  {irmasInfo && irmasInfo.count > 0 && (
                    <div style={{ background: '#fff4ce', border: '1px solid #f0d070', borderRadius: '4px', padding: '10px 12px', marginBottom: '10px' }}>
                      <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: '#c07400' }}>
                        Esta compra (factura {irmasInfo.numeroFatura}) tem mais {irmasInfo.count} encomenda{irmasInfo.count === 1 ? '' : 's'} associada{irmasInfo.count === 1 ? '' : 's'}
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '11px', color: '#605e5c' }}>
                        {irmasInfo.irmas.map(i => (
                          <li key={i.id}>{i.numero} — {i.cliente}</li>
                        ))}
                      </ul>
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#605e5c', fontStyle: 'italic' }}>
                        Ao gravar o tracking podes optar por aplicá-lo a todas (mesma encomenda física).
                      </p>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <div>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '4px' }}>Código tracking</label>
                      <input
                        type="text"
                        value={trackingForm.codigo}
                        onChange={e => setTrackingForm(f => ({ ...f, codigo: e.target.value }))}
                        placeholder="ex: LE123456789PT"
                        style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'monospace' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '4px' }}>Transportadora</label>
                      <select
                        value={trackingForm.transportadora}
                        onChange={e => setTrackingForm(f => ({ ...f, transportadora: e.target.value }))}
                        style={{ width: '100%', height: '32px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', background: '#fff', boxSizing: 'border-box' }}
                      >
                        <option value="">— escolher —</option>
                        <option value="CTT">CTT</option>
                        <option value="DHL">DHL</option>
                        <option value="UPS">UPS</option>
                        <option value="FedEx">FedEx</option>
                        <option value="GLS">GLS</option>
                        <option value="Seur">Seur</option>
                        <option value="Chronopost">Chronopost</option>
                        <option value="Nacex">Nacex</option>
                        <option value="Poste Italiane">Poste Italiane</option>
                        {/* ONTIME: é a que o fornecedor de entrega rápida usa
                            quase sempre. Estava a ter de se escolher "Outro",
                            e aí o código fica texto morto — sem link nenhum. */}
                        <option value="ONTIME">ONTIME</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '4px' }}>Data envio</label>
                      <input
                        type="date"
                        value={trackingForm.data}
                        onChange={e => setTrackingForm(f => ({ ...f, data: e.target.value }))}
                        style={{ width: '100%', height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  {trackingMsg.texto && (
                    <div style={{ marginBottom: '8px', padding: '8px 10px', background: trackingMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `3px solid ${trackingMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', fontSize: '12px', color: trackingMsg.tipo === 'ok' ? C.success : C.danger }}>
                      {trackingMsg.texto}
                    </div>
                  )}
                  {confirmPropagar && irmasInfo ? (
                    <div style={{ background: '#fff', border: `1px solid ${C.primary}`, borderRadius: '4px', padding: '12px 14px', marginTop: '4px' }}>
                      <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.text, fontWeight: '600' }}>
                        Aplicar este tracking a todas as {irmasInfo.count + 1} encomendas desta factura?
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                        <button
                          onClick={() => enviarTracking('todas')}
                          disabled={enviandoTracking}
                          style={{ height: '36px', border: 'none', borderRadius: '4px', background: C.success, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                          Aplicar a todas
                        </button>
                        <button
                          onClick={() => enviarTracking('so_esta')}
                          disabled={enviandoTracking}
                          style={{ height: '36px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                          Só esta encomenda
                        </button>
                        <button
                          onClick={() => setConfirmPropagar(false)}
                          disabled={enviandoTracking}
                          style={{ height: '36px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.bg, color: C.textMuted, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => enviarTracking()}
                      disabled={enviandoTracking || !trackingForm.codigo.trim()}
                      style={{ width: '100%', height: '38px', border: 'none', borderRadius: '4px', background: enviandoTracking ? C.bg : C.primary, color: enviandoTracking ? C.textMuted : '#fff', cursor: (enviandoTracking || !trackingForm.codigo.trim()) ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: (!trackingForm.codigo.trim() && !enviandoTracking) ? 0.5 : 1 }}>
                      {enviandoTracking ? (<><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> A enviar…</>) :
                        modalPag.tracking ? (<><Send size={13} /> Reenviar tracking ao cliente</>) :
                        (<><Send size={13} /> Marcar enviado & notificar cliente</>)}
                    </button>
                  )}
                </div>
              )}

              {/* Resumo data e estado */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '140px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '8px 10px' }}>
                  <p style={{ margin: '0 0 3px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Data Encomenda</p>
                  <p style={{ margin: 0, fontWeight: '700', color: C.text }}>{fmtDate(modalPag.created_at)}</p>
                </div>
                <div style={{ flex: 1, minWidth: '140px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '8px 10px' }}>
                  <p style={{ margin: '0 0 3px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Quantidade</p>
                  <p style={{ margin: 0, fontWeight: '700', color: C.text }}>{modalPag.quantidade} un.</p>
                </div>
                <div style={{ flex: 1, minWidth: '140px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '8px 10px' }}>
                  <p style={{ margin: '0 0 3px', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Estado</p>
                  <p style={{ margin: 0, fontWeight: '700', color: estadoInfo(modalPag, mpTotal).color }}>
                    {estadoInfo(modalPag, mpTotal).label}
                  </p>
                </div>
              </div>

              {isAdmin && (
                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${C.border}` }}>
                  <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zona de perigo</p>
                  <button
                    onClick={eliminarEncomenda}
                    disabled={eliminando}
                    style={{ width: '100%', height: '38px', border: `1px solid ${C.danger}`, borderRadius: '2px', backgroundColor: eliminando ? '#edebe9' : '#fde7e9', color: C.danger, cursor: eliminando ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: eliminando ? 0.7 : 1 }}
                  >
                    <Trash2 size={14} /> {eliminando ? 'A eliminar...' : 'Eliminar Encomenda'}
                  </button>
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.textMuted, textAlign: 'center' }}>
                    Apaga a encomenda e todos os seus itens. O número {modalPag.numero_encomenda || '—'} fica disponível para a próxima encomenda.
                    {(modalPag.pago_1a_prestacao || modalPag.pago_2a_prestacao) && (
                      <><br /><strong style={{ color: C.danger }}>
                        Esta encomenda tem pagamentos registados. Se foi anulada, usa Cancelar em vez de apagar - as contas ficam iguais e o motivo fica guardado.
                      </strong></>
                    )}
                  </p>
                </div>
              )}

              {/* Fechar tambem no fim: a modal e comprida e obrigava a subir ao
                  topo so para carregar no X. */}
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${C.border}` }}>
                <button onClick={() => setModalPag(null)}
                  style={{ width: '100%', height: '40px', border: `1px solid ${C.border}`, borderRadius: '3px', background: '#fff', color: C.text, cursor: 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                  <X size={15} /> Fechar
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── SUB-MODAL ADMIN: ANEXAR COMPROVATIVO + BANCO + DATA + REF ── */}
      {compModal && modalPag && (() => {
        const labelPrest = compModal.prestacao === '1a' ? '1.ª Prestação' : compModal.prestacao === '2a' ? '2.ª Prestação' : 'Total (100%)'
        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
            onClick={(e) => { if (e.target === e.currentTarget && !uploadingComp) setCompModal(null) }}
          >
            <div style={{ background: C.surface, borderRadius: '6px', width: '100%', maxWidth: '440px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
              <div style={{ background: C.primary, padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.85)' }}>Anexar comprovativo — {labelPrest}</p>
                  <p style={{ margin: '3px 0 0', fontSize: '15px', fontWeight: '700' }}>{modalPag.numero_encomenda || '—'}</p>
                  {modalPag.cliente_nome && <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>{modalPag.cliente_nome}</p>}
                </div>
                <button onClick={() => !uploadingComp && setCompModal(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px', fontSize: '20px' }}>×</button>
              </div>
              <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Referência canónica (sempre visível) */}
                {compModal.ref && (
                  <div style={{ padding: '10px 12px', background: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.primary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Referência da transferência</p>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', fontFamily: 'monospace', color: C.text, wordBreak: 'break-all' }}>{compModal.ref}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '10px', color: C.textMuted }}>Esta referência é enviada ao cliente no email automático.</p>
                  </div>
                )}
                {/* Ficheiro */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>Ficheiro (PDF/imagem) *</label>
                  <input type="file" accept="application/pdf,image/*"
                    disabled={!!uploadingComp}
                    onChange={(ev) => { const f = ev.target.files?.[0] || null; setCompModal(p => ({ ...p, file: f, msg: { tipo: '', texto: '' } })) }}
                    style={{ width: '100%', padding: '8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '13px', boxSizing: 'border-box' }} />
                  {compModal.file && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.success, fontWeight: '600' }}>✓ {compModal.file.name}</p>
                  )}
                </div>
                {/* Banco com autocomplete */}
                <div style={{ position: 'relative' }}>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>Banco de destino *</label>
                  <input type="text"
                    value={compModal.banco}
                    onChange={(ev) => setCompModal(p => ({ ...p, banco: ev.target.value, bancoOpen: true }))}
                    onFocus={() => setCompModal(p => ({ ...p, bancoOpen: true }))}
                    onBlur={() => setTimeout(() => setCompModal(p => p ? ({ ...p, bancoOpen: false }) : p), 150)}
                    placeholder="Comece a escrever: Mil, Sant, BPI, ..."
                    autoComplete="off"
                    disabled={!!uploadingComp}
                    style={{ width: '100%', height: '36px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '13px', boxSizing: 'border-box' }} />
                  {compModal.bancoOpen && (() => {
                    const q = (compModal.banco || '').trim().toLowerCase()
                    const sugestoes = q.length === 0
                      ? BANCOS_PT.slice(0, 8)
                      : BANCOS_PT.filter(b => b.toLowerCase().includes(q)).slice(0, 8)
                    if (sugestoes.length === 0) return null
                    return (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '2px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 6px 18px rgba(0,0,0,0.10)', zIndex: 10, maxHeight: '240px', overflowY: 'auto' }}>
                        {sugestoes.map(b => (
                          <div key={b}
                            onMouseDown={ev => { ev.preventDefault(); setCompModal(p => ({ ...p, banco: b, bancoOpen: false })) }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.bg}` }}
                            onMouseEnter={ev => ev.currentTarget.style.background = C.bg}
                            onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                          >{b}</div>
                        ))}
                        {q.length > 0 && !BANCOS_PT.some(b => b.toLowerCase() === q) && (
                          <div style={{ padding: '6px 12px', fontSize: '11px', color: C.textMuted, fontStyle: 'italic', background: C.bg }}>
                            ou continua a escrever um banco que não está na lista
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
                {/* Data */}
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' }}>Data do pagamento *</label>
                  <input type="date"
                    value={compModal.data}
                    onChange={(ev) => setCompModal(p => ({ ...p, data: ev.target.value }))}
                    disabled={!!uploadingComp}
                    style={{ width: '100%', height: '36px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                {/* Aviso fluxo */}
                <div style={{ padding: '8px 10px', background: '#fff4ce', border: `1px solid ${C.warning}66`, borderRadius: '3px', fontSize: '11px', color: C.text }}>
                  <strong>Ao confirmar:</strong> anexa o ficheiro, marca a prestação como paga com o banco indicado, dispara o email de confirmação ao cliente (com a referência) e — se for 1.ª/total — aplica os trânsitos.
                </div>
                {/* Mensagem */}
                {compModal.msg?.texto && (
                  <p style={{ margin: 0, fontSize: '12px', color: compModal.msg.tipo === 'erro' ? C.danger : C.success, fontWeight: '600' }}>{compModal.msg.texto}</p>
                )}
                {/* Acções */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button onClick={() => !uploadingComp && setCompModal(null)} disabled={!!uploadingComp}
                    style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, background: '#fff', color: C.text, borderRadius: '3px', cursor: uploadingComp ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>
                    Cancelar
                  </button>
                  <button onClick={submeterComprovativoAdmin} disabled={!!uploadingComp || !compModal.file || !compModal.banco.trim()}
                    style={{ height: '36px', padding: '0 14px', border: 'none', background: C.success, color: '#fff', borderRadius: '3px', cursor: (!!uploadingComp || !compModal.file || !compModal.banco.trim()) ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', opacity: (!!uploadingComp || !compModal.file || !compModal.banco.trim()) ? 0.6 : 1 }}>
                    {uploadingComp ? 'A processar…' : 'Anexar e confirmar pagamento'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── MODAL RECUSAR COMPROVATIVO ── */}
      {rejeitarModal && modalPag && (() => {
        const motivo = rejeitarModal.motivo || ''
        const motivoOk = motivo.trim().length >= 5
        const sugestoes = [
          'O valor transferido não corresponde ao da prestação.',
          'O comprovativo está ilegível.',
          'O comprovativo é de outra encomenda.',
          'A transferência não chegou a entrar na conta.',
        ]
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget && !rejeitando) setRejeitarModal(null) }}>
          <div style={{ background: C.surface, borderRadius: '6px', width: '100%', maxWidth: '460px', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ background: C.danger, padding: '14px 18px' }}>
              <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.85)' }}>
                Recusar comprovativo — {rejeitarModal.prest === '1a' ? '1.ª Prestação' : '2.ª Prestação'}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: '700', color: '#fff' }}>{modalPag.numero_encomenda || '—'} · {modalPag.cliente_nome || modalPag.cliente_email}</p>
            </div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ margin: 0, fontSize: '12px', color: C.text, lineHeight: 1.55 }}>
                O cliente recebe este motivo por email e vê-o em <strong>Minhas Encomendas</strong>, onde pode submeter um novo comprovativo.
                O ficheiro actual <strong>não é apagado</strong> — fica como registo do que foi enviado.
              </p>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>Motivo (vai para o cliente)</label>
                <textarea value={motivo} rows={3} autoFocus
                  onChange={e => setRejeitarModal(m => ({ ...m, motivo: e.target.value }))}
                  placeholder="Ex: O valor transferido foi 68,59 € mas a 1.ª prestação é de 79,72 €."
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '13px', color: C.text, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '7px' }}>
                  {sugestoes.map(s => (
                    <button key={s} onClick={() => setRejeitarModal(m => ({ ...m, motivo: s }))}
                      style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '3px 9px', fontSize: '10.5px', color: C.textMuted, cursor: 'pointer' }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={() => setRejeitarModal(null)} disabled={rejeitando}
                  style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, background: C.surface, color: C.textMuted, borderRadius: '3px', cursor: rejeitando ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                  Cancelar
                </button>
                <button onClick={rejeitarComprovativo} disabled={rejeitando || !motivoOk}
                  title={motivoOk ? '' : 'Escreve o motivo — o cliente precisa de saber o que corrigir'}
                  style={{ height: '36px', padding: '0 14px', border: 'none', background: C.danger, color: '#fff', borderRadius: '3px', cursor: (rejeitando || !motivoOk) ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', opacity: (rejeitando || !motivoOk) ? 0.6 : 1 }}>
                  {rejeitando ? 'A recusar…' : 'Recusar e avisar cliente'}
                </button>
              </div>
            </div>
          </div>
        </div>
        )
      })()}

      {/* ── MODAL PROPAGAR Nº FACTURA FORNECEDOR a outras encomendas ── */}
      {propagarForn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget && !aplicandoPropagacao) setPropagarForn(null) }}>
          <div style={{ background: C.surface, borderRadius: '8px', width: '100%', maxWidth: '560px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ background: '#0a0a0a', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: 0, color: '#FACC15', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Propagar factura fornecedor</p>
                <h2 style={{ color: '#fff', margin: '3px 0 0', fontSize: '16px', fontWeight: '700' }}>Aplicar Nº {propagarForn.numero} a outras encomendas?</h2>
              </div>
              <button onClick={() => !aplicandoPropagacao && setPropagarForn(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '4px', color: '#fff', cursor: aplicandoPropagacao ? 'not-allowed' : 'pointer', padding: '6px', display: 'flex' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '14px 20px', overflow: 'auto', flex: 1 }}>
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.textMuted, lineHeight: '1.5' }}>
                Encontrei <strong>{propagarForn.candidates.length}</strong> encomenda{propagarForn.candidates.length === 1 ? '' : 's'} sem nº factura fornecedor preenchido, dentro de ±45 dias. Marca as que pertencem à factura <strong>{propagarForn.numero}</strong>:
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
                <button onClick={() => setPropagarForn(p => ({ ...p, candidates: p.candidates.map(c => ({ ...c, marcada: true })) }))} style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontWeight: '600', fontSize: '11px' }}>Marcar todas</button>
                <button onClick={() => setPropagarForn(p => ({ ...p, candidates: p.candidates.map(c => ({ ...c, marcada: false })) }))} style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontWeight: '600', fontSize: '11px' }}>Desmarcar todas</button>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
                {propagarForn.candidates.map((c, idx) => (
                  <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderBottom: idx < propagarForn.candidates.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer', background: c.marcada ? '#f3faf7' : C.surface }}>
                    <input
                      type="checkbox"
                      checked={c.marcada}
                      onChange={(e) => setPropagarForn(p => ({ ...p, candidates: p.candidates.map(cc => cc.id === c.id ? { ...cc, marcada: e.target.checked } : cc) }))}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {c.numero_encomenda}
                        {c.numero_encomenda && <CopyButton text={c.numero_encomenda} size={11} title="Copiar nº encomenda" />}
                        {c.interno && (
                          <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '800', background: '#fff4ce', color: '#92400e', border: '1px solid #c0740055', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                            Interno
                          </span>
                        )}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: C.textMuted }}>{c.cliente_nome}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setPropagarForn(null)} disabled={aplicandoPropagacao}
                style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, fontSize: '13px', fontWeight: '600', cursor: aplicandoPropagacao ? 'not-allowed' : 'pointer' }}>
                Só esta encomenda
              </button>
              <button onClick={aplicarPropagacaoForn} disabled={aplicandoPropagacao || propagarForn.candidates.filter(c => c.marcada).length === 0}
                style={{ height: '36px', padding: '0 16px', border: 'none', borderRadius: '4px', background: C.success, color: '#fff', fontSize: '13px', fontWeight: '700', cursor: (aplicandoPropagacao || propagarForn.candidates.filter(c => c.marcada).length === 0) ? 'not-allowed' : 'pointer', opacity: propagarForn.candidates.filter(c => c.marcada).length === 0 ? 0.5 : 1 }}>
                {aplicandoPropagacao ? 'A aplicar…' : `Aplicar a ${propagarForn.candidates.filter(c => c.marcada).length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL VERIFICACAO DA FACTURA (discrepancia entre PDF e encomenda) ── */}
      {verifFatura && (() => {
        const r = verifFatura.result
        const ilegivel = !!r.ilegivel
        const probs = r.problemas || []
        const reverificacao = !!verifFatura.reverificacao
        const cancelar = () => { setVerifFatura(null); setFaturaMsg({ tipo: '', texto: '' }) }
        const continuar = async () => {
          if (reverificacao) {
            // Re-verificacao: apenas fechar (factura ja foi enviada anteriormente). Nao gravar como verificada.
            setVerifFatura(null)
            setFaturaMsg({ tipo: 'info', texto: 'Verificação ignorada. Factura mantém-se como NÃO verificada.' })
            setTimeout(() => setFaturaMsg({ tipo: '', texto: '' }), 6000)
            return
          }
          const file = verifFatura.file
          setVerifFatura(null)
          setFaturaMsg({ tipo: 'info', texto: '⚠ Verificação ignorada — a enviar mesmo assim...' })
          await uploadFaturaPDFConfirmed(file, /* verificada */ false)
        }
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
            onClick={(e) => { if (e.target === e.currentTarget) cancelar() }}>
            <div style={{ background: C.surface, borderRadius: '8px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.4)', border: `3px solid ${ilegivel ? C.warning : C.danger}` }}>
              <div style={{ background: ilegivel ? '#fef3c7' : '#fee2e2', padding: '14px 18px', borderBottom: `1px solid ${ilegivel ? C.warning : C.danger}33`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle size={22} color={ilegivel ? C.warning : C.danger} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '800', color: ilegivel ? '#92400e' : C.danger }}>
                    {ilegivel ? 'PDF não pôde ser verificado automaticamente' : 'Discrepância detectada entre o PDF e a encomenda'}
                  </div>
                  <div style={{ fontSize: '11px', color: ilegivel ? '#78350f' : '#7f1d1d', marginTop: '2px' }}>
                    {ilegivel ? 'Verifica manualmente antes de enviar ao cliente' : 'Confirma que estás a anexar a factura correcta antes de enviar'}
                  </div>
                </div>
              </div>
              <div style={{ padding: '18px' }}>
                {!ilegivel && (
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '8px 10px', fontSize: '12px', marginBottom: '14px' }}>
                    <div style={{ fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '10px' }}>Campo</div>
                    <div style={{ fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '10px' }}>Encomenda diz</div>
                    <div style={{ fontWeight: '700', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: '10px' }}>PDF diz</div>
                    {probs.map((p, idx) => (
                      <React.Fragment key={idx}>
                        <div style={{ fontWeight: '700', color: C.text, padding: '6px 0', borderTop: `1px solid ${C.border}` }}>{p.campo}</div>
                        <div style={{ padding: '6px 8px', background: '#e8f5e9', borderRadius: '3px', color: C.text, fontFamily: 'monospace', borderTop: `1px solid ${C.border}` }}>{p.esperado || '—'}</div>
                        <div style={{ padding: '6px 8px', background: '#fde7e9', borderRadius: '3px', color: C.text, fontFamily: 'monospace', borderTop: `1px solid ${C.border}`, wordBreak: 'break-word' }}>{p.encontrado || '—'}</div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {ilegivel && (
                  <div style={{ padding: '10px 12px', background: '#fef9c3', border: `1px solid ${C.warning}55`, borderRadius: '4px', fontSize: '12px', lineHeight: '1.5', marginBottom: '14px', color: '#78350f' }}>
                    {(r.problemas || []).map((p, i) => <div key={i}>{p.encontrado}</div>)}
                    <div style={{ marginTop: '6px', fontSize: '11px' }}>Provável causa: PDF gerado por scanner (imagem) em vez de exportado do Sage como texto.</div>
                  </div>
                )}
                <p style={{ margin: '0 0 14px', fontSize: '11px', color: C.textMuted }}>
                  Estás prestes a enviar a factura por email ao cliente <strong>{modalPag?.cliente_nome}</strong> ({modalPag?.cliente_email}).
                  {ilegivel ? ' Sem verificação automática, confere visualmente o PDF antes de continuar.' : ' Se o PDF está correcto, podes continuar.'}
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button onClick={cancelar} style={{ height: '36px', padding: '0 16px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: C.text }}>
                    Cancelar — não enviar
                  </button>
                  <button onClick={continuar} style={{ height: '36px', padding: '0 16px', border: `1px solid ${ilegivel ? C.warning : C.danger}`, borderRadius: '4px', background: ilegivel ? C.warning : C.danger, cursor: 'pointer', fontSize: '12px', fontWeight: '700', color: '#fff', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Send size={12} /> Continuar mesmo assim
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── MODAL PROMOVER ENCOMENDA NAO PAGA AO PEDIDOS A FORNECEDOR ── */}
      {promoverModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setPromoverModal(null) }}
        >
          <div style={{ background: C.surface, borderRadius: '8px', width: '100%', maxWidth: '500px', boxShadow: '0 24px 60px rgba(0,0,0,0.4)', border: `3px solid ${C.danger}`, overflow: 'hidden' }}>
            <div style={{ background: '#fee2e2', padding: '14px 18px', borderBottom: `1px solid ${C.danger}33`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>🚀</span>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800', color: C.danger }}>Promover sem pagamento?</div>
                <div style={{ fontSize: '11px', color: '#7f1d1d', marginTop: '2px' }}>A encomenda vai aparecer em "Pedidos a Fornecedor" antes de receber a 1ª prestação</div>
              </div>
            </div>
            <div style={{ padding: '18px' }}>
              <div style={{ padding: '10px 12px', background: C.surfaceAlt, borderRadius: '6px', marginBottom: '14px', fontSize: '12px', lineHeight: '1.5' }}>
                <div><strong>Encomenda:</strong> {promoverModal.enc?.numero_encomenda || '—'}</div>
                <div><strong>Cliente:</strong> {promoverModal.enc?.cliente_nome || promoverModal.enc?.cliente_email}</div>
                <div><strong>Total:</strong> {fmt2(Number(promoverModal.enc?.total_final ?? promoverModal.enc?.total_com_iva ?? 0))} €</div>
                <div style={{ marginTop: '6px', color: C.textMuted, fontStyle: 'italic' }}>Vai ser visível com um banner vermelho de alerta até a 1ª prestação ser registada (e nessa altura o banner some).</div>
              </div>

              <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                Nota (opcional)
              </label>
              <textarea
                rows={2}
                placeholder='Ex: "Cliente Premium, paga 100% em 7 dias"'
                value={promoverModal.nota}
                onChange={(e) => setPromoverModal(p => ({ ...p, nota: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />

              <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                <button onClick={() => setPromoverModal(null)} disabled={promovendo}
                  style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                  Cancelar
                </button>
                <button onClick={promoverPedido} disabled={promovendo}
                  style={{ height: '36px', padding: '0 16px', background: C.danger, color: '#fff', border: 'none', borderRadius: '6px', cursor: promovendo ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🚀 {promovendo ? 'A promover...' : 'Promover'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SUB-MODAL CONFIRMAR PAGAMENTO ── */}
      {pagModal && pagEnc && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) fecharPagamento() }}
        >
          <div style={{ background: C.surface, borderRadius: '6px', width: '100%', maxWidth: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
            <div style={{ background: C.success, padding: '14px 18px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.85)' }}>Confirmar pagamento — {pagModal.tranche === '1' ? '1.ª Prestação' : pagModal.tranche === '2' ? '2.ª Prestação' : 'TOTAL (100%)'}</p>
                <p style={{ margin: '3px 0 0', fontSize: '15px', fontWeight: '700' }}>{pagEnc.numero_encomenda || '—'}</p>
                {pagEnc.cliente_nome && <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>{pagEnc.cliente_nome}</p>}
              </div>
              <button onClick={fecharPagamento} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '18px' }}>
              {/* O VALOR RECEBIDO, que ate aqui nunca era guardado.
                  Vem preenchido com o esperado — um Enter e fica igual ao que
                  era antes. So se escreve outra coisa quando o cliente pagou
                  outra coisa, e este e o unico momento em que alguem tem o
                  comprovativo a frente. */}
              {/* SEM COMPROVATIVO, DE PROPOSITO.
                  O sistema nunca exigiu o ficheiro — mas a lista dizia "Sem
                  comprovativo" tanto para o que falta como para o que foi
                  confirmado no extracto. Sao coisas diferentes e agora
                  distinguem-se. O motivo e opcional: obrigar a escrever faz
                  escrever "n/a", e um campo cheio de "n/a" e pior que vazio. */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '12px', padding: '9px 11px', borderRadius: '3px', cursor: 'pointer', background: pagModal.semComprovativo ? '#fff4ce' : C.surfaceAlt, border: `1px solid ${pagModal.semComprovativo ? '#c0740055' : C.border}` }}>
                <input type="checkbox" checked={!!pagModal.semComprovativo}
                  onChange={e => setPagModal(m => ({ ...m, semComprovativo: e.target.checked }))}
                  style={{ cursor: 'pointer', accentColor: C.primary, marginTop: '2px' }} />
                <span style={{ fontSize: '12px', color: C.text }}>
                  <strong>Confirmo sem comprovativo</strong>
                  <div style={{ color: C.textMuted, fontSize: '11px', marginTop: '2px', lineHeight: 1.45 }}>
                    Vi no extracto e não há ficheiro para anexar. Deixa de aparecer como se faltasse.
                  </div>
                </span>
              </label>
              {pagModal.semComprovativo && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Motivo (opcional)
                  </label>
                  <input type="text" value={pagModal.nota || ''}
                    onChange={e => setPagModal(m => ({ ...m, nota: e.target.value }))}
                    placeholder="ex: recebido junto com a FT 2024/979 dos serviços"
                    style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              )}

              {pagModal.tranche !== '2' && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Valor recebido
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <input
                      type="text" inputMode="decimal"
                      value={pagModal.valor}
                      onChange={e => setPagModal(m => ({ ...m, valor: e.target.value }))}
                      style={{ flex: 1, height: '36px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '15px', fontWeight: '700', color: C.text, textAlign: 'right', outline: 'none', boxSizing: 'border-box' }} />
                    <span style={{ fontSize: '14px', fontWeight: '700', color: C.textMuted }}>€</span>
                  </div>
                  {/* O QUE O COMPROVATIVO DIZ. Lido, nao gravado — o admin e que
                      decide. Se o valor ja for o mesmo, nao vale a pena o botao. */}
                  {/* Com o visto marcado, o leitor nao falhou — nao ha
                      ficheiro de proposito. Manter o aviso era o ecra a
                      queixar-se de uma coisa que a pessoa acabou de dizer que
                      esta certa. */}
                  {pagModal.lido && !pagModal.lido.ok && !pagModal.semComprovativo && (
                    <p style={{ margin: '7px 0 0', fontSize: '10.5px', color: C.textMuted, lineHeight: 1.5 }}>
                      Não consegui ler o valor no comprovativo — {pagModal.lido.motivo}.
                      {pagModal.lido.candidatos?.length > 0 ? ' Encontrei estes números:' : ' Escreve à mão.'}
                      {pagModal.lido.candidatos?.length > 0 && (
                        <span style={{ display: 'block', marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {pagModal.lido.candidatos.slice(0, 6).map(v => (
                            <button key={v} type="button" onClick={() => setPagModal(m => ({ ...m, valor: fmt2(v) }))}
                              style={{ border: `1px solid ${C.border}`, borderRadius: '3px', background: C.surface, padding: '3px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', color: C.text }}>
                              {fmt2(v)} €
                            </button>
                          ))}
                        </span>
                      )}
                      {pagModal.lido.amostra && (
                        <span style={{ display: 'block', color: C.textFaint, fontFamily: 'monospace', fontSize: '9.5px', marginTop: '2px' }}>
                          li: {pagModal.lido.amostra}
                        </span>
                      )}
                    </p>
                  )}
                  {pagModal.lido?.ok && (() => {
                    const lido = Number(pagModal.lido.valor)
                    const actual = Number(String(pagModal.valor ?? '').replace(',', '.'))
                    const igual = Math.abs(lido - actual) < 0.005
                    return (
                      <div style={{ margin: '7px 0 0', padding: '8px 10px', borderRadius: '3px', background: igual ? '#dcfce7' : '#eff6fc', border: `1px solid ${igual ? C.success : C.info}55`, fontSize: '11.5px', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                        <span>
                          {igual ? '✓ ' : ''}O comprovativo diz <strong>{fmt2(lido)} €</strong>
                          {pagModal.lido.semEtiqueta && <span style={{ color: C.warning, fontWeight: '700' }}> · sem etiqueta, confirma</span>}
                          {pagModal.lido.banco && <span style={{ color: C.textMuted }}> · {pagModal.lido.banco}</span>}
                          {pagModal.lido.data && <span style={{ color: C.textMuted }}> · {pagModal.lido.data.split('-').reverse().join('/')}</span>}
                        </span>
                        {!igual && (
                          <button type="button" onClick={() => setPagModal(m => ({
                            ...m, valor: fmt2(lido),
                            banco: m.banco || pagModal.lido.banco || '',
                            data: pagModal.lido.data || m.data,
                          }))}
                            style={{ border: 'none', borderRadius: '3px', background: C.primary, color: '#fff', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                            Usar este valor
                          </button>
                        )}
                      </div>
                    )
                  })()}
                  {(() => {
                    const v = Number(String(pagModal.valor ?? '').replace(',', '.'))
                    const dif = Math.round((v - pagModal.valorEsperado) * 100) / 100
                    if (!(v > 0) || Math.abs(dif) < 0.005) {
                      return (
                        <p style={{ margin: '4px 0 0', fontSize: '10.5px', color: C.textMuted }}>
                          Esperado: <strong style={{ color: C.text }}>{fmt2(pagModal.valorEsperado)} €</strong>. Confirma contra o comprovativo.
                        </p>
                      )
                    }
                    return (
                      <p style={{ margin: '4px 0 0', fontSize: '10.5px', color: C.warning, fontWeight: '700', lineHeight: 1.4 }}>
                        {dif > 0 ? 'Pagou ' : 'Pagou menos '}<strong>{fmt2(Math.abs(dif))} €</strong> {dif > 0 ? 'a mais' : ''} do que o esperado ({fmt2(pagModal.valorEsperado)} €).
                        <br />Fica gravado o valor que escreveres — é ele que manda em todas as contas.
                      </p>
                    )
                  })()}
                </div>
              )}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Data do pagamento</label>
                <input
                  type="date"
                  value={pagModal.data}
                  onChange={e => setPagModal(m => ({ ...m, data: e.target.value }))}
                  style={{ width: '100%', height: '36px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '14px', position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Banco</label>
                <input
                  type="text"
                  value={pagModal.banco}
                  onChange={e => setPagModal(m => ({ ...m, banco: e.target.value, bancoOpen: true }))}
                  onFocus={() => setPagModal(m => ({ ...m, bancoOpen: true }))}
                  onBlur={() => setTimeout(() => setPagModal(m => ({ ...m, bancoOpen: false })), 150)}
                  placeholder="Comece a escrever: Mil, Sant, BPI, ..."
                  autoComplete="off"
                  style={{ width: '100%', height: '36px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '14px', color: C.text, outline: 'none', boxSizing: 'border-box' }}
                />
                {pagModal.bancoOpen && (() => {
                  const q = (pagModal.banco || '').trim().toLowerCase()
                  const sugestoes = q.length === 0
                    ? BANCOS_PT.slice(0, 8)
                    : BANCOS_PT.filter(b => b.toLowerCase().includes(q)).slice(0, 8)
                  if (sugestoes.length === 0) return null
                  return (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '2px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 6px 18px rgba(0,0,0,0.10)', zIndex: 10, maxHeight: '240px', overflowY: 'auto' }}>
                      {sugestoes.map(b => (
                        <div
                          key={b}
                          onMouseDown={e => { e.preventDefault(); setPagModal(m => ({ ...m, banco: b, bancoOpen: false })) }}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.bg}` }}
                          onMouseEnter={e => e.currentTarget.style.background = C.bg}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >{b}</div>
                      ))}
                      {q.length > 0 && !BANCOS_PT.some(b => b.toLowerCase() === q) && (
                        <div style={{ padding: '6px 12px', fontSize: '11px', color: C.textMuted, fontStyle: 'italic', background: C.bg }}>
                          ou continua a escrever um banco que não está na lista
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>

              {pagMsg.texto && (
                <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: pagMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', borderLeft: `4px solid ${pagMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '2px', color: pagMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px' }}>
                  {pagMsg.texto}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={fecharPagamento} disabled={savingPag}
                  style={{ flex: 1, height: '38px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, color: C.textMuted, cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                  Cancelar
                </button>
                {((pagModal.tranche === '1' && pagEnc.pago_1a_prestacao) || (pagModal.tranche === '2' && pagEnc.pago_2a_prestacao) || (pagModal.tranche === 'total' && pagEnc.pago_1a_prestacao && pagEnc.pago_2a_prestacao)) && (
                  <button onClick={desmarcarPagamento} disabled={savingPag}
                    style={{ height: '38px', padding: '0 14px', border: `1px solid ${C.danger}`, borderRadius: '2px', backgroundColor: '#fde7e9', color: C.danger, cursor: savingPag ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', opacity: savingPag ? 0.7 : 1 }}>
                    Desmarcar
                  </button>
                )}
                <button onClick={confirmarPagamento} disabled={savingPag}
                  style={{ flex: 1, height: '38px', border: 'none', borderRadius: '2px', backgroundColor: C.success, color: '#fff', cursor: savingPag ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: savingPag ? 0.7 : 1 }}>
                  {savingPag ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> A guardar...</> : <><CheckCircle size={14} /> Confirmar Pagamento</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Header */}
      <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '600', color: C.text, margin: '0 0 3px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CreditCard size={20} color={C.primary} /> Financeiro
            </h1>
            <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
              Gestão de compras, pagamentos e encomendas
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowPedidosForn(true)}
              title="Lista de encomendas com 1.ª prestação paga, pronta para enviar ao fornecedor"
              style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.primary}`, borderRadius: '3px', backgroundColor: '#eff6fc', color: C.primary, cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <Package size={13} /> Pedidos a Fornecedor
            </button>
          )}
        </div>

        {/* O RESUMO FINANCEIRO SAIU DAQUI (06/09/2026).
            Mostrava Top 3 clientes, Top 5 produtos e a tendencia de 6 meses — o
            Lucro ja mostra os mesmos numeros com Top 10, Top 10 e 12 meses, e o
            aviso de "produtos sem custo" tambem ja la esta, com a lista.
            Custava caro: para desenhar aquele bloco lia a `encomendas` inteira,
            a `encomenda_itens` inteira e a `Produtos` inteira (2.700+ linhas,
            paginada) — em TODAS as aberturas do Financeiro, que e o ecra de
            trabalho diario. Pagava-se uma analise de lucro para ver uma
            encomenda. */}

        {/* Tabs Ativas / Arquivo */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: `2px solid ${C.border}` }}>
            {[
              { id: 'ativas', label: 'Ativas', count: nAtivas, cor: C.primary },
              { id: 'arquivo', label: 'Arquivo', count: nArquivo, cor: C.textMuted },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderBottom: activeTab === t.id ? `3px solid ${t.cor}` : '3px solid transparent',
                  marginBottom: '-2px',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: activeTab === t.id ? '800' : '600',
                  color: activeTab === t.id ? t.cor : C.textMuted,
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                {t.label}
                <span style={{ padding: '1px 8px', background: activeTab === t.id ? t.cor : C.border, color: activeTab === t.id ? '#fff' : C.textMuted, borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>{t.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Chips de filtro rápido (só Ativas) */}
        {isAdmin && activeTab === 'ativas' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', marginRight: '4px' }}>Filtros:</span>
            {[
              { k: 'pendencias', label: 'Só com pendências', icon: '⏳' },
              { k: 'aguardaPag', label: `Aguarda pagamento (${nAguardaPag})`, icon: '💰' },
              { k: 'aguardaEnvio', label: `Aguarda envio (${nAguardaEnvio})`, icon: '📦' },
              { k: 'faltaFactura', label: `Falta fatura (${nFaltaFactura})`, icon: '📄' },
              { k: 'transitoHoje', label: `Chega hoje (${nTransitoHoje})`, icon: '🚚' },
            ].map(chip => {
              const ativo = chipsAtivos[chip.k]
              return (
                <button key={chip.k}
                  onClick={() => setChipsAtivos(prev => ({ ...prev, [chip.k]: !prev[chip.k] }))}
                  style={{
                    padding: '4px 10px',
                    border: `1px solid ${ativo ? C.primary : C.border}`,
                    borderRadius: '14px',
                    background: ativo ? C.primary : C.surface,
                    color: ativo ? '#fff' : C.text,
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                  <span>{chip.icon}</span> {chip.label}
                </button>
              )
            })}
            <button onClick={() => setShowFiltrosAvancados(v => !v)}
              style={{ padding: '4px 10px', border: `1px solid ${C.border}`, borderRadius: '14px', background: showFiltrosAvancados ? '#eff6fc' : C.surface, color: C.primary, cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
              {showFiltrosAvancados ? '▲ Ocultar' : '▼ Mais filtros'}
            </button>
            {(Object.values(chipsAtivos).some(Boolean) || filtroCliente || filtroPeriodo !== 'tudo' || filtroOrigem !== 'todas' || filtroFactFornecedor || filtroValorMin || filtroFacturacao !== 'todas') && (
              <button onClick={() => {
                setChipsAtivos({ pendencias: false, aguardaPag: false, aguardaEnvio: false, faltaFactura: false, transitoHoje: false })
                setFiltroCliente(''); setFiltroPeriodo('tudo'); setFiltroOrigem('todas'); setFiltroFactFornecedor(''); setFiltroValorMin(''); setFiltroFacturacao('todas')
              }} style={{ padding: '4px 10px', border: `1px solid ${C.danger}`, borderRadius: '14px', background: C.surface, color: C.danger, cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
                ✕ Limpar tudo
              </button>
            )}
          </div>
        )}

        {/* Filtros avançados (colapsáveis) */}
        {isAdmin && showFiltrosAvancados && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px', marginBottom: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Cliente</label>
              <select value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}
                style={{ width: '100%', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }}>
                <option value="">Todos</option>
                {[...new Set(encomendas.map(e => e.cliente_email).filter(Boolean))].sort().map(email => (
                  <option key={email} value={email}>{email}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Período (criação)</label>
              <select value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)}
                style={{ width: '100%', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }}>
                <option value="tudo">Tudo</option>
                <option value="7d">Últimos 7 dias</option>
                <option value="30d">Últimos 30 dias</option>
                <option value="90d">Últimos 90 dias</option>
                <option value="6m">Últimos 6 meses</option>
                <option value="12m">Últimos 12 meses</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Origem</label>
              <select value={filtroOrigem} onChange={e => setFiltroOrigem(e.target.value)}
                style={{ width: '100%', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }}>
                <option value="todas">Todas</option>
                <option value="b2b">B2B</option>
                <option value="b2c">B2C particular</option>
                <option value="interno">Interno (Cátia)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Fatura fornecedor Nº</label>
              <input value={filtroFactFornecedor} onChange={e => setFiltroFactFornecedor(e.target.value)} placeholder="ex: 84"
                style={{ width: '100%', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Valor mínimo (€)</label>
              <input type="number" value={filtroValorMin} onChange={e => setFiltroValorMin(e.target.value)} placeholder="0"
                style={{ width: '100%', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', marginBottom: '3px' }}>Facturação</label>
              <select value={filtroFacturacao} onChange={e => setFiltroFacturacao(e.target.value)}
                style={{ width: '100%', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }}>
                <option value="todas">Todas</option>
                <option value="faturadas">Só faturadas</option>
                <option value="sem_factura">Só sem factura</option>
              </select>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <p style={{ margin: '0 0 4px', fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase' }}>{activeTab === 'ativas' ? 'A mostrar' : 'No arquivo'}</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: C.text }}>{filtradas.length}</p>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <p style={{ margin: '0 0 4px', fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase' }}>Valor em Aberto</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: C.warning }}>{fmt2(totalPendente)} €</p>
          </div>
          {isAdmin && (
            <>
              <button onClick={() => { setActiveTab('ativas'); setChipsAtivos(p => ({ ...p, aguardaPag: !p.aguardaPag })) }}
                style={{ background: chipsAtivos.aguardaPag ? '#fff4ce' : C.surface, border: `1px solid ${chipsAtivos.aguardaPag ? C.warning : C.border}`, borderRadius: '4px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <p style={{ margin: '0 0 4px', fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase' }}>💰 Aguarda pagamento</p>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: C.warning }}>{nAguardaPag}</p>
              </button>
              <button onClick={() => { setActiveTab('ativas'); setChipsAtivos(p => ({ ...p, aguardaEnvio: !p.aguardaEnvio })) }}
                style={{ background: chipsAtivos.aguardaEnvio ? '#eff6fc' : C.surface, border: `1px solid ${chipsAtivos.aguardaEnvio ? C.primary : C.border}`, borderRadius: '4px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <p style={{ margin: '0 0 4px', fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase' }}>📦 Aguarda envio</p>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: C.primary }}>{nAguardaEnvio}</p>
              </button>
              <button onClick={() => { setActiveTab('ativas'); setChipsAtivos(p => ({ ...p, faltaFactura: !p.faltaFactura })) }}
                style={{ background: chipsAtivos.faltaFactura ? '#fdecea' : C.surface, border: `1px solid ${chipsAtivos.faltaFactura ? C.danger : C.border}`, borderRadius: '4px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <p style={{ margin: '0 0 4px', fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase' }}>📄 Falta fatura</p>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: C.danger }}>{nFaltaFactura}</p>
              </button>
              <div style={{ background: '#e8f5e9', border: `1px solid ${C.success}`, borderRadius: '4px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <p style={{ margin: '0 0 4px', fontSize: '10px', color: C.success, fontWeight: '700', textTransform: 'uppercase' }}>✅ Concluídas este mês</p>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: C.success }}>{nConcluidasMes}</p>
              </div>
            </>
          )}
        </div>

        {/* IBAN — configuração (admin) e visualização */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <CreditCard size={16} color={C.primary} style={{ flexShrink: 0 }} />
            <div>
              <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>IBAN para Pagamentos</p>
              {editandoIban ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                  {[{ label: 'IBAN', val: ibanEdit, set: setIbanEdit, ph: 'PT50 0000 0000 0000 0000 0000 0', mono: true },
                    { label: 'Banco', val: bancoNomeEdit, set: setBancoNomeEdit, ph: 'Ex: Millennium BCP', mono: false },
                    { label: 'Titular', val: bancoTitularEdit, set: setBancoTitularEdit, ph: 'Nome do titular da conta', mono: false },
                  ].map(({ label, val, set, ph, mono }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, width: '52px', flexShrink: 0 }}>{label}</span>
                      <input value={val} onChange={e => set(e.target.value)} placeholder={ph}
                        style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', fontFamily: mono ? 'monospace' : 'inherit', width: '280px', outline: 'none' }} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                    <button onClick={guardarIban} disabled={savingIban}
                      style={{ height: '30px', padding: '0 10px', border: 'none', borderRadius: '2px', backgroundColor: C.success, color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Save size={12} /> Guardar
                    </button>
                    <button onClick={() => setEditandoIban(false)}
                      style={{ height: '30px', width: '30px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={13} />
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '4px' }}>
                  {iban
                    ? <>
                        <p style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: '700', color: C.text, fontFamily: 'monospace', letterSpacing: '0.5px' }}>{iban}</p>
                        {bancoNome && <p style={{ margin: '0 0 1px', fontSize: '12px', color: C.textMuted }}>Banco: <strong>{bancoNome}</strong></p>}
                        {bancoTitular && <p style={{ margin: 0, fontSize: '12px', color: C.textMuted }}>Titular: <strong>{bancoTitular}</strong></p>}
                      </>
                    : <p style={{ margin: 0, fontSize: '13px', color: C.textMuted }}>{isAdmin ? 'Clique em Editar para configurar os dados bancários' : 'A aguardar configuração'}</p>
                  }
                </div>
              )}
              {ibanMsg.texto && (
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: ibanMsg.tipo === 'ok' ? C.success : C.danger }}>{ibanMsg.texto}</p>
              )}
            </div>
          </div>
          {isAdmin && !editandoIban && (
            <button onClick={() => { setIbanEdit(iban); setBancoNomeEdit(bancoNome); setBancoTitularEdit(bancoTitular); setEditandoIban(true) }}
              style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.primary}`, borderRadius: '2px', backgroundColor: '#eff6fc', cursor: 'pointer', fontSize: '12px', color: C.primary, display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600', flexShrink: 0 }}>
              <Pencil size={12} /> Editar IBAN
            </button>
          )}
        </div>

        {/* Desconto do fornecedor (admin) */}
        {isAdmin && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '14px 16px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
                <DollarSign size={16} color={C.warning} style={{ marginTop: '1px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Desconto do Fornecedor (afeta cálculo de lucro)
                  </p>
                  {/* Um numero global deixou de fazer sentido: cada
                      fornecedor tem o seu desconto, e ha um que nao da nenhum.
                      Mostra-se o de cada um, sem caixa para escrever por cima —
                      edita-se na ficha do fornecedor, que e onde vivem todas as
                      outras condicoes dele. */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '2px' }}>
                    {fornsAdmin.filter(f => f.ativo !== false).map(f => {
                      const d = f.desconto_base_pct
                      return (
                        <div key={f.id} style={{ fontSize: '13px', color: C.text }}>
                          <strong>{f.nome}</strong>{' — '}
                          {d != null && Number(d) > 0
                            ? <span style={{ fontWeight: '700' }}>{fmt2(d)} % de desconto</span>
                            : <span style={{ color: C.textMuted }}>sem desconto — vende ao preço de tabela</span>}
                        </div>
                      )
                    })}
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.textMuted, lineHeight: '1.5' }}>
                    Cada artigo usa o desconto do <strong>seu</strong> fornecedor:{' '}
                    <strong>preço de custo = preço fornecedor × (1 − desconto dele)</strong>.
                    O preço de venda não é tocado. Para alterar, edita a ficha do fornecedor.
                  </p>
                </div>
              </div>
              {/* Recalcular: sem percentagem para escrever. Cada artigo é
                  recalculado com o desconto DELE. O botão antigo mandava o mesmo
                  número para todos e, com dois fornecedores, isso era uma
                  espingarda apontada aos 2.582 artigos do que não dá desconto. */}
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <button onClick={recalcularCustos} disabled={savingDesconto}
                  title="Recalcula o preço de custo de cada artigo a partir do preço do fornecedor e do desconto do fornecedor dele"
                  style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.border}`, borderRadius: '2px', backgroundColor: C.surface, cursor: savingDesconto ? 'not-allowed' : 'pointer', fontSize: '12px', color: C.text, display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600', opacity: savingDesconto ? 0.7 : 1 }}>
                  {savingDesconto ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> A recalcular…</> : <><RefreshCw size={12} /> Recalcular custos</>}
                </button>
              </div>
            </div>
            {descontoMsg.texto && (
              <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: descontoMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', border: `1px solid ${descontoMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '3px', color: descontoMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {descontoMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertCircle size={13} />} {descontoMsg.texto}
              </div>
            )}
          </div>
        )}

        {/* Barra amarela de aviso condições */}
        <div style={{ background: '#fffbf0', border: `1px solid #f0d070`, borderRadius: '3px', padding: '8px 10px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '12px', color: C.text }}>
          <CreditCard size={14} color={C.warning} style={{ flexShrink: 0, marginTop: '1px' }} />
          {/* As condições não são iguais para tudo: dependem do prazo de entrega
              do artigo. Descritas pelo prazo e nunca pelo nome do fornecedor —
              este aviso é visível ao cliente, e quem nos fornece é nosso. */}
          <span>
            <strong>Condições comerciais:</strong> dependem do <strong>prazo de entrega</strong> de cada artigo, indicado no catálogo.
            <span style={{ display: 'block', marginTop: '4px' }}>
              {[...condicoesForn]
                .sort((a, b) => (Number(a.prazo_envio_dias) || 0) - (Number(b.prazo_envio_dias) || 0))
                .map(f => {
                  const dias = Number(f.prazo_envio_dias) || 0
                  const prazo = dias > 0 ? `${dias} ${dias === 1 ? 'dia útil' : 'dias úteis'}` : 'prazo a confirmar'
                  const minimo = Number(f.min_encomenda_b2b) || 0
                  return (
                    <span key={f.id} style={{ display: 'block' }}>
                      • Entrega em <strong>{prazo}</strong> — {f.exige_pagamento_total
                        ? <>pagamento de <strong>100% na compra</strong></>
                        : <>pagamento em 2 prestações: <strong>50% na compra</strong> e <strong>50% até 5 dias antes do envio</strong></>}
                      {minimo > 0 && <> · encomenda mínima <strong>{fmt2(minimo)} € s/IVA</strong></>}
                    </span>
                  )
                })}
            </span>
            {/* Portes: duas palavras e mais nada. Explicar que "já estão
                incluídos no preço" convida o cliente a procurar onde é que os
                está a pagar; o que se quer é que leia uma oferta. */}
            <span style={{ display: 'block', marginTop: '6px', fontSize: '13px', fontWeight: '800', letterSpacing: '0.5px', color: C.success }}>
              PORTES GRÁTIS
            </span>
            {/* O sinal flexível NÃO se escreve aqui. É um acordo fechado com
                três clientes e não se anuncia num painel de condições gerais,
                nem sequer entre nós — este aviso é para ler de relance e ficou
                a explicar por escrito o que devia ser discreto. Quem o tem
                vê-o no carrinho, sobre a sua própria encomenda. */}
          </span>
        </div>

        {/* Pesquisa + Export CSV */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '380px' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar por produto ou cliente…"
              style={{ width: '100%', height: '34px', padding: '0 10px 0 32px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {isAdmin && filtradas.length > 0 && (
            <button onClick={exportarCSV} title={`Exportar ${filtradas.length} encomenda(s) para CSV`}
              style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.primary}`, borderRadius: '2px', background: '#fff', color: C.primary, cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FileText size={13} /> Exportar CSV ({filtradas.length})
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div style={{ padding: '0 28px 28px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '10px', color: C.textMuted }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> A carregar...
          </div>
        ) : filtradas.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '200px', gap: '12px', color: C.textMuted }}>
            <Package size={48} style={{ opacity: 0.2 }} />
            <p style={{ fontSize: '15px', margin: 0 }}>Nenhuma compra registada.</p>
          </div>
        ) : (
          <>
            {/* Agregado: total pendente para encomendar ao fornecedor (mínimo 100 un)
                INCLUI encomendas pagas (1ª) E encomendas promovidas pelo admin sem pagamento.
                EXCLUI encomendas B2C (origem='b2c_fornecedor_directo') porque essas vão directas ao fornecedor à parte */}
            {isAdmin && (() => {
              const totalPend = filtradas
                .filter(e =>
                  !e.encomendado_fornecedor_em
                  && !e.historica          // registo historico, anterior ao sistema
                  && e.estado !== 'cancelado'
                  && e.estado !== 'concluido'
                  && e.origem !== 'b2c_fornecedor_directo'
                  // So contam as encomendas do fornecedor que TEM minimo de
                  // unidades. Era o `tipo_pagamento = '100'` que fazia esta
                  // triagem — servia com dois fornecedores e partia-se ao
                  // terceiro que tambem pagasse a 100%. Agora e o fornecedor da
                  // encomenda, gravado nela, contra o minimo da ficha dele.
                  && fornecedorTemMinimoUn(e)
                  && (e.pago_1a_prestacao || e.promovido_pedido_em)
                )
                .reduce((s, e) => s + Number(e.total_unidades || 0), 0)
              if (totalPend === 0) return null
              // Dois marcos: 100 un e o minimo do fornecedor, 200 un dao mais 2%
              // de desconto. A barra so mostrava o primeiro — e a diferenca entre
              // 170 e 200 vale dinheiro, por isso passa a mostrar o proximo alvo.
              const atingiu = totalPend >= 100
              const atingiu200 = totalPend >= DESCONTO_ESCALAO_UN
              const faltam200 = DESCONTO_ESCALAO_UN - totalPend
              const perto200 = !atingiu200 && faltam200 <= 40   // vale a pena esticar
              const pct = Math.min(100, (totalPend / DESCONTO_ESCALAO_UN) * 100)
              const cor = atingiu200 ? C.success : atingiu ? C.info : totalPend >= 50 ? C.warning : C.textMuted
              const bg = atingiu200 ? '#e8f5e9' : atingiu ? '#eff6fc' : totalPend >= 50 ? '#fff4ce' : '#f3f2f1'
              return (
                <div style={{ background: bg, border: `1px solid ${cor}66`, borderRadius: '4px', padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <Package size={20} color={cor} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px' }}>Pendente para encomendar ao fornecedor</div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: cor, marginTop: '2px' }}>
                      {totalPend} un {atingiu ? '✓ atinge o mínimo de 100' : `· faltam ${100 - totalPend} para o mínimo de 100`}
                    </div>
                    {/* Marco dos 200 — vale +2% de desconto do fornecedor */}
                    {atingiu200 ? (
                      <div style={{ fontSize: '12px', fontWeight: '700', color: C.success, marginTop: '3px' }}>
                        ✓ Passa as {DESCONTO_ESCALAO_UN} un — tens direito aos +{DESCONTO_ESCALAO_PCT}% de desconto
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '12px', fontWeight: '700', marginTop: '3px',
                        color: perto200 ? '#92400e' : C.textMuted,
                        background: perto200 ? '#fff4ce' : 'transparent',
                        border: perto200 ? '1px solid #c0740055' : 'none',
                        borderRadius: perto200 ? '3px' : 0,
                        padding: perto200 ? '3px 8px' : 0,
                        display: 'inline-block',
                      }}>
                        {perto200 ? '⚠ ' : ''}Faltam <strong>{faltam200} un</strong> para as {DESCONTO_ESCALAO_UN} — a partir daí ganhas +{DESCONTO_ESCALAO_PCT}% de desconto
                      </div>
                    )}
                    <div style={{ marginTop: '6px', height: '8px', background: '#fff', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${cor}33`, position: 'relative' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: cor, transition: 'width 0.3s' }} />
                      {/* marca dos 100 na barra, agora que a escala vai até 200 */}
                      <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px', background: `${C.textMuted}66` }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: C.textMuted, fontWeight: '700', marginTop: '2px' }}>
                      <span>0</span><span>100 · mínimo</span><span>{DESCONTO_ESCALAO_UN} · +{DESCONTO_ESCALAO_PCT}%</span>
                    </div>
                  </div>
                </div>
              )
            })()}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <table style={{ width: '100%', minWidth: '1100px', borderCollapse: 'collapse', fontSize: '12.5px' }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Data</th>
                  {isAdmin && <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cliente</th>}
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nº Encomenda</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fatura</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Produto</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Qtd</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Entrega</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total c/ IVA</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }} title={isAdmin
                    ? '50% por defeito. Nas encomendas com sinal reduzido é o valor que o cliente escolheu.'
                    : 'Valor pago no momento da encomenda.'}>1.ª Prest.</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }} title="Sempre o total menos a 1.ª prestação.">2.ª Prest.</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Estado</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '700', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((e, i) => {
                  const total = totalEfetivo(e)
                  const st = estadoInfo(e, total)
                  // 1.ª = o que entrou / o sinal escolhido / metade. 2.ª = o resto.
                  const metade = primeiraPrestacao(e, total)
                  const linhaPagaTudo = pagaTudoDeUmaVez(e)
                  // Arquivo: cor por factura fornecedor (todas as encomendas da mesma factura têm o mesmo fundo).
                  // Ativas: zebra clássico.
                  const rowBg = activeTab === 'arquivo'
                    ? (corLinhaFactura(e.fornecedor_fatura_numero) || (i % 2 === 0 ? C.surface : '#fafafa'))
                    : (i % 2 === 0 ? C.surface : '#fafafa')
                  return (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${C.border}`, background: rowBg }}>
                      <td style={{ padding: '8px 10px', color: C.textMuted, whiteSpace: 'nowrap' }}>{fmtDate(e.created_at)}</td>
                      {isAdmin && (
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ fontWeight: '600', color: C.text, display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                            <span>{e.cliente_nome || '—'}</span>
                            {particularesIds.has(e.cliente_id) && (
                              <span title="Cliente particular B2C" style={{ fontSize: '9px', fontWeight: '700', color: C.warning, background: '#fff3e0', border: `1px solid ${C.warning}66`, borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase' }}>particular</span>
                            )}
                            {e.origem === 'b2c_fornecedor_directo' && (
                              <span title="Encomenda B2C — comprar directamente ao fornecedor, não consome stock B2B" style={{ fontSize: '9px', fontWeight: '700', color: '#fff', background: C.warning, borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>B2C · Directo Fornecedor</span>
                            )}
                            {internosIds.has(e.cliente_id) && (
                              <>
                                <span title="Cliente interno" style={{ fontSize: '9px', fontWeight: '700', color: '#6264a7', background: '#f3f0ff', border: '1px solid #d1cef0', borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase' }}>interno</span>
                                <span title="Sem IVA — compras intracomunitárias OSS reverse charge" style={{ fontSize: '9px', fontWeight: '700', color: C.success, background: C.successSoft, border: `1px solid ${C.success}55`, borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase' }}>Reverse Charge · 0% IVA</span>
                              </>
                            )}
                            {e.aguarda_pagamento_fornecedor && (
                              <span title="Cliente já pagou. A factura ao fornecedor ainda não foi paga — o trânsito não foi aplicado. Aplica-se quando registares o pagamento da factura ao fornecedor." style={{ fontSize: '9px', fontWeight: '700', color: '#92400e', background: '#fff8e1', border: `1px solid ${C.warning}66`, borderRadius: '8px', padding: '1px 6px', textTransform: 'uppercase', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>⏳ Aguarda Fornecedor</span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                            {e.cliente_email}
                            {e.cliente_email && <CopyButton text={e.cliente_email} size={10} title="Copiar email" />}
                          </div>
                        </td>
                      )}
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: '700', color: C.primary, fontSize: '13px' }}>{e.numero_encomenda || '—'}</span>
                        {e.numero_encomenda && <CopyButton text={e.numero_encomenda} size={11} title="Copiar nº encomenda" />}
                        {/* Registo historico: fica de fora de tudo o que e operacional.
                            A marca torna isso obvio a quem olha para a linha. */}
                        {e.historica && (
                          <span
                            title="Registo histórico, anterior ao sistema. Existe só para o stock e o capital baterem certo — não entra em pedidos ao fornecedor nem em contadores de unidades."
                            style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '800', background: '#ede9fe', color: '#5b21b6', border: '1px solid #5b21b644', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>
                            Histórica
                          </span>
                        )}
                        {/* Encomendas de sinal reduzido, por baixo do numero.
                            O rotulo de estado ja dizia "8% Pago", mas so DEPOIS
                            de a prestacao entrar e desaparecia quando a
                            encomenda avancava. O acordo existe desde que ela e
                            criada e nao muda: e por isso que fica ao pe do
                            numero e nao ao pe do estado.
                            Le-se do sinal_escolhido, que e o valor acordado em
                            euros — nao do que foi pago, que pode ser outro. */}
                        {(() => {
                          if (e.sinal_escolhido == null) return null
                          const tot = Number(e.total_final ?? e.total_com_iva ?? 0)
                          if (!(tot > 0)) return null
                          const pct = pctDoTotal(Number(e.sinal_escolhido), tot)
                          // A 100% nao e sinal reduzido, e pagamento total.
                          if (pct >= 99.9) return null
                          return (
                            <div
                              title={`Acordo de sinal reduzido: o cliente paga ${fmtPct(pct)}% (${Number(e.sinal_escolhido).toFixed(2)} €) a cabeça e o resto antes do envio.`}
                              style={{ marginTop: '3px', display: 'inline-block', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '800', background: '#fff4ce', color: '#92400e', border: '1px solid #c0740055', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                              Encomenda {fmtPct(pct)}%
                            </div>
                          )
                        })()}
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {e.fatura_emitida_em ? (
                          <div>
                            {e.fatura_numero ? (
                              e.fatura_pdf_url ? (
                                <button
                                  onClick={(ev) => { ev.stopPropagation(); verFactura(e.fatura_pdf_url) }}
                                  title={`Ver PDF · ${e.fatura_numero}`}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: '#e8f5e9', color: C.success, border: `1px solid ${C.success}40`, cursor: 'pointer' }}>
                                  <FileText size={11} /> Faturada · {e.fatura_numero}
                                </button>
                              ) : (
                                <div title={`Facturada · ${e.fatura_numero}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: '#e8f5e9', color: C.success, border: `1px solid ${C.success}40` }}>
                                  <FileText size={11} /> Faturada · {e.fatura_numero}
                                </div>
                              )
                            ) : (
                              <div title="Factura carregada mas SEM número associado — completa o nº fatura no modal" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', backgroundColor: '#fff3e0', color: C.warning, border: `1px solid ${C.warning}55` }}>
                                <AlertTriangle size={11} /> Faturada · sem nº
                              </div>
                            )}
                            <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '2px' }}>{fmtDate(e.fatura_emitida_em)}</div>
                          </div>
                        ) : internosIds.has(e.cliente_id) ? (
                          <span title="Cliente interno — facturação individual às vendas ao consumidor. Não emitimos factura agregada." style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '800', backgroundColor: C.primary, color: '#fff', border: `1px solid ${C.primary}`, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', boxShadow: `0 1px 3px ${C.primary}40` }}>
                            <Repeat size={13} /> Revenda Interna
                          </span>
                        ) : e.estado === 'cancelado' ? (
                          // CANCELADA MANDA EM TUDO O QUE FALTA FAZER.
                          //
                          // Uma encomenda cancelada continuava a dizer "Aguarda
                          // envio" e a oferecer "Encomendar ao fornecedor" — ou
                          // seja, a pedir trabalho para uma encomenda que deixou
                          // de existir. Nao e so confuso: e o caminho para
                          // alguem comprar mercadoria que ninguem vai receber.
                          //
                          // Fica antes destes tres estados e depois da factura:
                          // se ela chegou a ser emitida, continua a ver-se, que
                          // e o que interessa para a nota de credito.
                          <span title="Encomenda cancelada — nao ha factura a emitir nem mercadoria a encomendar"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '800', backgroundColor: C.danger, color: '#fff', border: `1px solid ${C.danger}`, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', animation: 'pulse-falta-factura 1.1s ease-in-out infinite' }}>
                            <AlertTriangle size={11} /> Cancelada
                          </span>
                        ) : e.pago_2a_prestacao && e.data_envio ? (
                          <span title="Encomenda paga a 100% e ja enviada — emite a fatura no Sage e carrega o PDF aqui" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '800', backgroundColor: C.danger, color: '#fff', border: `1px solid ${C.danger}`, textTransform: 'uppercase', letterSpacing: '0.4px', animation: 'pulse-falta-factura 1.1s ease-in-out infinite' }}>
                            <AlertTriangle size={11} /> Falta factura
                          </span>
                        ) : e.pago_2a_prestacao ? (
                          <span title="Encomenda paga a 100% mas ainda nao enviada — factura sera emitida apos marcar tracking (regra v3)" style={{ fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>Aguarda envio</span>
                        ) : (
                          <span style={{ fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>Por emitir</span>
                        )}

                        {/* Factura do SINAL — so aparece quando existe. Nao se
                            mostra "por facturar" nos que nao a levam: a maioria
                            dos clientes so e facturada na entrega, e isso seria
                            um aviso permanente para algo que nao esta em falta. */}
                        {e.fatura_sinal_pdf_url && (
                          <div title={`Factura do sinal · ${e.fatura_sinal_numero || 'sem nº'} · ${fmtDate(e.fatura_sinal_emitida_em)}`}
                            style={{ marginTop: '3px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: '#e8f5e9', color: C.success, border: `1px solid ${C.success}40` }}>
                            <FileText size={10} /> Sinal facturado{e.fatura_sinal_numero ? ` · ${e.fatura_sinal_numero}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: '600', color: C.text }}>{e.produto_nome}</div>
                        {e.produto_ean && <div style={{ fontSize: '11px', color: C.textMuted }}>{e.produto_ean}</div>}
                        {/* Chips fornecedor: a quem se compra + encomendado + nº factura */}
                        <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {/* A QUEM SE COMPRA, sem ter de abrir a encomenda.
                              Ate agora so se via la dentro, e com encomendas a
                              cair de tres fornecedores diferentes era preciso
                              abrir uma a uma para saber onde ia a compra.
                              SAI DOS ARTIGOS DA ENCOMENDA e nao de `produto_nome`:
                              essa coluna e do tempo em que uma encomenda levava um
                              artigo so, e nas de hoje vem vazia. */}
                          {(() => {
                            const nomes = [...new Set((e.encomenda_itens || [])
                              .map(it => fornecedorPorNome.get(it.produto_nome))
                              .filter(Boolean))]
                            // Encomendas antigas, de artigo unico, nao tem itens.
                            if (nomes.length === 0 && e.produto_nome) {
                              const antigo = fornecedorPorNome.get(e.produto_nome)
                              if (antigo) nomes.push(antigo)
                            }
                            if (nomes.length === 0) return null
                            // Mais do que um so acontece se a base de dados deixar
                            // passar o que nao devia. Mostram-se todos em vez de se
                            // escolher um: um numero errado aqui manda comprar ao
                            // fornecedor errado.
                            return (
                              <span
                                title={nomes.length > 1
                                  ? `ATENCAO: esta encomenda tem artigos de ${nomes.length} fornecedores — ${nomes.join(', ')}`
                                  : `Compra-se a ${nomes[0]}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '800', backgroundColor: nomes.length > 1 ? '#fff4ce' : C.surfaceAlt, color: nomes.length > 1 ? C.warning : C.text, border: `1px solid ${nomes.length > 1 ? C.warning + '66' : C.border}`, whiteSpace: 'nowrap', letterSpacing: '0.2px' }}>
                                <Truck size={10} /> {nomes.join(' · ')}
                              </span>
                            )
                          })()}
                          {e.encomendado_fornecedor_em ? (
                            <span
                              onClick={isAdmin ? (ev) => toggleEncomendadoFornecedor(e, ev) : undefined}
                              title={isAdmin ? `Marcado em ${fmtDate(e.encomendado_fornecedor_em)} · Clique para desmarcar` : `Encomendado em ${fmtDate(e.encomendado_fornecedor_em)}`}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: '#e8f5e9', color: C.success, border: `1px solid ${C.success}40`, cursor: isAdmin ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                              <CheckCircle size={10} /> Encomendado
                            </span>
                          ) : isAdmin && e.estado !== 'cancelado' ? (
                            // Numa cancelada nao se oferece encomendar. O chip
                            // "Encomendado" continua a aparecer se ja tiver sido
                            // marcado antes do cancelamento — isso e facto, e ha
                            // mercadoria a caminho que alguem tem de resolver.
                            <span
                              onClick={(ev) => toggleEncomendadoFornecedor(e, ev)}
                              title="Clique para marcar como encomendado ao fornecedor"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: '#fff4ce', color: C.warning, border: `1px solid ${C.warning}66`, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              Encomendar ao fornecedor →
                            </span>
                          ) : null}
                          {e.fornecedor_fatura_numero && (
                            <span
                              title={`Factura fornecedor ${e.fornecedor_fatura_numero}`}
                              style={estiloChipFactura(e.fornecedor_fatura_numero, 'medio')}>
                              <FileText size={12} /> Fact. {e.fornecedor_fatura_numero}
                            </span>
                          )}
                          {(() => {
                            if (!e.fornecedor_fatura_numero) return null
                            const fac = facturasFornecedor.get(String(e.fornecedor_fatura_numero))
                            if (!fac?.data_envio_prevista) return null
                            const hoje = new Date(); hoje.setHours(0,0,0,0)
                            const dataChegada = new Date(fac.data_envio_prevista); dataChegada.setHours(0,0,0,0)
                            const diasAte = Math.ceil((dataChegada - hoje) / 86400000)
                            const atrasado = diasAte < 0
                            const proximo = diasAte >= 0 && diasAte <= 2
                            const cor = atrasado ? C.danger : proximo ? C.warning : C.primary
                            const bg = atrasado ? '#fde7e9' : proximo ? '#fff4ce' : '#eff6fc'
                            const border = atrasado ? `${C.danger}66` : proximo ? `${C.warning}66` : `${C.primary}33`
                            return (
                              <span title={`Chegada prevista pelo fornecedor: ${fac.data_envio_prevista}${atrasado ? ' (atrasada)' : proximo ? ` (em ${diasAte}d)` : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: bg, color: cor, border: `1px solid ${border}`, whiteSpace: 'nowrap' }}>
                                <Truck size={10} /> Chegada: {fmtDate(fac.data_envio_prevista)}
                              </span>
                            )
                          })()}
                          {/* Chip tracking — clicável, abre site da transportadora em nova tab */}
                          {e.tracking && (() => {
                            const trk = detectTracking(e.transportadora, e.tracking)
                            if (!trk) return null
                            const codigoCurto = /^https?:\/\//i.test(String(e.tracking))
                              ? trk.nome
                              : e.tracking.length > 10
                                ? `${e.tracking.slice(0, 4)}…${e.tracking.slice(-4)}`
                                : e.tracking
                            return (
                              <a
                                href={trk.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(ev) => ev.stopPropagation()}
                                title={`${e.transportadora || 'Tracking'}: ${e.tracking}${e.data_envio ? ` · Enviado em ${fmtDate(e.data_envio)}` : ''} · Clique para acompanhar`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: '#dff6dd', color: '#107c10', border: '1px solid #107c1066', whiteSpace: 'nowrap', textDecoration: 'none', cursor: 'pointer' }}>
                                <Truck size={10} /> {e.transportadora || 'Tracking'}: {codigoCurto}
                              </a>
                            )
                          })()}
                          {/* Pedir 2.ª tranche — só admin, encomenda paga 1a sem 2a, cliente não interno/particular, chegada em <=2d */}
                          {isAdmin && e.pago_1a_prestacao && !e.pago_2a_prestacao && !internosIds.has(e.cliente_id) && !particularesIds.has(e.cliente_id) && e.fornecedor_fatura_numero && (() => {
                            const fac = facturasFornecedor.get(String(e.fornecedor_fatura_numero))
                            if (!fac?.data_envio_prevista) return null
                            const hoje = new Date(); hoje.setHours(0,0,0,0)
                            const dataChegada = new Date(fac.data_envio_prevista); dataChegada.setHours(0,0,0,0)
                            const diasAte = Math.ceil((dataChegada - hoje) / 86400000)
                            if (diasAte > 2 || diasAte < -7) return null // só faixa [-7d ... +2d]
                            return (
                              <span
                                onClick={(ev) => { ev.stopPropagation(); abrirModal(e) }}
                                title="Chegada do stock está próxima. Notificar cliente para pagar a 2.ª prestação (abre modal de pagamento)."
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '800', backgroundColor: C.danger, color: '#fff', border: `1px solid ${C.danger}`, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', cursor: 'pointer', animation: 'pulse-falta-factura 1.1s ease-in-out infinite' }}>
                                <AlertTriangle size={10} /> Pedir 2.ª tranche
                              </span>
                            )
                          })()}
                          {/* Promover ao Pedidos a Fornecedor sem pagamento (admin only) */}
                          {isAdmin && !e.pago_1a_prestacao && e.origem !== 'b2c_fornecedor_directo' && !['cancelado','concluido'].includes(e.estado) && (
                            e.promovido_pedido_em ? (
                              <span
                                onClick={(ev) => { ev.stopPropagation(); despromoverPedido(e) }}
                                title={`Promovida sem pagamento em ${fmtDate(e.promovido_pedido_em)}${e.promovido_pedido_nota ? ` · "${e.promovido_pedido_nota}"` : ''} · Clique para despromover`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: '#fee2e2', color: C.danger, border: `1px solid ${C.danger}66`, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                🚀 Promovida s/ pagamento
                              </span>
                            ) : (
                              <span
                                onClick={(ev) => { ev.stopPropagation(); setPromoverModal({ enc: e, nota: '' }) }}
                                title="Promover ao Pedidos a Fornecedor mesmo sem pagamento (cliente confiável)"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', backgroundColor: '#f3f2f1', color: C.textMuted, border: `1px dashed ${C.border}`, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                🚀 Promover sem pagar
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', minWidth: '92px' }}>
                        {(() => {
                          const qtd = Number(e.total_unidades || 0)
                          const jaEncomendado = !!e.encomendado_fornecedor_em
                          const isB2C = e.origem === 'b2c_fornecedor_directo'
                          // B2C: encomenda directa ao fornecedor, sem barra de progresso (não conta para mínimo 100)
                          if (isB2C) {
                            return (
                              <div title={`${qtd} unidades — encomenda B2C, pedir directamente ao fornecedor`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: C.warning, fontWeight: '700', fontSize: '13px' }}>
                                📦 {qtd} un
                              </div>
                            )
                          }
                          if (jaEncomendado) {
                            return (
                              <div title={`${qtd} unidades — já encomendado ao fornecedor`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: C.success, fontWeight: '700', fontSize: '13px' }}>
                                <CheckCircle size={12} /> {qtd} un
                              </div>
                            )
                          }
                          // O minimo de 100 unidades e uma condicao do fornecedor
                          // de 2 prestacoes. Onde se paga a 100% o minimo e em
                          // euros, nao em unidades — a barra dizia "faltam 79"
                          // numa encomenda que nao tem minimo de unidades nenhum.
                          if (linhaPagaTudo) {
                            return (
                              <div title={`${qtd} unidades`} style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>{qtd} un</div>
                            )
                          }
                          const pct = Math.min(100, qtd)
                          const cor = qtd >= 100 ? C.success : qtd >= 50 ? C.warning : C.textMuted
                          const bg = qtd >= 100 ? '#e8f5e9' : qtd >= 50 ? '#fff4ce' : '#f3f2f1'
                          return (
                            <div title={`${qtd} unidades${qtd < 100 ? ` — faltam ${100 - qtd} para atingir o mínimo de 100` : ' — atinge o mínimo do fornecedor'}`}>
                              <div style={{ fontSize: '13px', fontWeight: '700', color: cor }}>{qtd} un</div>
                              <div style={{ marginTop: '4px', height: '5px', background: bg, borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: cor, transition: 'width 0.3s' }} />
                              </div>
                              {qtd < 100 && (
                                <div style={{ fontSize: '9px', color: C.textMuted, marginTop: '2px', fontWeight: '600' }}>faltam {100 - qtd}</div>
                              )}
                            </div>
                          )
                        })()}
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {e.entrega_nome ? (
                          <div style={{ fontSize: '11px' }} title={`${e.entrega_nome}\n${e.entrega_morada || ''}\n${e.entrega_cod_postal || ''} ${e.entrega_pais || ''}${e.entrega_nif ? '\nNIF: ' + e.entrega_nif : ''}${e.entrega_telefone ? '\nTel: ' + e.entrega_telefone : ''}`}>
                            <div style={{ fontWeight: '700', color: C.text }}>{e.entrega_nome.split(' ')[0]}</div>
                            <div style={{ color: C.textMuted, fontSize: '10px' }}>{e.entrega_cod_postal}</div>
                          </div>
                        ) : <span style={{ color: C.textMuted, fontSize: '11px' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: C.text }}>
                        {fmt2(total)} €
                        {Number(e.desconto_valor || 0) > 0 && (
                          <div style={{ fontSize: '10px', color: C.success, fontWeight: '600' }}>
                            (–{Number(e.desconto_percentagem || 0)}%)
                          </div>
                        )}
                      </td>
                      <td
                        onClick={isAdmin ? (ev) => { ev.stopPropagation(); abrirPagamento(linhaPagaTudo ? 'total' : '1', e) } : undefined}
                        title={isAdmin ? 'Clique para registar/editar pagamento' : ''}
                        style={{ padding: '8px 10px', textAlign: 'right', color: C.text, cursor: isAdmin ? 'pointer' : 'default' }}
                      >
                        <div style={{ fontWeight: '700', color: e.pago_1a_prestacao ? C.success : C.text }}>{fmt2(metade)} €</div>
                        {linhaPagaTudo && (
                          <div style={{ fontSize: '9px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>pagamento único</div>
                        )}
                        {e.pago_1a_prestacao ? (
                          <div style={{ marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: C.success, fontWeight: '700', justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                            <CheckCircle size={11} />
                            <span>{fmtDate(e.pago_1a_data)}{e.pago_1a_banco ? ` · ${e.pago_1a_banco}` : ''}</span>
                          </div>
                        ) : e.comprovativo_1a_url ? (
                          <div style={{ marginTop: '3px', fontSize: '10px', color: C.warning, fontWeight: '700', whiteSpace: 'nowrap' }}>
                            ⏳ Falta aprovar
                          </div>
                        ) : e.pago_1a_sem_comprovativo ? (
                          <div title={e.pago_1a_nota || 'Confirmado contra o extracto'}
                            style={{ marginTop: '3px', fontSize: '10px', color: C.success, fontWeight: '700', whiteSpace: 'nowrap' }}>
                            ✓ Sem comprovativo
                          </div>
                        ) : (
                          <div style={{ marginTop: '3px', fontSize: '10px', color: C.textMuted, fontWeight: '600', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                            Sem comprovativo
                          </div>
                        )}
                      </td>
                      <td
                        onClick={isAdmin && !linhaPagaTudo ? (ev) => { ev.stopPropagation(); abrirPagamento('2', e) } : undefined}
                        title={isAdmin && !linhaPagaTudo ? 'Clique para registar/editar pagamento' : ''}
                        style={{ padding: '8px 10px', textAlign: 'right', color: C.text, cursor: isAdmin && !linhaPagaTudo ? 'pointer' : 'default' }}
                      >
                        {/* A 2.ª é o que falta, não uma cópia da 1.ª. Nas encomendas
                            normais dá exactamente o mesmo (metade = total/2); só
                            difere quando a encomenda foi alterada depois de paga.
                            Nas de pagamento total nao existe: mostrar 0,00 € a
                            "Falta aprovar" fazia parecer que havia dinheiro por
                            cobrar numa encomenda ja liquidada. */}
                        {linhaPagaTudo ? (
                          <div style={{ color: C.textMuted, fontWeight: '600', fontSize: '11px' }}>— sem 2.ª prestação</div>
                        ) : (<>
                        <div style={{ fontWeight: '700', color: e.pago_2a_prestacao ? C.success : C.text }}>{fmt2(total - metade)} €</div>
                        {e.pago_2a_prestacao ? (
                          <div style={{ marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: C.success, fontWeight: '700', justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                            <CheckCircle size={11} />
                            <span>{fmtDate(e.pago_2a_data)}{e.pago_2a_banco ? ` · ${e.pago_2a_banco}` : ''}</span>
                          </div>
                        ) : e.comprovativo_2a_url ? (
                          <div style={{ marginTop: '3px', fontSize: '10px', color: C.warning, fontWeight: '700', whiteSpace: 'nowrap' }}>
                            ⏳ Falta aprovar
                          </div>
                        ) : e.pago_2a_sem_comprovativo ? (
                          <div title={e.pago_2a_nota || 'Confirmado contra o extracto'}
                            style={{ marginTop: '3px', fontSize: '10px', color: C.success, fontWeight: '700', whiteSpace: 'nowrap' }}>
                            ✓ Sem comprovativo
                          </div>
                        ) : (
                          <div style={{ marginTop: '3px', fontSize: '10px', color: C.textMuted, fontWeight: '600', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                            Sem comprovativo
                          </div>
                        )}
                        </>)}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <span style={{ padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', color: st.color, background: st.bg, whiteSpace: 'nowrap' }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button onClick={() => abrirModal(e)}
                          title="Ver detalhe / registar pagamento"
                          style={{ width: '28px', height: '28px', padding: 0, border: `1px solid ${C.primary}`, borderRadius: '4px', background: '#eff6fc', color: C.primary, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        <p style={{ marginTop: '16px', fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '5px' }}>
          <FileText size={11} /> A 2.ª prestação inclui sempre os portes de envio, calculados na data de expedição.
        </p>
      </div>
      <style>{`@keyframes pulse-falta-factura { 0%,100% { opacity: 1; box-shadow: 0 0 0 0 ${C.danger}66 } 50% { opacity: .55; box-shadow: 0 0 0 6px ${C.danger}00 } }`}</style>
    </div>
  )
}
